import { useEffect, useRef, useState } from "react";

declare global {
  interface Window {
    supabase: any;
    DeviceOrientationEvent: any;
  }
}

const SUPABASE_URL = "https://xutiyiavtuhwcixqbpiy.supabase.co";
const SUPABASE_KEY = "sb_publishable_lxptnSb0C1eHG-NC9dp2QA_g9L32kic";
const ADMIN_PASSCODE = "5327";
const TOTAL_FRAMES = 75;
const LOCAL_DRAFT_KEY = "drivewitharturo-360-drafts";
const PUBLIC_SHARE_KEY = "drivewitharturo-public-vehicles";

type VehicleImage = string | null;

type Hotspot = {
  id: number;
  x: number;
  y: number;
  title: string;
  note: string;
};

type InventoryVehicle = {
  id: string;
  name: string;
  vin?: string;
  year?: string;
  make?: string;
  model?: string;
  trim?: string;
  body_class?: string;
  hologram_type?: string;
  created_at?: string;
};

type DamageReport = {
  id?: string;
  vehicle_id?: string;
  frame_number: number;
  image_url: string;
  damage_type: string;
  severity: string;
  x: number;
  y: number;
  note: string;
  created_at?: string;
};

const safeSlug = (value: string) =>
  (value || "vehicle").toLowerCase().replace(/[^a-z0-9]/g, "-");

const chooseHologramTypeFromBody = (body: string) => {
  const b = (body || "").toLowerCase();
  if (b.includes("pickup")) return "pickup";
  if (b.includes("sport utility") || b.includes("suv")) return "suv";
  if (b.includes("van")) return "van";
  if (b.includes("coupe")) return "coupe";
  if (b.includes("sedan")) return "sedan";
  if (b.includes("truck")) return "truck";
  return "universal";
};

const largestMissingRunFromImages = (images: VehicleImage[]) => {
  let longest = 0;
  let current = 0;

  for (let i = 0; i < images.length; i++) {
    if (!images[i]) {
      current += 1;
      longest = Math.max(longest, current);
    } else {
      current = 0;
    }
  }

  return longest;
};

const smartScoreFromImages = (images: VehicleImage[]) => {
  const completion = images.filter(Boolean).length;
  const progress = Math.round((completion / TOTAL_FRAMES) * 100);
  const largestGap = largestMissingRunFromImages(images);

  return Math.max(
    0,
    Math.min(
      100,
      Math.round(
        progress -
          Math.min(largestGap * 1.5, 25) +
          (completion >= 50 ? 8 : 0) +
          (completion >= 75 ? 10 : 0)
      )
    )
  );
};

function runSelfTests() {
  console.assert(chooseHologramTypeFromBody("Sedan") === "sedan", "Sedan guide test failed");
  console.assert(chooseHologramTypeFromBody("Sport Utility Vehicle") === "suv", "SUV guide test failed");
  console.assert(largestMissingRunFromImages(["a", null, null, "b", null]) === 2, "Missing run test failed");
  console.assert(smartScoreFromImages(Array(TOTAL_FRAMES).fill("x")) === 100, "Smart score full capture test failed");
}

export default function App() {
  const handleBulkUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
      .filter((file) => file.type.startsWith("image/"))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

    if (!files.length) return;

    const emptySlots = images
      .map((img, index) => (img ? null : index))
      .filter((index): index is number => index !== null);

    if (!emptySlots.length) {
      alert("All 75 frame slots are already filled. Delete or replace a slot before bulk uploading.");
      e.currentTarget.value = "";
      return;
    }

    const filesToUpload = files.slice(0, emptySlots.length);

    try {
      setUploading(true);

      for (let i = 0; i < filesToUpload.length; i++) {
        const slotIndex = emptySlots[i];
        await handleUpload(slotIndex, filesToUpload[i]);
      }

      const nextEmptyAfterUpload = images.findIndex((img, index) => !img && !emptySlots.slice(0, filesToUpload.length).includes(index));
      setCaptureSlot(nextEmptyAfterUpload >= 0 ? nextEmptyAfterUpload : 0);

      alert(
        `Bulk upload complete: ${filesToUpload.length} photo${filesToUpload.length === 1 ? "" : "s"} added to the next empty frame slots.`
      );
    } catch (err: any) {
      console.error(err);
      alert(`Bulk upload failed: ${err?.message || "Unknown error"}`);
    } finally {
      setUploading(false);
      e.currentTarget.value = "";
    }
  };
  const [vehicleName, setVehicleName] = useState("");
  const [vin, setVin] = useState("");
  const [year, setYear] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [trim, setTrim] = useState("");
  const [bodyClass, setBodyClass] = useState("");
  const [hologramType, setHologramType] = useState("universal");

  const [images, setImages] = useState<VehicleImage[]>(Array(TOTAL_FRAMES).fill(null));
  const [currentFrame, setCurrentFrame] = useState(0);
  const [captureMode, setCaptureMode] = useState(false);
  const [captureSlot, setCaptureSlot] = useState(0);

  const [interiorImage, setInteriorImage] = useState<string | null>(null);
  const [hotspots, setHotspots] = useState<Hotspot[]>([]);
  const [hotspotMode, setHotspotMode] = useState(false);

  const [damageMode, setDamageMode] = useState(false);
  const [damageReports, setDamageReports] = useState<DamageReport[]>([]);
  const [damageType, setDamageType] = useState("scratch");
  const [damageSeverity, setDamageSeverity] = useState("light");

  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exportingVideo, setExportingVideo] = useState(false);
  const [videoDownloadUrl, setVideoDownloadUrl] = useState("");
  const [studioEnhance, setStudioEnhance] = useState(true);
  const [studioPreset, setStudioPreset] = useState("luxury");
  const [backgroundMode, setBackgroundMode] = useState("original");
  const [removeBackground, setRemoveBackground] = useState(false);
  const [addWatermark, setAddWatermark] = useState(true);
  const [savedDrafts, setSavedDrafts] = useState<any[]>([]);
  const [publicVehicleUrl, setPublicVehicleUrl] = useState("");
  const [vehiclePrice, setVehiclePrice] = useState("");
  const [mileage, setMileage] = useState("");
  const [drivetrain, setDrivetrain] = useState("");
  const [fuelType, setFuelType] = useState("");
  const [transmission, setTransmission] = useState("");
  const [mpg, setMpg] = useState("");
  const [featureNotes, setFeatureNotes] = useState("");
  const [featureBadges, setFeatureBadges] = useState<string[]>([]);
  const [arShowroomEnabled, setArShowroomEnabled] = useState(false);
  const [arHotspots, setArHotspots] = useState<any[]>([]);
  const [salesAnalytics, setSalesAnalytics] = useState({
    leads: 0,
    appointments: 0,
    sold: 0,
    reels: 0,
    viralHooks: 0,
    topVehicle: "No data yet",
    bestPlatform: "No data yet",
    avgClosingRatio: 0,
  });
  const [scenes, setScenes] = useState<any[]>([]);
  const [publicViewVehicle, setPublicViewVehicle] = useState<any | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [showLeadDashboard, setShowLeadDashboard] = useState(false);
  const [generatedMarketing, setGeneratedMarketing] = useState<any | null>(null);
  const [reelStoryboard, setReelStoryboard] = useState<any[]>([]);
  const [renderingReel, setRenderingReel] = useState(false);
  const [renderedReelUrl, setRenderedReelUrl] = useState("");
  const [seoPreview, setSeoPreview] = useState<any | null>(null);
  const [analyticsData, setAnalyticsData] = useState<any>({
    pageViews: 0,
    uniqueVisitors: 0,
    repeatVisitors: 0,
    qrScans: 0,
    reelPlays: 0,
    chatOpens: 0,
    financingClicks: 0,
    callClicks: 0,
    textClicks: 0,
    heatmap: [],
  });
  const [storedLeads, setStoredLeads] = useState<any[]>([]);
  const [adminUnlocked, setAdminUnlocked] = useState(() => localStorage.getItem("drivewitharturo-admin-unlocked") === "true");
  const [adminPasscodeInput, setAdminPasscodeInput] = useState("");
  const [showInventoryDashboard, setShowInventoryDashboard] = useState(false);
  const [inventoryVehicles, setInventoryVehicles] = useState<any[]>([]);
  const [inventorySearch, setInventorySearch] = useState("");
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [activityFeed, setActivityFeed] = useState<any[]>([]);
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [followUpDrafts, setFollowUpDrafts] = useState<any[]>([]);
  const [otdInputs, setOtdInputs] = useState({
    salePrice: "",
    downPayment: "",
    tradeAllowance: "",
    payoff: "",
    prepFee: "995",
    docFee: "365",
    govFee: "346",
    taxableErt: "0",
    shipping: "0",
    warranty: "0",
    gap: "0",
    taxArea: "chicago",
    warrantyFinanced: true,
    gapFinanced: true,
  });
  const [otdResult, setOtdResult] = useState<any | null>(null);
  const [socialContent, setSocialContent] = useState<any | null>(null);
  const [voiceoverScript, setVoiceoverScript] = useState<any | null>(null);
  const [viralHooks, setViralHooks] = useState<any[]>([]);
  const [depthOverlayMode, setDepthOverlayMode] = useState(true);
  const [trueSegmentationMode, setTrueSegmentationMode] = useState(false);
  const [exportPackage, setExportPackage] = useState<any | null>(null);
  const [captureAssistantEnabled, setCaptureAssistantEnabled] = useState(true);
  const [captureGuidance, setCaptureGuidance] = useState<any[]>([]);
  const [currentShotIndex, setCurrentShotIndex] = useState(0);
  const [autoEditMode, setAutoEditMode] = useState("luxury");
  const [autoEditTimeline, setAutoEditTimeline] = useState<any[]>([]);
  const [cloudSyncEnabled, setCloudSyncEnabled] = useState(true);
  const [syncStatus, setSyncStatus] = useState<"synced" | "syncing" | "offline" | "failed">("synced");
  const [lastSyncAt, setLastSyncAt] = useState<string>("");
  const [backgroundStudioMode, setBackgroundStudioMode] = useState("luxury-studio");
  const [backgroundPreview, setBackgroundPreview] = useState<string>("");
  const [adCampaign, setAdCampaign] = useState<any | null>(null);
  const [vehicleFeedData, setVehicleFeedData] = useState<any | null>(null);
  const [financeAssistant, setFinanceAssistant] = useState<any>({
    creditScore: "",
    downPayment: "",
    tradeValue: "",
    monthlyBudget: "",
    employment: "",
    firstTimeBuyer: false,
    itin: false,
  });
  const [financeEstimate, setFinanceEstimate] = useState<any | null>(null);
  const [aiLeadMessage, setAiLeadMessage] = useState("");
  const [aiConversation, setAiConversation] = useState<any[]>([]);
  const [leadTemperature, setLeadTemperature] = useState("COLD");
  const [chatInput, setChatInput] = useState("");
  const [aiPersonality, setAiPersonality] = useState("bilingual");

  const [leadForm, setLeadForm] = useState({
    name: "",
    phone: "",
    email: "",
    tradeIn: false,
    financing: true,
    appointment: "",
    notes: "",
  });
  const [exportedVideoFile, setExportedVideoFile] = useState<File | null>(null);
  const [shareLink, setShareLink] = useState("");
  const [currentVehicleId, setCurrentVehicleId] = useState("");
  const [analytics, setAnalytics] = useState<any[]>([]);

  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [publicInventoryMode, setPublicInventoryMode] = useState(false);

  const [customerMode, setCustomerMode] = useState(false);
  const [loadingVehicle, setLoadingVehicle] = useState(false);
  const [decodingVin, setDecodingVin] = useState(false);
  const [vinScannerOpen, setVinScannerOpen] = useState(false);
  const [vinScanning, setVinScanning] = useState(false);
  const [vinScanMessage, setVinScanMessage] = useState("Point camera at VIN barcode/QR or windshield VIN label.");

  const [supabaseReady, setSupabaseReady] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraZoom, setCameraZoom] = useState(1);
  const [cameraZoomSupported, setCameraZoomSupported] = useState(false);
  const [cameraGridEnabled, setCameraGridEnabled] = useState(true);
  const [cameraError, setCameraError] = useState("");
  const [motionPermission, setMotionPermission] = useState(false);
  const [motionSupported, setMotionSupported] = useState(true);
  const [devicePitch, setDevicePitch] = useState(0);
  const [deviceRoll, setDeviceRoll] = useState(0);
  const [deviceCompass, setDeviceCompass] = useState<number | null>(null);
  const [motionMessage, setMotionMessage] = useState("Enable motion alignment for live phone-level guidance.");

  const supabaseRef = useRef<any>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const liveStreamRef = useRef<MediaStream | null>(null);
  const vinVideoRef = useRef<HTMLVideoElement | null>(null);
  const vinStreamRef = useRef<MediaStream | null>(null);
  const vinScanLoopRef = useRef<any>(null);
  const isDragging = useRef(false);
  const lastX = useRef(0);
  const velocity = useRef(0);
  const autoSpinRef = useRef<any>(null);
  const resumeSpinTimeoutRef = useRef<any>(null);
  const lastDragTrackRef = useRef(0);

  const completion = images.filter(Boolean).length;
  const minimumSpinFrames = 30;
  const canUse360 = completion >= minimumSpinFrames;
  const progressPercent = Math.round((completion / TOTAL_FRAMES) * 100);
  const activeFrameEntries = images
    .map((url, index) => (url ? { url, originalIndex: index } : null))
    .filter((item): item is { url: string; originalIndex: number } => item !== null);
  const activeFrameCount = activeFrameEntries.length;
  const activeFramePosition = activeFrameCount > 0 ? currentFrame % activeFrameCount : 0;
  const activeFrameEntry = activeFrameCount > 0 ? activeFrameEntries[activeFramePosition] : null;
  const activeFrameUrl = activeFrameEntry?.url || null;
  const activeOriginalFrameNumber = activeFrameEntry ? activeFrameEntry.originalIndex + 1 : currentFrame + 1;
  const targetAngle = Math.round((captureSlot / TOTAL_FRAMES) * 360);
  const angleSpacing = (360 / TOTAL_FRAMES).toFixed(1);

  const missingFrames = images
    .map((img, index) => (img ? null : index))
    .filter((index): index is number => index !== null);

  const nextMissingFrame = missingFrames.find((index) => index >= captureSlot) ?? missingFrames[0] ?? 0;
  const largestMissingRun = largestMissingRunFromImages(images);
  const smartCaptureScore = smartScoreFromImages(images);

  const smartCaptureStatus =
    smartCaptureScore >= 95
      ? "Inventory-ready 360"
      : smartCaptureScore >= 80
      ? "Strong capture"
      : smartCaptureScore >= 55
      ? "Needs more angles"
      : "Coverage too light";

  const smartCaptureTip =
    missingFrames.length === 0
      ? "All angles captured. Review, then save and share."
      : largestMissingRun >= 10
      ? "Large missing section detected. Jump to the next missing angle."
      : completion < 25
      ? "Start building coverage. Capture every few steps around the vehicle."
      : "Fill the missing frames to smooth out the customer spin.";

  const liveTiltScore = Math.max(
    0,
    Math.min(100, Math.round(100 - (Math.abs(deviceRoll) + Math.abs(devicePitch - 90)) * 2.2))
  );

  const alignmentScore = motionPermission
    ? Math.round((liveTiltScore + (deviceCompass === null ? 70 : 90) + 80 + 80) / 4)
    : 80;

  const readyToCapture = alignmentScore >= 85;

  const alignmentMessage = motionPermission
    ? motionMessage
    : cameraOn
    ? "Camera ready. Enable motion alignment for live leveling guidance."
    : "Start the camera or upload manually into each frame slot.";

  const vehicleSubtitle = `${year || ""} ${make || ""} ${model || ""} ${trim || ""}`.trim();

  const getSupabase = () => {
    if (typeof window !== "undefined" && window.supabase) {
      return window.supabase.createClient(SUPABASE_URL, SUPABASE_KEY);
    }
    return null;
  };

  const ensureSupabase = () => {
    if (supabaseRef.current) return supabaseRef.current;

    const client = getSupabase();
    if (client) {
      supabaseRef.current = client;
      setSupabaseReady(true);
      return client;
    }

    return null;
  };

  const loadSupabaseClient = () => {
    const existingClient = getSupabase();
    if (existingClient) {
      supabaseRef.current = existingClient;
      setSupabaseReady(true);
      return;
    }

    const existingScript = document.querySelector("script[data-supabase-cdn='true']");
    if (existingScript) return;

    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2";
    script.async = true;
    script.setAttribute("data-supabase-cdn", "true");

    script.onload = () => {
      const client = getSupabase();
      if (client) {
        supabaseRef.current = client;
        setSupabaseReady(true);
        console.log("Supabase connected");
      }
    };

    script.onerror = () => {
      setSupabaseReady(false);
      console.error("Supabase CDN failed to load");
    };

    document.body.appendChild(script);
  };

  const trackEvent = async (eventType: string, eventLabel = "") => {
    if (!supabaseRef.current || !currentVehicleId) return;

    try {
      await supabaseRef.current.from("vehicle_analytics").insert({
        vehicle_id: currentVehicleId,
        event_type: eventType,
        event_label: eventLabel,
        user_agent: navigator.userAgent,
      });
    } catch (err) {
      console.warn("Analytics failed:", err);
    }
  };

  const loadAnalytics = async (vehicleId: string) => {
    if (!supabaseRef.current || !vehicleId) return;

    const { data, error } = await supabaseRef.current
      .from("vehicle_analytics")
      .select("*")
      .eq("vehicle_id", vehicleId)
      .order("created_at", { ascending: false });

    if (!error && data) setAnalytics(data);
  };

  const countEvent = (type: string) => analytics.filter((event) => event.event_type === type).length;

  const stopAutoSpin = () => {
    if (autoSpinRef.current) clearInterval(autoSpinRef.current);
    if (resumeSpinTimeoutRef.current) clearTimeout(resumeSpinTimeoutRef.current);
    autoSpinRef.current = null;
    resumeSpinTimeoutRef.current = null;
  };

  const startAutoSpin = () => {
    stopAutoSpin();
    if (captureMode || activeFrameCount === 0) return;
    autoSpinRef.current = setInterval(() => setCurrentFrame((prev) => (prev + 1) % activeFrameCount), 145);
  };

  const scheduleAutoSpin = () => {
    if (captureMode) return;
    if (resumeSpinTimeoutRef.current) clearTimeout(resumeSpinTimeoutRef.current);
    resumeSpinTimeoutRef.current = setTimeout(startAutoSpin, 2200);
  };

  const stopLiveCamera = () => {
    if (liveStreamRef.current) {
      liveStreamRef.current.getTracks().forEach((track) => track.stop());
      liveStreamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOn(false);
  };

  const extractVinFromText = (text: string) => {
    const cleaned = (text || "").toUpperCase().replace(/[IOQ]/g, "").replace(/[^A-Z0-9]/g, "");
    const match = cleaned.match(/[A-HJ-NPR-Z0-9]{17}/);
    return match ? match[0] : "";
  };

  const stopVinScanner = () => {
    if (vinScanLoopRef.current) {
      cancelAnimationFrame(vinScanLoopRef.current);
      vinScanLoopRef.current = null;
    }

    if (vinStreamRef.current) {
      vinStreamRef.current.getTracks().forEach((track) => track.stop());
      vinStreamRef.current = null;
    }

    if (vinVideoRef.current) vinVideoRef.current.srcObject = null;
    setVinScannerOpen(false);
    setVinScanning(false);
  };

  const startVinScanner = async () => {
    setVinScannerOpen(true);
    setVinScanMessage("Starting VIN scanner...");

    const isSecure =
      window.location.protocol === "https:" ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    if (!isSecure) {
      setVinScanMessage("Safari requires HTTPS for camera access. Publish the app on HTTPS or use localhost while testing.");
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setVinScanMessage("Camera is not available in this browser. Enter VIN manually.");
      return;
    }

    try {
      stopVinScanner();
      setVinScannerOpen(true);
      setVinScanning(true);
      setVinScanMessage("Opening rear camera... Allow camera permission when Safari asks.");

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });

      vinStreamRef.current = stream;

      setTimeout(async () => {
        if (!vinVideoRef.current) return;

        vinVideoRef.current.srcObject = stream;
        vinVideoRef.current.setAttribute("playsinline", "true");
        vinVideoRef.current.muted = true;

        try {
          await vinVideoRef.current.play();
        } catch (playErr) {
          console.warn("Safari video play warning:", playErr);
          setVinScanMessage("Camera opened, but Safari blocked autoplay. Tap the video or Start VIN Scanner again.");
        }
      }, 150);

      const BarcodeDetectorClass = (window as any).BarcodeDetector;

      if (!BarcodeDetectorClass) {
        setVinScanMessage("Camera preview is active. Safari may not support automatic barcode scanning on this device/browser, so enter the 17-character VIN manually after viewing it.");
        return;
      }

      const detector = new BarcodeDetectorClass({
        formats: ["code_39", "code_128", "qr_code", "data_matrix", "pdf417"],
      });

      setVinScanMessage("Scanning... Center the VIN barcode/QR inside the camera view.");

      const scan = async () => {
        try {
          if (!vinVideoRef.current || !vinStreamRef.current) return;

          const codes = await detector.detect(vinVideoRef.current);

          for (const code of codes) {
            const raw = code.rawValue || "";
            const foundVin = extractVinFromText(raw);

            if (foundVin.length === 17) {
              setVin(foundVin);
              setVinScanMessage(`VIN found: ${foundVin}`);
              stopVinScanner();
              setTimeout(() => decodeVinFromValue(foundVin), 250);
              return;
            }
          }
        } catch (err) {
          console.warn("VIN scan error:", err);
        }

        vinScanLoopRef.current = requestAnimationFrame(scan);
      };

      vinScanLoopRef.current = requestAnimationFrame(scan);
    } catch (err: any) {
      console.error(err);
      setVinScanMessage(`Could not open camera: ${err?.message || "permission blocked"}. On iPhone Safari, make sure the site is HTTPS and camera permission is allowed.`);
      setVinScanning(false);
    }
  };

  const applyCameraZoom = async (zoomValue: number) => {
    const track = liveStreamRef.current?.getVideoTracks?.()[0];
    if (!track) return;

    const capabilities = track.getCapabilities?.() as any;

    if (!capabilities?.zoom) {
      setCameraZoomSupported(false);
      return;
    }

    const minZoom = capabilities.zoom.min || 1;
    const maxZoom = capabilities.zoom.max || 5;
    const nextZoom = Math.max(minZoom, Math.min(maxZoom, zoomValue));

    try {
      await track.applyConstraints({
        advanced: [{ zoom: nextZoom } as any],
      });
      setCameraZoom(nextZoom);
      setCameraZoomSupported(true);
    } catch (err) {
      console.warn("Camera zoom not supported by this browser/device:", err);
      setCameraZoomSupported(false);
    }
  };

  const attachStreamToVideo = (stream: MediaStream) => {
    setTimeout(() => {
      if (!videoRef.current) return;
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(() => {});
    }, 100);
  };

  const startLiveCamera = async () => {
    setCameraError("");
    stopAutoSpin();

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraError("Live camera is not available in this browser. Use Safari/Chrome or upload into slots manually.");
      return;
    }

    const attempts: MediaStreamConstraints[] = [
      { video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false },
      { video: { facingMode: "environment" }, audio: false },
      { video: true, audio: false },
    ];

    for (const constraints of attempts) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        liveStreamRef.current = stream;
        setCameraOn(true);
        attachStreamToVideo(stream);

        const track = stream.getVideoTracks?.()[0];
        const capabilities = track?.getCapabilities?.() as any;
        const supportsZoom = Boolean(capabilities?.zoom);
        setCameraZoomSupported(supportsZoom);

        if (supportsZoom) {
          const minZoom = capabilities.zoom.min || 1;
          setCameraZoom(minZoom);
          await applyCameraZoom(minZoom);
        }
        return;
      } catch (err: any) {
        console.warn("Camera attempt failed:", err?.name || err, constraints);
      }
    }

    setCameraOn(false);
    setCameraError("No camera was found or permission was blocked. You can still use the Upload button in each frame slot.");
  };

  const takeLivePhoto = async () => {
    if (!videoRef.current || !cameraOn) {
      alert("Start Live Camera first, or use Upload Slot.");
      return;
    }

    const video = videoRef.current;

    const sourceWidth = video.videoWidth || 1280;
    const sourceHeight = video.videoHeight || 720;

    // Always export in TikTok/Reels 9:16 format
    const targetWidth = 1080;
    const targetHeight = 1920;

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const sourceAspect = sourceWidth / sourceHeight;
    const targetAspect = targetWidth / targetHeight;

    let cropWidth = sourceWidth;
    let cropHeight = sourceHeight;
    let cropX = 0;
    let cropY = 0;

    // Smart center crop to preserve subject framing
    if (sourceAspect > targetAspect) {
      cropWidth = sourceHeight * targetAspect;
      cropX = (sourceWidth - cropWidth) / 2;
    } else {
      cropHeight = sourceWidth / targetAspect;
      cropY = (sourceHeight - cropHeight) / 2;
    }

    ctx.drawImage(
      video,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      targetWidth,
      targetHeight
    );

    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], `frame-${captureSlot + 1}-${Date.now()}.jpg`, { type: "image/jpeg" });
      await handleUpload(captureSlot, file);
      setCaptureSlot((prev) => (prev + 1) % TOTAL_FRAMES);
    }, "image/jpeg", 0.92);
  };

  const handleDeviceOrientation = (event: DeviceOrientationEvent) => {
    const beta = typeof event.beta === "number" ? event.beta : 0;
    const gamma = typeof event.gamma === "number" ? event.gamma : 0;
    const alpha = typeof event.alpha === "number" ? event.alpha : null;

    setDevicePitch(beta);
    setDeviceRoll(gamma);
    setDeviceCompass(alpha);

    const rollAbs = Math.abs(gamma);
    const pitchTarget = 90;
    const pitchDiff = Math.abs(beta - pitchTarget);

    if (rollAbs < 4 && pitchDiff < 7) {
      setMotionMessage("Locked — phone is level. Hold steady and capture.");
    } else if (rollAbs >= 4) {
      setMotionMessage(gamma > 0 ? "Tilt phone slightly left to level." : "Tilt phone slightly right to level.");
    } else if (pitchDiff >= 7) {
      setMotionMessage(beta > pitchTarget ? "Lower the top of the phone slightly." : "Raise the top of the phone slightly.");
    } else {
      setMotionMessage("Almost aligned — hold steady.");
    }
  };

  const enableMotionAlignment = async () => {
    try {
      const DeviceOrientation = window.DeviceOrientationEvent;
      if (!DeviceOrientation) {
        setMotionSupported(false);
        alert("Motion sensors are not supported on this device/browser.");
        return;
      }

      if (typeof DeviceOrientation.requestPermission === "function") {
        const response = await DeviceOrientation.requestPermission();
        if (response !== "granted") {
          alert("Motion permission was not granted.");
          return;
        }
      }

      window.removeEventListener("deviceorientation", handleDeviceOrientation as EventListener);
      window.addEventListener("deviceorientation", handleDeviceOrientation as EventListener, true);
      setMotionPermission(true);
      setMotionSupported(true);
      setMotionMessage("Motion alignment enabled. Level the phone inside the guide.");
    } catch (err) {
      console.error(err);
      setMotionSupported(false);
      alert("Could not enable motion alignment on this browser.");
    }
  };

  useEffect(() => {
    runSelfTests();
    loadSupabaseClient();
    loadPublicVehicleFromRoute();

    const interval = setInterval(() => {
      const client = ensureSupabase();
      if (client) {
        clearInterval(interval);
        const vehicleId = new URLSearchParams(window.location.search).get("vehicle");
        if (vehicleId) loadSharedVehicle(vehicleId);
      }
    }, 200);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!captureMode) startAutoSpin();
    return () => stopAutoSpin();
  }, [captureMode, customerMode, completion]);

  useEffect(() => {
    return () => {
      stopLiveCamera();
      window.removeEventListener("deviceorientation", handleDeviceOrientation as EventListener);
    };
  }, []);

  const decodeVinFromValue = async (vinValue: string) => {
    const cleanVin = vinValue.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, "");

    if (cleanVin.length !== 17) {
      alert("VIN must be exactly 17 characters.");
      return;
    }

    try {
      setDecodingVin(true);
      const res = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValuesExtended/${cleanVin}?format=json`);
      const data = await res.json();
      const r = data.Results[0];

      const y = r.ModelYear || "";
      const mk = r.Make || "";
      const md = r.Model || "";
      const tr = r.Trim || "";
      const body = r.BodyClass || "";
      const drive = r.DriveType || "";
      const fuel = r.FuelTypePrimary || "";
      const trans = r.TransmissionStyle || r.TransmissionSpeeds || "";

      setVin(cleanVin);
      setYear(y);
      setMake(mk);
      setModel(md);
      setTrim(tr);
      setBodyClass(body);
      setDrivetrain(drive);
      setFuelType(fuel);
      setTransmission(trans);
      setVehicleName(`${y} ${mk} ${md} ${tr}`.trim());
      setHologramType(chooseHologramTypeFromBody(body));
      alert(`VIN decoded ✅
${`${y} ${mk} ${md} ${tr}`.trim()}
Guide: ${chooseHologramTypeFromBody(body).toUpperCase()}`);
    } catch (err) {
      console.error(err);
      alert("VIN decode failed");
    } finally {
      setDecodingVin(false);
    }
  };

  const decodeVin = async () => {
    await decodeVinFromValue(vin);
  };

  const enhanceImageBeforeUpload = async (file: File) => {
    if (!studioEnhance) return file;

    return new Promise<File>((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const targetWidth = 1080;
        const targetHeight = 1920;
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          URL.revokeObjectURL(objectUrl);
          resolve(file);
          return;
        }

        const sourceAspect = img.width / img.height;
        const targetAspect = targetWidth / targetHeight;

        let cropWidth = img.width;
        let cropHeight = img.height;
        let cropX = 0;
        let cropY = 0;

        if (sourceAspect > targetAspect) {
          cropWidth = img.height * targetAspect;
          cropX = (img.width - cropWidth) / 2;
        } else {
          cropHeight = img.width / targetAspect;
          cropY = (img.height - cropHeight) / 2;
        }

        const presetFilters: Record<string, string> = {
          natural: "brightness(1.06) contrast(1.06) saturate(1.04)",
          luxury: "brightness(1.08) contrast(1.14) saturate(1.12)",
          dark: "brightness(0.92) contrast(1.28) saturate(1.08)",
          outdoor: "brightness(1.12) contrast(1.08) saturate(1.1)",
          sport: "brightness(1.05) contrast(1.2) saturate(1.22)",
        };

        ctx.filter = presetFilters[studioPreset] || presetFilters.luxury;

        if (removeBackground) {
          const backgrounds: Record<string, string> = {
            luxury: "#050505",
            white: "#f4f4f4",
            jdm: "#12001f",
            truck: "#1c140d",
            sunset: "#311400",
            chicago: "#050816",
          };

          ctx.fillStyle = backgrounds[backgroundMode] || "#000";
          ctx.fillRect(0, 0, targetWidth, targetHeight);

          const gradientBg = ctx.createRadialGradient(
            targetWidth / 2,
            targetHeight * 0.48,
            120,
            targetWidth / 2,
            targetHeight * 0.52,
            targetHeight
          );

          if (backgroundMode === "jdm") {
            gradientBg.addColorStop(0, "rgba(255,0,180,0.22)");
            gradientBg.addColorStop(1, "rgba(0,0,0,0)");
          } else if (backgroundMode === "sunset") {
            gradientBg.addColorStop(0, "rgba(255,120,0,0.28)");
            gradientBg.addColorStop(1, "rgba(0,0,0,0)");
          } else if (backgroundMode === "chicago") {
            gradientBg.addColorStop(0, "rgba(0,120,255,0.18)");
            gradientBg.addColorStop(1, "rgba(0,0,0,0)");
          } else {
            gradientBg.addColorStop(0, "rgba(255,255,255,0.06)");
            gradientBg.addColorStop(1, "rgba(0,0,0,0)");
          }

          ctx.fillStyle = gradientBg;
          ctx.fillRect(0, 0, targetWidth, targetHeight);

          ctx.shadowColor = "rgba(0,0,0,0.5)";
          ctx.shadowBlur = 40;
        }

        ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, targetWidth, targetHeight);
        ctx.filter = "none";
        ctx.shadowBlur = 0;

        if (removeBackground) {
          const floorGradient = ctx.createLinearGradient(0, targetHeight * 0.72, 0, targetHeight);
          floorGradient.addColorStop(0, "rgba(255,255,255,0.02)");
          floorGradient.addColorStop(1, "rgba(0,0,0,0.55)");
          ctx.fillStyle = floorGradient;
          ctx.fillRect(0, targetHeight * 0.72, targetWidth, targetHeight * 0.28);
        }

        const gradient = ctx.createLinearGradient(0, 0, 0, targetHeight);
        gradient.addColorStop(0, "rgba(0,0,0,0.08)");
        gradient.addColorStop(0.55, "rgba(0,0,0,0)");
        gradient.addColorStop(1, "rgba(0,0,0,0.24)");
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, targetWidth, targetHeight);

        if (addWatermark) {
          ctx.fillStyle = "rgba(0,0,0,0.48)";
          ctx.fillRect(0, targetHeight - 132, targetWidth, 132);
          ctx.fillStyle = "#d4af37";
          ctx.font = "800 38px Arial";
          ctx.fillText("DriveWithArturo", 42, targetHeight - 76);
          ctx.fillStyle = "#ffffff";
          ctx.font = "500 26px Arial";
          ctx.fillText("Text Arturo: (872) 364-0886", 42, targetHeight - 34);
        }

        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(objectUrl);
            if (!blob) {
              resolve(file);
              return;
            }

            const enhancedFile = new File(
              [blob],
              file.name.replace(/\.[^.]+$/, "") + "-studio.jpg",
              { type: "image/jpeg" }
            );
            resolve(enhancedFile);
          },
          "image/jpeg",
          0.92
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(file);
      };

      img.src = objectUrl;
    });
  };

  const loadDrafts = () => {
    try {
      const raw = localStorage.getItem(LOCAL_DRAFT_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      setSavedDrafts(Array.isArray(parsed) ? parsed : []);
    } catch {
      setSavedDrafts([]);
    }
  };

  const loadPublicVehicleFromRoute = () => {
    try {
      const match = window.location.pathname.match(/^\/view\/([^/]+)/);
      if (!match) return;

      const slug = decodeURIComponent(match[1]);
      const raw = localStorage.getItem(PUBLIC_SHARE_KEY);
      const existing = raw ? JSON.parse(raw) : [];
      const found = Array.isArray(existing)
        ? existing.find((item: any) => item.slug === slug)
        : null;

      if (found) {
        setPublicViewVehicle(found);
        setVehicleName(found.vehicleName || "");
        setVin(found.vin || "");
        setVehiclePrice(found.vehiclePrice || "");
        setImages(Array.isArray(found.images) ? found.images : Array(TOTAL_FRAMES).fill(null));
        setCustomerMode(false);
        setCaptureMode(false);
      }
    } catch (err) {
      console.error("Public route load failed", err);
    }
  };

  const downloadQrCode = async () => {
    if (!qrCodeUrl) {
      alert("Generate a public vehicle page first.");
      return;
    }

    try {
      const res = await fetch(qrCodeUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeSlug(vehicleName || vin || "drivewitharturo-qr")}-qr.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      window.open(qrCodeUrl, "_blank");
    }
  };

  const printQrWindowSticker = () => {
    if (!publicVehicleUrl || !qrCodeUrl) {
      alert("Generate a public vehicle page first.");
      return;
    }

    const html = `
      <html>
        <head>
          <title>DriveWithArturo QR Sticker</title>
          <style>
            body { margin: 0; font-family: Arial, sans-serif; background: #000; color: #fff; }
            .sheet { width: 8.5in; height: 11in; box-sizing: border-box; padding: 0.6in; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; }
            .brand { color: #d4af37; font-size: 22px; font-weight: 900; letter-spacing: 2px; margin-bottom: 18px; }
            h1 { color: #fff; font-size: 40px; margin: 0 0 8px; }
            .price { color: #d4af37; font-size: 34px; font-weight: 900; margin-bottom: 20px; }
            img { width: 520px; height: 520px; background: #fff; padding: 18px; border-radius: 24px; }
            .scan { color: #d4af37; font-size: 34px; font-weight: 900; margin-top: 24px; }
            .small { color: #aaa; font-size: 18px; line-height: 1.5; margin-top: 16px; max-width: 620px; }
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          <div class="sheet">
            <div class="brand">DRIVEWITHARTURO 360</div>
            <h1>${vehicleName || "Vehicle 360 View"}</h1>
            <div class="price">${vehiclePrice || "Text for price"}</div>
            <img src="${qrCodeUrl}" />
            <div class="scan">SCAN TO VIEW 360°</div>
            <div class="small">Text Arturo: (872) 364-0886<br/>Chicago Cars US · Summit, IL · Hablamos Español</div>
          </div>
          <script>window.print();</script>
        </body>
      </html>
    `;

    const win = window.open("", "_blank");
    if (!win) return;
    win.document.open();
    win.document.write(html);
    win.document.close();
  };

  const money = (value: number) =>
    value.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    });

  const roundCents = (value: number) => Math.round(value * 100) / 100;

  const cleanMoney = (value: string | number) => {
    const parsed = Number(value || 0);
    return Number.isFinite(parsed) ? Math.max(parsed, 0) : 0;
  };

  const calculateOtd = () => {
    // Matched to the DriveWithArturo.biz calculator logic:
    // taxable subtotal = price + doc + prep + taxable ERT - trade ACV
    // payoff is added back later, products/shipping/title/gov are non-taxable
    // Cook/Chicago tax is split 8.5% + 1%, then total tax is rounded to nearest dollar
    const salePrice = cleanMoney(otdInputs.salePrice);
    const downPayment = cleanMoney(otdInputs.downPayment);
    const tradeValue = cleanMoney(otdInputs.tradeAllowance);
    const tradePayoff = cleanMoney(otdInputs.payoff);
    const tradeAllowance = roundCents(tradeValue - tradePayoff);
    const docFee = cleanMoney(otdInputs.docFee);
    const prepFee = cleanMoney(otdInputs.prepFee);
    const govFee = cleanMoney(otdInputs.govFee);
    const taxableErt = cleanMoney(otdInputs.taxableErt);
    const titleAndPlates = 0;
    const serviceContract = cleanMoney(otdInputs.warranty);
    const gap = cleanMoney(otdInputs.gap);
    const shipping = cleanMoney(otdInputs.shipping);

    const taxableSubtotal = Math.max(
      salePrice + docFee + prepFee + taxableErt - tradeValue,
      0
    );

    let baseRate = 0.085;
    let cityRate = 0.01;
    let taxLabel = "Cook County / Chicago split tax: 8.5% + 1%";

    if (otdInputs.taxArea === "cook") {
      baseRate = 0.085;
      cityRate = 0;
      taxLabel = "Other Illinois Counties: 8.5%";
    }

    if (otdInputs.taxArea === "other") {
      baseRate = 0.0725;
      cityRate = 0;
      taxLabel = "Other Illinois estimate: 7.25%";
    }

    const baseTax = roundCents(taxableSubtotal * baseRate);
    const cityTax = roundCents(taxableSubtotal * cityRate);
    const detailedTotalTax = roundCents(baseTax + cityTax);
    const totalTax = roundCents(Math.round(detailedTotalTax));
    const nonTaxableProducts = serviceContract + gap;
    const nonTaxableFees = titleAndPlates + govFee + nonTaxableProducts + shipping;

    const totalBeforeDown = roundCents(
      taxableSubtotal + totalTax + nonTaxableFees + tradePayoff
    );

    const balanceAfterDown = Math.max(roundCents(totalBeforeDown - downPayment), 0);
    const taxSavings = roundCents(tradeValue * (baseRate + cityRate));

    setOtdResult({
      salePrice,
      tradeValue,
      tradePayoff,
      tradeAllowance,
      taxableSubtotal: roundCents(taxableSubtotal),
      baseTax,
      cityTax,
      detailedTotalTax,
      totalTax,
      taxes: totalTax,
      nonTaxableProducts: roundCents(nonTaxableProducts),
      nonTaxableFees: roundCents(nonTaxableFees),
      totalBeforeDown,
      dueBeforeDown: totalBeforeDown,
      balanceAfterDown,
      taxSavings,
      taxLabel,
    });
  };

  const generateFollowUpsForLead = (lead: any) => {
    const name = lead.name || "there";
    const vehicle = lead.vehicleName || vehicleName || "the vehicle";
    const priceAngle = lead.financing ? "financing options" : "availability";

    const drafts = [
      {
        label: "15-Minute Hot Lead Text",
        body: `Hi ${name}, this is Arturo with Chicago Cars US. I saw you were checking out ${vehicle}. What’s holding you back from coming in today — price, down payment, trade-in, or financing?`,
      },
      {
        label: "2-Hour Re-Engagement",
        body: `${name}, quick question — how soon are you looking to purchase? I can help with ${priceAngle}, trade-in value, and a full out-the-door breakdown on ${vehicle}.`,
      },
      {
        label: "Spanish Follow-Up",
        body: `Hola ${name}, soy Arturo de Chicago Cars US. Vi que estabas interesado/a en ${vehicle}. ¿Qué te detiene — enganche, crédito, trade-in o precio final? Hablamos Español.`,
      },
      {
        label: "Next Morning Follow-Up",
        body: `Good morning ${name}, just following up on ${vehicle}. Inventory moves fast — do you want me to confirm availability and work up numbers for you today?`,
      },
    ];

    setFollowUpDrafts(drafts);
    pushActivityAlert(
      "Follow-Up Drafts Created",
      `Generated ${drafts.length} follow-up messages for ${name}`,
      lead.financing || lead.appointment ? "HOT" : "WARM"
    );
  };

  const generateAiResponse = async (message: string) => {
    const lower = message.toLowerCase();

    let response = "Thanks for reaching out. Text Arturo at (872) 364-0886 for more information.";

    if (lower.includes("available")) {
      response = `${vehicleName || "This vehicle"} is currently available. Inventory changes quickly, so text Arturo now at (872) 364-0886 to confirm availability and reserve it.`;
    } else if (lower.includes("finance") || lower.includes("credit")) {
      response = "Financing is available for many credit situations including first-time buyers. Trade-ins are welcome and we work with many lenders. Text Arturo to get started.";
    } else if (lower.includes("trade") || lower.includes("trade-in")) {
      response = "We accept trade-ins. Send photos, VIN, miles, and payoff information to Arturo for a quick estimate.";
    } else if (lower.includes("carplay")) {
      response = "Features can vary by trim/package. Text Arturo and we can confirm Apple CarPlay, Android Auto, and specific options for this vehicle.";
    } else if (lower.includes("price") || lower.includes("otd") || lower.includes("down")) {
      response = `Current listed price is ${vehiclePrice || "available upon request"}. Taxes and fees are additional. Text Arturo for an exact out-the-door breakdown.`;
      pushActivityAlert(
        "HOT Lead: Pricing Question",
        `Customer asked pricing/OTD questions for ${vehicleName || "vehicle"}`,
        "HOT"
      );
    } else if (lower.includes("today") || lower.includes("appointment") || lower.includes("test drive")) {
      response = "Test drives are available. Text Arturo now at (872) 364-0886 to schedule your appointment today.";
      pushActivityAlert(
        "HOT Lead: Appointment Intent",
        `Customer wants to see ${vehicleName || "vehicle"}`,
        "HOT"
      );
    } else if (lower.includes("shipping") || lower.includes("delivery")) {
      response = "Delivery and out-of-state assistance may be available depending on location. Contact Arturo for details and transport pricing.";
    }

    if (aiPersonality === "closer") {
      response += " Inventory moves fast — don’t miss this one.";
    }

    if (aiPersonality === "hispanic") {
      response += " Hablamos Español.";
    }

    const userMessage = {
      role: "user",
      content: message,
      createdAt: new Date().toISOString(),
    };

    const aiMessage = {
      role: "assistant",
      content: response,
      createdAt: new Date().toISOString(),
    };

    setChatMessages((prev) => [...prev, userMessage, aiMessage]);
    setChatInput("");
  };

  const pushActivityAlert = async (title: string, body: string, priority: "HOT" | "WARM" | "LOW" = "LOW") => {
    const entry = {
      id: Date.now(),
      title,
      body,
      priority,
      createdAt: new Date().toISOString(),
    };

    setActivityFeed((prev) => [entry, ...prev].slice(0, 100));
    setUnreadAlerts((prev) => prev + 1);

    try {
      const raw = localStorage.getItem("drivewitharturo-activity-feed");
      const existing = raw ? JSON.parse(raw) : [];
      localStorage.setItem(
        "drivewitharturo-activity-feed",
        JSON.stringify([entry, ...existing].slice(0, 100))
      );
    } catch {}

    if (
      notificationsEnabled &&
      "Notification" in window &&
      Notification.permission === "granted"
    ) {
      new Notification(title, {
        body,
        icon: "/favicon.ico",
      });
    }
  };

  const enablePushNotifications = async () => {
    if (!("Notification" in window)) {
      alert("Push notifications are not supported on this device/browser.");
      return;
    }

    const permission = await Notification.requestPermission();

    if (permission === "granted") {
      setNotificationsEnabled(true);
      localStorage.setItem("drivewitharturo-push-enabled", "true");
      alert("Push notifications enabled.");
    } else {
      alert("Push notifications were denied.");
    }
  };

  const loadActivityFeed = () => {
    try {
      const raw = localStorage.getItem("drivewitharturo-activity-feed");
      const parsed = raw ? JSON.parse(raw) : [];
      setActivityFeed(Array.isArray(parsed) ? parsed : []);
    } catch {
      setActivityFeed([]);
    }
  };

  const loadInventoryVehicles = () => {
    try {
      const raw = localStorage.getItem(PUBLIC_SHARE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];

      const enriched = Array.isArray(parsed)
        ? parsed.map((vehicle: any) => {
            const leadRaw = localStorage.getItem("drivewitharturo-leads");
            const leads = leadRaw ? JSON.parse(leadRaw) : [];

            const relatedLeads = Array.isArray(leads)
              ? leads.filter((lead: any) => lead.vin === vehicle.vin)
              : [];

            return {
              ...vehicle,
              status: vehicle.status || "published",
              leadCount: relatedLeads.length,
              thumbnail: Array.isArray(vehicle.images)
                ? vehicle.images.find(Boolean)
                : null,
            };
          })
        : [];

      setInventoryVehicles(enriched);
    } catch {
      setInventoryVehicles([]);
    }
  };

  const updateVehicleStatus = (slug: string, status: string) => {
    const updated = inventoryVehicles.map((vehicle) =>
      vehicle.slug === slug
        ? { ...vehicle, status }
        : vehicle
    );

    setInventoryVehicles(updated);

    localStorage.setItem(
      PUBLIC_SHARE_KEY,
      JSON.stringify(updated)
    );
  };

  const deleteVehicleListing = (slug: string) => {
    const updated = inventoryVehicles.filter(
      (vehicle) => vehicle.slug !== slug
    );

    setInventoryVehicles(updated);

    localStorage.setItem(
      PUBLIC_SHARE_KEY,
      JSON.stringify(updated)
    );
  };

  const loadStoredLeads = () => {
    try {
      const raw = localStorage.getItem("drivewitharturo-leads");
      const parsed = raw ? JSON.parse(raw) : [];
      setStoredLeads(Array.isArray(parsed) ? parsed : []);
    } catch {
      setStoredLeads([]);
    }
  };

  const updateLeadStatus = (submittedAt: string, status: string) => {
    const updated = storedLeads.map((lead) =>
      lead.submittedAt === submittedAt
        ? { ...lead, status }
        : lead
    );

    localStorage.setItem(
      "drivewitharturo-leads",
      JSON.stringify(updated)
    );

    setStoredLeads(updated);
  };

  const deleteLead = (submittedAt: string) => {
    const updated = storedLeads.filter(
      (lead) => lead.submittedAt !== submittedAt
    );

    localStorage.setItem(
      "drivewitharturo-leads",
      JSON.stringify(updated)
    );

    setStoredLeads(updated);
  };

  const submitVehicleLead = async () => {
    try {
      const lead = {
        ...leadForm,
        vehicleName,
        vin,
        vehiclePrice,
        submittedAt: new Date().toISOString(),
      };

      const raw = localStorage.getItem("drivewitharturo-leads");
      const existing = raw ? JSON.parse(raw) : [];

      localStorage.setItem(
        "drivewitharturo-leads",
        JSON.stringify([lead, ...existing].slice(0, 500))
      );

      const leadPriority = lead.financing || lead.tradeIn
        ? "HOT"
        : lead.appointment
        ? "WARM"
        : "LOW";

      pushActivityAlert(
        `${leadPriority} Lead Submitted`,
        `${lead.name || "New customer"} submitted a lead for ${lead.vehicleName || "a vehicle"}`,
        leadPriority as "HOT" | "WARM" | "LOW"
      );

      alert("Lead submitted successfully. Arturo will contact you shortly.");

      setLeadForm({
        name: "",
        phone: "",
        email: "",
        tradeIn: false,
        financing: true,
        appointment: "",
        notes: "",
      });
    } catch (err) {
      console.error(err);
      alert("Failed to submit lead.");
    }
  };

  const trackAnalyticsEvent = (type: string, extra?: any) => {
    setAnalyticsData((prev: any) => {
      const updated = {
        ...prev,
        [type]: (prev[type] || 0) + 1,
      };

      try {
        localStorage.setItem(
          "drivewitharturo-analytics",
          JSON.stringify(updated)
        );
      } catch {}

      return updated;
    });

    if (extra?.x && extra?.y) {
      setAnalyticsData((prev: any) => ({
        ...prev,
        heatmap: [
          ...prev.heatmap,
          {
            x: extra.x,
            y: extra.y,
            ts: Date.now(),
          },
        ].slice(-250),
      }));
    }
  };

  const loadLocalAnalytics = () => {
    try {
      const raw = localStorage.getItem("drivewitharturo-analytics");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      setAnalyticsData((prev: any) => ({ ...prev, ...parsed }));
    } catch {}
  };

  const downloadTextFile = (filename: string, content: string, type = "application/json") => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportFullBackup = () => {
    const backup = {
      exportedAt: new Date().toISOString(),
      app: "DriveWithArturo 360 Studio",
      drafts: JSON.parse(localStorage.getItem(LOCAL_DRAFT_KEY) || "[]"),
      publicVehicles: JSON.parse(localStorage.getItem(PUBLIC_SHARE_KEY) || "[]"),
      leads: JSON.parse(localStorage.getItem("drivewitharturo-leads") || "[]"),
      activityFeed: JSON.parse(localStorage.getItem("drivewitharturo-activity-feed") || "[]"),
      analytics: JSON.parse(localStorage.getItem("drivewitharturo-analytics") || "{}"),
    };

    downloadTextFile(
      `drivewitharturo-360-backup-${new Date().toISOString().slice(0, 10)}.json`,
      JSON.stringify(backup, null, 2)
    );
  };

  const exportLeadsCsv = () => {
    const leads = JSON.parse(localStorage.getItem("drivewitharturo-leads") || "[]");
    if (!Array.isArray(leads) || leads.length === 0) {
      alert("No leads to export.");
      return;
    }

    const headers = ["Date", "Name", "Phone", "Email", "Vehicle", "VIN", "Price", "Trade-In", "Financing", "Appointment", "Notes", "Status"];
    const rows = leads.map((lead: any) => [
      lead.submittedAt || "",
      lead.name || "",
      lead.phone || "",
      lead.email || "",
      lead.vehicleName || "",
      lead.vin || "",
      lead.vehiclePrice || "",
      lead.tradeIn ? "Yes" : "No",
      lead.financing ? "Yes" : "No",
      lead.appointment || "",
      lead.notes || "",
      lead.status || "new",
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    downloadTextFile(
      `drivewitharturo-leads-${new Date().toISOString().slice(0, 10)}.csv`,
      csv,
      "text/csv;charset=utf-8"
    );
  };

  const exportInventoryCsv = () => {
    const vehicles = JSON.parse(localStorage.getItem(PUBLIC_SHARE_KEY) || "[]");
    if (!Array.isArray(vehicles) || vehicles.length === 0) {
      alert("No public vehicle inventory to export.");
      return;
    }

    const headers = ["Created", "Vehicle", "VIN", "Price", "Slug", "Frames", "Status", "URL"];
    const rows = vehicles.map((vehicle: any) => [
      vehicle.createdAt || "",
      vehicle.vehicleName || "",
      vehicle.vin || "",
      vehicle.vehiclePrice || "",
      vehicle.slug || "",
      Array.isArray(vehicle.images) ? vehicle.images.filter(Boolean).length : 0,
      vehicle.status || "published",
      vehicle.slug ? `${window.location.origin}/view/${vehicle.slug}` : "",
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    downloadTextFile(
      `drivewitharturo-inventory-${new Date().toISOString().slice(0, 10)}.csv`,
      csv,
      "text/csv;charset=utf-8"
    );
  };

  const restoreBackupFromFile = async (file: File | null) => {
    if (!file) return;

    try {
      const text = await file.text();
      const backup = JSON.parse(text);

      if (!confirm("Restore this backup? This will replace local drafts, public vehicles, leads, activity feed, and analytics on this device.")) return;

      localStorage.setItem(LOCAL_DRAFT_KEY, JSON.stringify(backup.drafts || []));
      localStorage.setItem(PUBLIC_SHARE_KEY, JSON.stringify(backup.publicVehicles || []));
      localStorage.setItem("drivewitharturo-leads", JSON.stringify(backup.leads || []));
      localStorage.setItem("drivewitharturo-activity-feed", JSON.stringify(backup.activityFeed || []));
      localStorage.setItem("drivewitharturo-analytics", JSON.stringify(backup.analytics || {}));

      loadDrafts();
      loadStoredLeads();
      loadInventoryVehicles();
      loadActivityFeed();
      loadLocalAnalytics();
      restoreStudioSession();

      alert("Backup restored successfully.");
    } catch (err) {
      console.error(err);
      alert("Backup restore failed. Make sure you selected a valid DriveWithArturo backup JSON file.");
    }
  };

  const captureSequence = [
    {
      title: "Front Hero Shot",
      guidance: "Center the vehicle. Lower the camera slightly for a premium aggressive stance.",
    },
    {
      title: "Front 3/4 Driver Side",
      guidance: "Move 45° left. Keep equal spacing around the vehicle.",
    },
    {
      title: "Side Profile",
      guidance: "Align wheels straight and keep camera level with the body line.",
    },
    {
      title: "Rear 3/4",
      guidance: "Capture taillights and rear stance while avoiding lot clutter.",
    },
    {
      title: "Rear Hero",
      guidance: "Center the vehicle and keep horizon straight.",
    },
    {
      title: "Wheel Close-Up",
      guidance: "Move closer and angle slightly downward for depth.",
    },
    {
      title: "Driver Cockpit",
      guidance: "Focus steering wheel and dashboard. Avoid overexposed screens.",
    },
    {
      title: "Infotainment",
      guidance: "Reduce reflections and tap screen brightness up if needed.",
    },
    {
      title: "Rear Seats",
      guidance: "Use wide framing and keep seats clean and centered.",
    },
    {
      title: "Cargo / Trunk",
      guidance: "Capture full cargo opening with enough lighting.",
    },
  ];

  const analyzeCaptureQuality = () => {
    const guidance = [];

    if (images.filter(Boolean).length < 30) {
      guidance.push({ type: "warning", text: "Capture at least 30 images for smooth 360 spin quality." });
    }

    guidance.push({ type: "tip", text: "Keep camera height consistent between shots." });
    guidance.push({ type: "tip", text: "Avoid reflections and harsh shadows on glossy paint." });
    guidance.push({ type: "tip", text: "Use front 3/4 angles for strongest engagement." });

    if (images.filter(Boolean).length > 60) {
      guidance.push({ type: "success", text: "Excellent frame coverage for premium 360 motion." });
    }

    setCaptureGuidance(guidance);
  };

  const nextCaptureShot = () => {
    setCurrentShotIndex((prev) =>
      prev + 1 >= captureSequence.length ? 0 : prev + 1
    );
  };

  const generateCreatorExportPackage = async () => {
    const hooksText = viralHooks.map((h) => `• ${h.type}: ${h.text}`).join("\n");
    const subtitles = Array.isArray(voiceoverScript?.subtitles)
  ? voiceoverScript.subtitles.join("\n")
  : "";
    const hashtags = socialContent?.tiktokCaption
      ?.split(" ")
      .filter((word: string) => word.startsWith("#"))
      .join(" ") || "#DriveWithArturo #ChicagoCars #CarTok";

    const caption = socialContent?.tiktokCaption || "Vehicle available now at Chicago Cars US.";

    let thumbnail = "";

    try {
      const firstImage = images.find(Boolean);

      if (firstImage) {
        const canvas = document.createElement("canvas");
        canvas.width = 1080;
        canvas.height = 1920;

        const ctx = canvas.getContext("2d");

        if (ctx) {
          const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = "anonymous";
            image.onload = () => resolve(image);
            image.onerror = reject;
            image.src = firstImage;
          });

          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          const overlay = ctx.createLinearGradient(0, 0, 0, canvas.height);
          overlay.addColorStop(0, "rgba(0,0,0,0.15)");
          overlay.addColorStop(1, "rgba(0,0,0,0.72)");
          ctx.fillStyle = overlay;
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          ctx.fillStyle = "#d4af37";
          ctx.font = "900 88px Arial";
          ctx.textAlign = "center";
          ctx.shadowColor = "rgba(212,175,55,0.85)";
          ctx.shadowBlur = 30;
          ctx.fillText(vehicleName || "AVAILABLE NOW", canvas.width / 2, canvas.height * 0.82);

          ctx.shadowBlur = 0;
          ctx.fillStyle = "#ffffff";
          ctx.font = "700 48px Arial";
          ctx.fillText(vehiclePrice || "FINANCING AVAILABLE", canvas.width / 2, canvas.height * 0.89);

          thumbnail = canvas.toDataURL("image/jpeg", 0.92);
        }
      }
    } catch (err) {
      console.error(err);
    }

    setExportPackage({
      caption,
      hashtags,
      hooksText,
      subtitles,
      thumbnail,
      reelUrl: renderedReelUrl,
      exportMode: "TikTok/Reels/Shorts",
    });

    pushActivityAlert(
      "Creator Export Package Generated",
      `${vehicleName || "Vehicle"} social media package is ready for export`,
      "LOW"
    );
  };

  const generateVoiceoverAndHooks = () => {
    const name = vehicleName || "This vehicle";
    const price = vehiclePrice || "Financing available";
    const lower = name.toLowerCase();

    const isLuxury = /(bmw|audi|mercedes|lexus|cadillac|porsche|infiniti|acura)/i.test(lower);
    const isTruck = /(silverado|f-150|ram|tundra|sierra|tacoma)/i.test(lower);
    const isSport = /(s2000|s5|mustang|camaro|corvette|m3|m4|supra|type r)/i.test(lower);

    let style = "professional";
    if (isLuxury) style = "luxury";
    if (isTruck) style = "truck";
    if (isSport) style = "enthusiast";

    const hooks = [
      {
        type: "Shock Hook",
        text: `Nobody expects ${name} to look this clean for ${price}...`,
        audio: "cinematic bass drop",
      },
      {
        type: "POV Hook",
        text: `POV: You finally found the right ${name}.`,
        audio: "slow atmospheric beat",
      },
      {
        type: "Closer Hook",
        text: `Stop scrolling — this ${name} will not last long.`,
        audio: "fast TikTok transition beat",
      },
      {
        type: "Hispanic Community Hook",
        text: `Financiamiento disponible aunque tengas crédito difícil.`,
        audio: "Latin urban intro",
      },
    ];

    let narration = `Introducing the ${name}, available now at Chicago Cars US in Summit, Illinois.`;

    if (style === "luxury") {
      narration += ` Experience premium luxury, aggressive styling, and refined performance for ${price}.`;
    } else if (style === "truck") {
      narration += ` Built for both work and lifestyle, this truck is ready for the road ahead.`;
    } else if (style === "enthusiast") {
      narration += ` Built for real enthusiasts, this machine delivers performance, sound, and presence.`;
    }

    narration += ` Financing available. Trade-ins welcome. Text Arturo at (872) 364-0886 today.`;

    const subtitles = narration
      .split(".")
      .map((line) => line.trim())
      .filter(Boolean);

    setVoiceoverScript({
      style,
      narration,
      subtitles,
    });

    setViralHooks(hooks);

    pushActivityAlert(
      "Voiceover + Viral Hooks Generated",
      `${name} reel intro hooks and narration created`,
      "LOW"
    );
  };

  const generateSocialContent = () => {
    const name = vehicleName || "Vehicle";
    const price = vehiclePrice || "Financing Available";
    const lower = name.toLowerCase();

    const isLuxury = /(bmw|audi|mercedes|lexus|cadillac|porsche|infiniti|acura)/i.test(lower);
    const isTruck = /(silverado|f-150|ram|tundra|sierra|tacoma)/i.test(lower);
    const isSport = /(s2000|s5|mustang|camaro|corvette|m3|m4|supra|type r)/i.test(lower);

    let vibe = "clean daily driver";

    if (isLuxury) vibe = "luxury lifestyle";
    if (isTruck) vibe = "work + lifestyle truck";
    if (isSport) vibe = "enthusiast performance build";

    const tiktokCaption = isSport
      ? `${name} just landed 😮‍💨🔥 ${price}. Built for real drivers, not NPC traffic. Who’s taking this home? Text Arturo at (872) 364-0886. #ChicagoCars #DriveWithArturo #CarTok #JDM #UsedCars`
      : `${name} available now for ${price} 🔥 Financing available. Trade-ins welcome. Text Arturo at (872) 364-0886 to book a test drive today. #ChicagoCars #DriveWithArturo #CarTok #UsedCars #Chicago`;

    const instagramCaption = `Experience ${vibe} with this ${name}. Available now at Chicago Cars US in Summit, IL. Financing options available including first-time buyers and challenged credit. Hablamos Español. Text Arturo at (872) 364-0886.`;

    const facebookDescription = `${name} available now at Chicago Cars US in Summit, Illinois.

Price: ${price}

✔ Financing Available
✔ Trade-Ins Welcome
✔ First-Time Buyer Programs
✔ ITIN / Matricula Friendly
✔ Extended Warranty Options
✔ Hablamos Español

Message Arturo today at (872) 364-0886 for availability and out-the-door pricing.`;

    const whatsappPitch = `${name} available now for ${price}. Financing available and trade-ins welcome. Text Arturo at (872) 364-0886 for details.`;

    const englishAlt = `${name} photographed at Chicago Cars US in Summit IL with financing available. DriveWithArturo used car inventory near Chicago.`;

    const spanishAlt = `${name} disponible en Chicago Cars US en Summit Illinois con financiamiento disponible. Inventario de autos usados cerca de Chicago por DriveWithArturo.`;

    const overlays = [
      "AVAILABLE NOW",
      "FINANCING AVAILABLE",
      "TRADE-INS WELCOME",
      "HABLAMOS ESPAÑOL",
      "TEXT ARTURO",
      "BOOK A TEST DRIVE",
    ];

    setSocialContent({
      tiktokCaption,
      instagramCaption,
      facebookDescription,
      whatsappPitch,
      englishAlt,
      spanishAlt,
      overlays,
    });

    pushActivityAlert(
      "Social Media Content Generated",
      `${name} marketing captions and SEO copy created`,
      "LOW"
    );
  };

  const [scheduledPosts, setScheduledPosts] = useState<any[]>([]);
  const [scheduledDate, setScheduledDate] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState("TikTok");
  const [crmLeads, setCrmLeads] = useState<any[]>([]);
  const [dealJackets, setDealJackets] = useState<any[]>([]);
  const [inventoryAcquisitions, setInventoryAcquisitions] = useState<any[]>([]);
  const [managerDashboard, setManagerDashboard] = useState({
    totalLeads: 0,
    appointments: 0,
    soldUnits: 0,
    closingRatio: 0,
    frontGross: 0,
    backendGross: 0,
    avgGrossPerCopy: 0,
    reviewsGenerated: 0,
  });
  const [acquisitionForm, setAcquisitionForm] = useState({
    vehicle: "",
    source: "Trade-In",
    acv: "",
    recon: "",
    pack: "995",
    marketValue: "",
    targetRetail: "",
    mileage: "",
  });
  const [dealForm, setDealForm] = useState({
    customer: "",
    vehicle: "",
    salePrice: "",
    tradeValue: "",
    payoff: "",
    downPayment: "",
    apr: "12.9",
    term: "72",
    gap: false,
    warranty: false,
  });
  const [crmForm, setCrmForm] = useState({
    name: "",
    phone: "",
    vehicle: "",
    notes: "",
    stage: "New Lead",
  });

  const [serviceReconVehicles, setServiceReconVehicles] = useState<any[]>([]);
  const [pricingInsights, setPricingInsights] = useState<any[]>([]);
  const [customerAccounts, setCustomerAccounts] = useState<any[]>([]);
  const [voiceCalls, setVoiceCalls] = useState<any[]>([]);
  const [digitalRetailDeals, setDigitalRetailDeals] = useState<any[]>([]);
  const [mediaStudioProjects, setMediaStudioProjects] = useState<any[]>([]);
  const [arShowrooms, setArShowrooms] = useState<any[]>([]);
  const [aiCommandInput, setAiCommandInput] = useState("");
  const [aiCommandLogs, setAiCommandLogs] = useState<any[]>([]);
  const [mobileLotSessions, setMobileLotSessions] = useState<any[]>([]);
  const [mobileLotForm, setMobileLotForm] = useState({
    vehicle: "",
    vin: "",
    condition: "Clean",
    signalMode: "Offline Lot Mode",
    captureType: "Walkaround Video",
  });
  const [arForm, setArForm] = useState({
    vehicle: "",
    environment: "Luxury Showroom",
    walkthroughMode: "AI Guided",
    hotspotTheme: "Performance",
  });
  const [mediaStudioForm, setMediaStudioForm] = useState({
    vehicle: "",
    scene: "Chicago Skyline",
    style: "Luxury",
    format: "TikTok 9:16",
    payment: "",
  });
  const [digitalRetailForm, setDigitalRetailForm] = useState({
    customer: "",
    vehicle: "",
    salePrice: "",
    downPayment: "2000",
    apr: "11.9",
    term: "72",
    warranty: false,
    gap: false,
    tradeValue: "",
    tradePayoff: "",
  });
  const [voiceAssistantForm, setVoiceAssistantForm] = useState({
    customer: "",
    phone: "",
    language: "English",
    topic: "Inventory",
    personality: "Professional",
  });
  const [customerPortalForm, setCustomerPortalForm] = useState({
    name: "",
    phone: "",
    email: "",
    savedVehicle: "",
    financingStatus: "Submitted",
  });
  const [pricingForm, setPricingForm] = useState({
    vehicle: "",
    mileage: "",
    askingPrice: "",
    marketAverage: "",
    inventoryAge: "",
    category: "SUV",
    drivetrain: "AWD",
  });
  const [serviceForm, setServiceForm] = useState({
    vehicle: "",
    status: "Fresh Trade",
    labor: "",
    parts: "",
    vendor: "",
    notes: "",
  });

  const createMobileLotSession = () => {
    const session = {
      id: Date.now(),
      ...mobileLotForm,
      aiDetectedFeatures: [
        "Leather Seats",
        "Apple CarPlay",
        "AWD System",
        "Premium Package",
      ],
      generatedActions: [
        "CRM Lead Ready",
        "Media Studio Upload Ready",
        "VIN Decoded",
        "Social Clip Generated",
      ],
      syncStatus:
        mobileLotForm.signalMode === "Offline Lot Mode"
          ? "Pending Sync"
          : "Cloud Synced",
      createdAt: new Date().toLocaleString(),
    };

    setMobileLotSessions((prev) => [session, ...prev]);

    pushActivityAlert(
      "Mobile Lot Session Created",
      `${mobileLotForm.vehicle || "Vehicle"} captured in mobile workflow`,
      "HOT"
    );
  };

  const runAiDealershipCommand = () => {
    if (!aiCommandInput.trim()) return;

    let response = "AI analysis complete. Monitoring dealership operations.";

    const command = aiCommandInput.toLowerCase();

    if (command.includes("hot leads")) {
      response = "3 hot leads require immediate follow-up. Two customers requested financing information within the last hour.";
    } else if (command.includes("aged inventory")) {
      response = "5 vehicles exceed 45 days in inventory. Recommend pricing adjustment on luxury SUV segment.";
    } else if (command.includes("campaign")) {
      response = "Generated seasonal marketing campaign with bilingual TikTok and Instagram assets.";
    } else if (command.includes("gross")) {
      response = "Luxury inventory currently generating highest projected front-end gross opportunities.";
    } else if (command.includes("appointments")) {
      response = "Appointment show rate strongest between 11 AM and 4 PM this week.";
    }

    const log = {
      id: Date.now(),
      command: aiCommandInput,
      response,
      createdAt: new Date().toLocaleString(),
    };

    setAiCommandLogs((prev) => [log, ...prev]);
    setAiCommandInput("");

    pushActivityAlert(
      "AI Operating System Executed",
      response,
      "HOT"
    );
  };

  const createARShowroomExperience = () => {
    const showroom = {
      id: Date.now(),
      ...arForm,
      arFeatures: [
        "360° Vehicle Walkaround",
        "Interactive Interior Hotspots",
        "Payment Overlay Display",
        "AI Voice Guided Tour",
      ],
      socialSharing: [
        "TikTok AR Reel",
        "Instagram AR Story",
        "Dream Garage Screenshot",
      ],
      aiNarration:
        "Welcome to your immersive AR vehicle experience powered by DriveWithArturo AI.",
      createdAt: new Date().toLocaleString(),
    };

    setArShowrooms((prev) => [showroom, ...prev]);

    pushActivityAlert(
      "AR Showroom Experience Created",
      `${arForm.vehicle || "Vehicle"} AR environment generated`,
      "HOT"
    );
  };

  const generateMediaStudioProject = () => {
    const overlays = [
      "Hablamos Español",
      "Easy Financing",
      "$0 Down Options",
      "Drive Today",
    ];

    const project = {
      id: Date.now(),
      ...mediaStudioForm,
      generatedAssets: [
        "TikTok Cover",
        "Instagram Reel",
        "Facebook Banner",
        "Vertical Story",
      ],
      aiEnhancements: [
        "Background Removed",
        "Cinematic Lighting",
        "Depth Overlay Effects",
        "Showroom Reflection",
      ],
      overlays,
      createdAt: new Date().toLocaleString(),
    };

    setMediaStudioProjects((prev) => [project, ...prev]);

    pushActivityAlert(
      "AI Media Studio Project Generated",
      `${mediaStudioForm.vehicle || "Vehicle"} media assets created`,
      "HOT"
    );
  };

  const createDigitalRetailDeal = () => {
    const salePrice = Number(digitalRetailForm.salePrice || 0);
    const downPayment = Number(digitalRetailForm.downPayment || 0);
    const apr = Number(digitalRetailForm.apr || 0);
    const term = Number(digitalRetailForm.term || 72);
    const tradeValue = Number(digitalRetailForm.tradeValue || 0);
    const tradePayoff = Number(digitalRetailForm.tradePayoff || 0);

    const equity = tradeValue - tradePayoff;

    const backend =
      (digitalRetailForm.warranty ? 2495 : 0) +
      (digitalRetailForm.gap ? 1295 : 0);

    const financedAmount =
      salePrice + backend - downPayment - Math.max(equity, 0);

    const monthlyRate = apr / 100 / 12;

    const estimatedPayment = monthlyRate === 0
      ? financedAmount / term
      : financedAmount * (monthlyRate * Math.pow(1 + monthlyRate, term)) /
        (Math.pow(1 + monthlyRate, term) - 1);

    const deal = {
      id: Date.now(),
      ...digitalRetailForm,
      equity,
      backend,
      financedAmount,
      estimatedPayment,
      financingStatus: "Soft Approval Ready",
      reservationStatus: "Vehicle Reservable",
      createdAt: new Date().toLocaleString(),
    };

    setDigitalRetailDeals((prev) => [deal, ...prev]);

    pushActivityAlert(
      "Digital Retail Deal Generated",
      `${digitalRetailForm.customer || "Customer"} completed online structuring`,
      "HOT"
    );
  };

  const createVoiceAssistantCall = () => {
    const responses: Record<string, string> = {
      Inventory: "We have several financing options available and inventory ready for immediate delivery.",
      Financing: "We work with all credit types and can help explore approval options.",
      Trade: "We accept trade-ins and can help estimate your vehicle value.",
      Appointment: "Let's schedule your dealership appointment today.",
    };

    const call = {
      id: Date.now(),
      ...voiceAssistantForm,
      status: "Call Completed",
      duration: `${Math.floor(Math.random() * 6) + 2} min`,
      outcome: Math.random() > 0.4 ? "Appointment Set" : "Lead Follow-Up Needed",
      aiResponse:
        responses[voiceAssistantForm.topic] ||
        "Thank you for contacting Chicago Cars US.",
      createdAt: new Date().toLocaleString(),
    };

    setVoiceCalls((prev) => [call, ...prev]);

    pushActivityAlert(
      "AI Voice Assistant Call Logged",
      `${voiceAssistantForm.customer || "Customer"} handled via AI assistant`,
      call.outcome === "Appointment Set" ? "HOT" : "LOW"
    );
  };

  const createCustomerPortalAccount = () => {
    const account = {
      id: Date.now(),
      ...customerPortalForm,
      viewedVehicles: [customerPortalForm.savedVehicle || "Inventory Vehicle"],
      engagementScore: Math.floor(Math.random() * 40) + 60,
      recommendations: [
        "Lower payment alternatives available",
        "Similar AWD inventory available",
        "Price drop alerts enabled",
      ],
      createdAt: new Date().toLocaleString(),
    };

    setCustomerAccounts((prev) => [account, ...prev]);

    pushActivityAlert(
      "Customer Portal Account Created",
      `${customerPortalForm.name || "Customer"} added to customer ecosystem`,
      "HOT"
    );
  };

  const analyzeInventoryPricing = () => {
    const mileage = Number(pricingForm.mileage || 0);
    const askingPrice = Number(pricingForm.askingPrice || 0);
    const marketAverage = Number(pricingForm.marketAverage || 0);
    const inventoryAge = Number(pricingForm.inventoryAge || 0);

    let marketHeat = "HOT MARKET";
    let recommendation = "Pricing is highly competitive.";
    let projectedTurn = "14-21 Days";
    let risk = "LOW";

    const priceDifference = askingPrice - marketAverage;

    if (priceDifference > 1500) {
      marketHeat = "OVERPRICED";
      recommendation = `Reduce pricing by approximately $${priceDifference.toLocaleString()} to improve engagement.`;
      projectedTurn = "45-60 Days";
      risk = "MEDIUM";
    }

    if (inventoryAge > 45) {
      recommendation = "Inventory aging indicates pricing adjustment recommended.";
      projectedTurn = "60+ Days";
      risk = "HIGH";
    }

    if (pricingForm.drivetrain === "AWD" && pricingForm.category === "SUV") {
      marketHeat = "HIGH AWD DEMAND";
    }

    const insight = {
      id: Date.now(),
      ...pricingForm,
      marketHeat,
      recommendation,
      projectedTurn,
      risk,
      projectedSaleProbability:
        risk === "HIGH" ? "48%" : risk === "MEDIUM" ? "67%" : "84%",
      estimatedTargetPrice:
        marketAverage > 0 ? marketAverage - 500 : askingPrice,
      createdAt: new Date().toLocaleString(),
    };

    setPricingInsights((prev) => [insight, ...prev]);

    pushActivityAlert(
      "AI Pricing Analysis Generated",
      `${pricingForm.vehicle || "Vehicle"} market intelligence updated`,
      risk === "HIGH" ? "WARM" : "LOW"
    );
  };

  const addServiceReconVehicle = () => {
    const labor = Number(serviceForm.labor || 0);
    const parts = Number(serviceForm.parts || 0);
    const totalRecon = labor + parts;

    let aiAlert = "Vehicle progressing normally.";

    if (totalRecon > 3500) {
      aiAlert = "Recon exceeds average target. Review profitability before frontline approval.";
    }

    if (serviceForm.status === "Photo Pending") {
      aiAlert = "Vehicle ready for media production and frontline marketing.";
    }

    const item = {
      id: Date.now(),
      ...serviceForm,
      totalRecon,
      aiAlert,
      createdAt: new Date().toLocaleString(),
    };

    setServiceReconVehicles((prev) => [item, ...prev]);

    pushActivityAlert(
      "Recon Workflow Updated",
      `${serviceForm.vehicle || "Vehicle"} moved into ${serviceForm.status}`,
      totalRecon > 3500 ? "WARM" : "LOW"
    );
  };

  const dealershipInsights = [
    "No store data yet. Add leads, inventory, pricing, and sales activity to generate insights.",
    "Dashboard starts at zero so all analytics reflect your real live usage.",
    "Use CRM, OTD Desk, Pricing Engine, and Media Studio to build performance history.",
  ];

  const salespersonPerformance = [
    {
      name: "Arturo Garza",
      sold: 0,
      appointments: 0,
      closing: 0,
      reviews: 0,
    },
  ];

  const inventoryAging = [
    { bucket: "0-15 Days", units: 0, color: "#22c55e" },
    { bucket: "16-30 Days", units: 0, color: "#facc15" },
    { bucket: "31-60 Days", units: 0, color: "#fb923c" },
    { bucket: "60+ Days", units: 0, color: "#ef4444" },
  ];

  const evaluateInventoryAcquisition = () => {
    const acv = Number(acquisitionForm.acv || 0);
    const recon = Number(acquisitionForm.recon || 0);
    const pack = Number(acquisitionForm.pack || 0);
    const marketValue = Number(acquisitionForm.marketValue || 0);
    const targetRetail = Number(acquisitionForm.targetRetail || 0);
    const mileage = Number(acquisitionForm.mileage || 0);

    const totalInvestment = acv + recon + pack;
    const projectedGross = targetRetail - totalInvestment;

    let risk = "LOW";
    let score = "HOT ACQUISITION";

    if (projectedGross < 2500) {
      risk = "MEDIUM";
      score = "SAFE BUY";
    }

    if (projectedGross < 1200 || mileage > 140000) {
      risk = "HIGH";
      score = "WHOLESALE ONLY";
    }

    const appraisal = {
      id: Date.now(),
      ...acquisitionForm,
      totalInvestment,
      projectedGross,
      risk,
      score,
      createdAt: new Date().toLocaleString(),
    };

    setInventoryAcquisitions((prev) => [appraisal, ...prev]);

    pushActivityAlert(
      "Inventory Acquisition Evaluated",
      `${acquisitionForm.vehicle || "Vehicle"} classified as ${score}`,
      risk === "HIGH" ? "WARM" : "HOT"
    );
  };

  const createDealJacket = () => {
    const salePrice = Number(dealForm.salePrice || 0);
    const tradeValue = Number(dealForm.tradeValue || 0);
    const payoff = Number(dealForm.payoff || 0);
    const downPayment = Number(dealForm.downPayment || 0);
    const apr = Number(dealForm.apr || 0);
    const term = Number(dealForm.term || 72);

    const tradeEquity = tradeValue - payoff;
    const taxableAmount = Math.max(salePrice - Math.max(tradeEquity, 0), 0);
    const estimatedTax = taxableAmount * 0.095;

    const backendProducts =
      (dealForm.gap ? 1295 : 0) +
      (dealForm.warranty ? 2495 : 0);

    const amountFinanced =
      salePrice + estimatedTax + backendProducts - downPayment - Math.max(tradeEquity, 0);

    const monthlyRate = apr / 100 / 12;

    const payment = monthlyRate === 0
      ? amountFinanced / term
      : amountFinanced * (monthlyRate * Math.pow(1 + monthlyRate, term)) /
        (Math.pow(1 + monthlyRate, term) - 1);

    const frontGross = salePrice * 0.08;
    const backendGross = backendProducts * 0.55;
    const estimatedCommission = (frontGross + backendGross) * 0.25;

    const deal = {
      id: Date.now(),
      ...dealForm,
      tradeEquity,
      estimatedTax,
      backendProducts,
      amountFinanced,
      payment,
      frontGross,
      backendGross,
      estimatedCommission,
      createdAt: new Date().toLocaleString(),
    };

    setDealJackets((prev) => [deal, ...prev]);

    pushActivityAlert(
      "Deal Jacket Created",
      `${dealForm.customer || "Customer"} desked successfully`,
      "HOT"
    );
  };

  const holidayCampaigns = [
    {
      name: "Memorial Day Sales Event",
      season: "Spring",
      hook: "Huge Memorial Day savings are here 🇺🇸",
      sms: "Memorial Day specials are live at Chicago Cars US 🇺🇸 Financing available and trade-ins welcome. Text Arturo at (872) 364-0886.",
      category: "Truck / SUV",
    },
    {
      name: "Tax Season Approval Event",
      season: "Spring",
      hook: "Use your tax refund as a down payment 💰",
      sms: "Tax season is one of the best times to upgrade your vehicle. Ask about low down payment options today.",
      category: "Financing",
    },
    {
      name: "Fourth of July Event",
      season: "Summer",
      hook: "Red, white, and horsepower 🇺🇸🔥",
      sms: "Celebrate Fourth of July with special financing offers and upgraded inventory availability.",
      category: "Performance",
    },
    {
      name: "Winter AWD Campaign",
      season: "Winter",
      hook: "Chicago winter ready ❄️ AWD season is here",
      sms: "Stay ready for snow season with AWD SUVs and winter-ready vehicles available now.",
      category: "AWD / SUV",
    },
    {
      name: "Cinco de Mayo Event",
      season: "Spring",
      hook: "Celebrando con ofertas increíbles 🇲🇽🔥",
      sms: "Hablamos español y tenemos especiales disponibles este Cinco de Mayo.",
      category: "Hispanic Marketing",
    },
  ];

  const addCrmLead = () => {
    if (!crmForm.name || !crmForm.phone) {
      alert("Customer name and phone are required.");
      return;
    }

    const lead = {
      id: Date.now(),
      ...crmForm,
      createdAt: new Date().toLocaleString(),
      lastContact: "Just now",
      aiSuggestion:
        "Follow up within 15 minutes and invite customer for a test drive.",
    };

    setCrmLeads((prev) => [lead, ...prev]);

    setCrmForm({
      name: "",
      phone: "",
      vehicle: "",
      notes: "",
      stage: "New Lead",
    });

    pushActivityAlert(
      "CRM Lead Added",
      `${lead.name} added to dealership pipeline`,
      "HOT"
    );
  };

  const moveLeadStage = (id: number, stage: string) => {
    setCrmLeads((prev) =>
      prev.map((lead) =>
        lead.id === id
          ? {
              ...lead,
              stage,
              lastContact: new Date().toLocaleString(),
            }
          : lead
      )
    );
  };

  const scheduleContentPost = () => {
    const post = {
      id: Date.now(),
      vehicle: vehicleName || "Vehicle",
      platform: selectedPlatform,
      date: scheduledDate || new Date().toISOString(),
      caption:
        socialContent?.tiktokCaption ||
        `${vehicleName || "Vehicle"} available now at Chicago Cars US 🔥`,
      hook:
        viralHooks?.[0]?.text ||
        `This ${vehicleName || "vehicle"} is built different 🔥`,
      thumbnail: backgroundPreview || images?.[0] || "",
      status: "Scheduled",
    };

    setScheduledPosts((prev) => [post, ...prev]);

    pushActivityAlert(
      "Content Scheduled",
      `${vehicleName || "Vehicle"} scheduled for ${selectedPlatform}`,
      "HOT"
    );
  };

  const generateSeoVehiclePage = () => {
    const slug = safeSlug(vehicleName || "vehicle");
    const title = `${vehicleName || "Vehicle"} for Sale in Summit, IL | Chicago Cars US`;
    const description = `${vehicleName || "Vehicle"} available now at Chicago Cars US in Summit, Illinois. Financing available for first-time buyers, challenged credit, ITIN customers, and trade-ins. Contact Arturo Garza today.`;

    const keywords = [
      vehicleName,
      "Used Cars Chicago",
      "Summit IL Cars",
      "Chicago Cars US",
      "Bad Credit Financing",
      "ITIN Car Financing",
      "Hablamos Español",
      "DriveWithArturo",
    ]
      .filter(Boolean)
      .join(", ");

    const schema = {
      "@context": "https://schema.org",
      "@type": "Vehicle",
      name: vehicleName || "Vehicle",
      brand: vehicleName?.split(" ")[1] || "Vehicle",
      description,
      offers: {
        "@type": "Offer",
        price: vehiclePrice || "Contact Dealer",
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
      },
    };

    setSeoPreview({
      slug,
      url: `${window.location.origin}/view/${slug}`,
      title,
      description,
      keywords,
      schema,
    });

    pushActivityAlert(
      "SEO Vehicle Page Generated",
      `${vehicleName || "Vehicle"} SEO landing page created`,
      "LOW"
    );
  };

  const renderDepthOverlayText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    canvasWidth: number,
    canvasHeight: number
  ) => {
    const fontSize = Math.max(52, canvasWidth * 0.065);

    ctx.save();
    ctx.font = `900 ${fontSize}px Arial`;
    ctx.textAlign = "center";

    ctx.globalAlpha = 0.25;
    ctx.fillStyle = "#000";
    ctx.fillText(text, canvasWidth / 2, canvasHeight * 0.78);

    ctx.globalAlpha = 1;
    ctx.shadowColor = "rgba(212,175,55,0.85)";
    ctx.shadowBlur = 30;
    ctx.fillStyle = "#d4af37";
    ctx.fillText(text, canvasWidth / 2, canvasHeight * 0.775);

    ctx.shadowBlur = 0;
    ctx.lineWidth = 2;
    ctx.strokeStyle = "rgba(255,255,255,0.55)";
    ctx.strokeText(text, canvasWidth / 2, canvasHeight * 0.775);

    ctx.fillStyle = "rgba(0,0,0,0.22)";
    ctx.fillRect(0, canvasHeight * 0.76, canvasWidth * 0.18, 120);
    ctx.fillRect(canvasWidth * 0.82, canvasHeight * 0.76, canvasWidth * 0.18, 120);
    ctx.restore();
  };

  const renderTrueSegmentationOverlay = (
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    text: string,
    canvasWidth: number,
    canvasHeight: number,
    progress = 0
  ) => {
    const subjectCanvas = document.createElement("canvas");
    subjectCanvas.width = canvasWidth;
    subjectCanvas.height = canvasHeight;
    const subjectCtx = subjectCanvas.getContext("2d");
    if (!subjectCtx) return;

    const scale = 1.04 + Math.sin(progress * Math.PI) * 0.025;
    const drawWidth = canvasWidth * scale;
    const drawHeight = canvasHeight * scale;
    const drawX = -(drawWidth - canvasWidth) / 2;
    const drawY = -(drawHeight - canvasHeight) / 2;

    subjectCtx.drawImage(img, drawX, drawY, drawWidth, drawHeight);

    const imageData = subjectCtx.getImageData(0, 0, canvasWidth, canvasHeight);
    const data = imageData.data;
    const centerX = canvasWidth / 2;
    const centerY = canvasHeight * 0.54;
    const radiusX = canvasWidth * 0.46;
    const radiusY = canvasHeight * 0.34;

    for (let y = 0; y < canvasHeight; y += 1) {
      for (let x = 0; x < canvasWidth; x += 1) {
        const i = (y * canvasWidth + x) * 4;
        const dx = (x - centerX) / radiusX;
        const dy = (y - centerY) / radiusY;
        const ellipse = dx * dx + dy * dy;

        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const brightness = (r + g + b) / 3;
        const saturation = Math.max(r, g, b) - Math.min(r, g, b);

        const likelySubject = ellipse < 1.05 && (brightness > 25 || saturation > 18);
        data[i + 3] = likelySubject ? 255 : 0;
      }
    }

    subjectCtx.putImageData(imageData, 0, 0);

    ctx.save();
    ctx.font = `950 ${Math.max(64, canvasWidth * 0.075)}px Arial`;
    ctx.textAlign = "center";
    ctx.shadowColor = "rgba(212,175,55,0.9)";
    ctx.shadowBlur = 38;
    ctx.fillStyle = "#d4af37";
    ctx.fillText(text.toUpperCase(), canvasWidth / 2, canvasHeight * 0.52);
    ctx.shadowBlur = 0;
    ctx.lineWidth = 4;
    ctx.strokeStyle = "rgba(255,255,255,0.5)";
    ctx.strokeText(text.toUpperCase(), canvasWidth / 2, canvasHeight * 0.52);

    ctx.globalAlpha = 0.95;
    ctx.drawImage(subjectCanvas, 0, 0);

    ctx.globalAlpha = 1;
    const fog = ctx.createLinearGradient(0, canvasHeight * 0.66, 0, canvasHeight);
    fog.addColorStop(0, "rgba(255,255,255,0)");
    fog.addColorStop(1, "rgba(212,175,55,0.16)");
    ctx.fillStyle = fog;
    ctx.fillRect(0, canvasHeight * 0.66, canvasWidth, canvasHeight * 0.34);
    ctx.restore();
  };

  const generateAiLeadReply = () => {
    const message = aiLeadMessage.toLowerCase();

    let reply = "Thanks for reaching out. Financing options are available. What vehicle are you interested in and about how much were you looking to put down?";
    let temperature = "COLD";

    if (message.includes("bad credit") || message.includes("repos") || message.includes("credit")) {
      reply = "Many customers with challenged credit still get approved depending on income, down payment, and trade equity. About how much were you looking to put down?";
      temperature = "WARM";
    }

    if (message.includes("monthly payment") || message.includes("payment")) {
      reply = "What monthly range are you trying to stay around? We have several financing options available depending on credit and down payment.";
      temperature = "HOT";
    }

    if (message.includes("still available") || message.includes("available")) {
      reply = 'Yes, ' + (vehicleName || 'the vehicle') + ' is currently available. Would you like to schedule a test drive today or tomorrow?';
      temperature = "HOT";
    }

    if (message.includes("trade") || message.includes("trade-in")) {
      reply = "We do accept trade-ins. What year, make, model, and mileage is your trade?";
      temperature = "WARM";
    }

    if (message.includes("itin") || message.includes("matricula")) {
      reply = "Yes, we work with ITIN and matrícula customers depending on the full application structure. How much were you planning to put down?";
      temperature = "HOT";
    }

    if (message.includes("hablo") || message.includes("español")) {
      reply = "Sí hablamos español. ¿Cuánto piensa dar de enganche y qué tipo de vehículo busca?";
      temperature = "HOT";
    }

    const entry = {
      customer: aiLeadMessage,
      ai: reply,
      time: new Date().toLocaleTimeString(),
      temperature,
    };

    setLeadTemperature(temperature);
    setAiConversation((prev) => [entry, ...prev]);

    pushActivityAlert(
      "AI Lead Assistant Reply Generated",
      `Lead classified as ${temperature}`,
      temperature as any
    );
  };

  const runFinanceAssistant = () => {
    const price = Number((vehiclePrice || "0").replace(/[^0-9.]/g, "")) || 0;
    const credit = Number(financeAssistant.creditScore || 0);
    const down = Number(financeAssistant.downPayment || 0);
    const trade = Number(financeAssistant.tradeValue || 0);
    const budget = Number(financeAssistant.monthlyBudget || 0);

    let apr = 18.9;
    let approval = "Needs Review";
    let program = "Standard Financing";

    if (credit >= 720) {
      apr = 6.9;
      approval = "Strong Approval Odds";
      program = "Prime Financing";
    } else if (credit >= 640) {
      apr = 11.9;
      approval = "Good Approval Odds";
      program = "Near-Prime Financing";
    } else if (credit >= 560) {
      apr = 16.9;
      approval = "Possible Approval";
      program = "Second Chance Financing";
    } else {
      apr = 22.9;
      approval = "Requires Strong Structure";
      program = "Credit Rebuilding Program";
    }

    if (financeAssistant.firstTimeBuyer) {
      program = "First-Time Buyer Program";
    }

    if (financeAssistant.itin) {
      program += " + ITIN Friendly";
    }

    const estimatedOtd = price * 1.12;
    const financedAmount = Math.max(estimatedOtd - down - trade, 0);
    const term = 72;
    const monthlyRate = apr / 100 / 12;

    const payment = monthlyRate === 0
      ? financedAmount / term
      : financedAmount * (monthlyRate * Math.pow(1 + monthlyRate, term)) /
        (Math.pow(1 + monthlyRate, term) - 1);

    const affordable = budget > 0
      ? payment <= budget
      : true;

    let recommendation = "Vehicle structure looks reasonable.";

    if (!affordable && budget > 0) {
      recommendation = "Increase down payment or lower vehicle price to meet target budget.";
    }

    if (down < 1000) {
      recommendation += " Stronger down payment may improve approval chances.";
    }

    setFinanceEstimate({
      apr,
      approval,
      program,
      estimatedOtd,
      financedAmount,
      payment,
      affordable,
      recommendation,
    });

    pushActivityAlert(
      "AI Financing Estimate Generated",
      `${vehicleName || "Vehicle"} financing prequalification created`,
      credit >= 640 ? "HOT" : "WARM"
    );
  };

  const generateSeoVehiclePageDriveWithArturo = () => {
    const slug = safeSlug(vehicleName || "vehicle");

    const seoTitle = `${vehicleName || "Vehicle"} for Sale in Summit, IL | DriveWithArturo`;

    const seoDescription = `${vehicleName || "Vehicle"} available now at Chicago Cars US in Summit, Illinois. Financing available, trade-ins welcome, and bilingual assistance. ${featureBadges.slice(0, 4).join(", ")}.`;

    const localKeywords = [
      `${vehicleName} Chicago`,
      `${vehicleName} Summit IL`,
      `Used ${vehicleName}`,
      `Bad credit financing Chicago`,
      `Hablamos Español autos Chicago`,
      `DriveWithArturo`,
    ];

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width,initial-scale=1.0" />
<title>${seoTitle}</title>
<meta name="description" content="${seoDescription}" />
<meta property="og:title" content="${seoTitle}" />
<meta property="og:description" content="${seoDescription}" />
<meta property="og:image" content="${images?.[0] || ""}" />
<meta property="og:type" content="website" />
</head>
<body style="background:#050505;color:#fff;font-family:Arial;padding:24px;max-width:1100px;margin:auto;">
<h1 style="color:#d4af37;">${vehicleName || "Vehicle"}</h1>
<p>${seoDescription}</p>
<img src="${images?.[0] || ""}" style="width:100%;border-radius:18px;margin-bottom:20px;" />
<h2>Vehicle Highlights</h2>
<ul>
${featureBadges.map((b) => `<li>${b}</li>`).join("")}
</ul>
<h2>Vehicle Specs</h2>
<p>Mileage: ${mileage || "N/A"}</p>
<p>Drivetrain: ${drivetrain || "N/A"}</p>
<p>Fuel Type: ${fuelType || "N/A"}</p>
<p>Transmission: ${transmission || "N/A"}</p>
<p>MPG: ${mpg || "N/A"}</p>
<h2>Why Buy This Vehicle?</h2>
<p>${featureNotes || "Financing available, trade-ins welcome, and bilingual assistance available."}</p>
<h2>Financing Available</h2>
<p>Flexible financing options available depending on approval structure.</p>
<h2>Trade-Ins Welcome</h2>
<p>We accept trade-ins and help customers upgrade into newer vehicles.</p>
<h2>Schedule a Test Drive</h2>
<p>Text Arturo at (872) 364-0886 to schedule your appointment today.</p>
<p><strong>SEO Keywords:</strong> ${localKeywords.join(", ")}</p>
</body>
</html>`;

    downloadTextFile(`${slug}-landing-page.html`, html, "text/html;charset=utf-8");

    pushActivityAlert(
      "SEO Vehicle Landing Page Generated",
      `${vehicleName || "Vehicle"} public inventory page created`,
      "HOT"
    );
  };

  const generateSalesInsights = () => {
    const closingRatio = salesAnalytics.appointments > 0
      ? ((salesAnalytics.sold / salesAnalytics.appointments) * 100).toFixed(1)
      : "0";

    return {
      closingRatio,
      recommendations: [
        "Post performance vehicles during evening TikTok hours.",
        "Luxury inventory is generating the highest engagement.",
        "Reels with cinematic hooks outperform static captions.",
        "Spanish financing hooks are increasing lead conversion.",
      ],
      hottestInventory: [
        "Audi S5",
        "Honda S2000",
        "Cadillac Escalade",
      ],
    };
  };

  const launchArShowroom = () => {
    const hotspots = [
      {
        title: "Financing",
        description: "Flexible financing options available depending on approval structure.",
      },
      {
        title: "Drivetrain",
        description: drivetrain || "Performance drivetrain available.",
      },
      {
        title: "Fuel Economy",
        description: mpg || "Contact dealer for MPG details.",
      },
      {
        title: "Technology",
        description: featureNotes || "Premium technology and convenience features.",
      },
    ];

    setArHotspots(hotspots);
    setArShowroomEnabled(true);

    pushActivityAlert(
      "AR Showroom Activated",
      `${vehicleName || "Vehicle"} AR experience launched`,
      "HOT"
    );
  };

  const generateVehicleFeatureBadges = () => {
    const badges: string[] = [];

    const combined = `${vehicleName} ${featureNotes} ${drivetrain} ${fuelType} ${transmission}`.toLowerCase();

    if (combined.includes("awd") || combined.includes("4matic") || combined.includes("4x4")) {
      badges.push("AWD / 4x4 Capability");
    }

    if (combined.includes("carplay")) {
      badges.push("Apple CarPlay");
    }

    if (combined.includes("android auto")) {
      badges.push("Android Auto");
    }

    if (combined.includes("leather")) {
      badges.push("Leather Interior");
    }

    if (combined.includes("sunroof") || combined.includes("moonroof")) {
      badges.push("Sunroof / Moonroof");
    }

    if (combined.includes("navigation") || combined.includes("navi")) {
      badges.push("Built-In Navigation");
    }

    if (combined.includes("heated")) {
      badges.push("Heated Seats");
    }

    if (combined.includes("third row")) {
      badges.push("3rd Row Seating");
    }

    if (combined.includes("backup") || combined.includes("camera")) {
      badges.push("Backup Camera");
    }

    if (combined.includes("bluetooth")) {
      badges.push("Bluetooth Connectivity");
    }

    if (combined.includes("turbo")) {
      badges.push("Turbocharged Performance");
    }

    if (badges.length === 0) {
      badges.push("Financing Available");
      badges.push("Trade-Ins Welcome");
      badges.push("Hablamos Español");
    }

    setFeatureBadges(badges);

    pushActivityAlert(
      "Vehicle Feature Badges Generated",
      `${vehicleName || "Vehicle"} marketing spec highlights created`,
      "LOW"
    );
  };

  const generateVehicleFeed = () => {
    const slug = safeSlug(vehicleName || "vehicle");

    const listing = {
      id: vin || slug || `vehicle-${Date.now()}`,
      title: vehicleName || "Vehicle",
      description:
        socialContent?.facebookDescription ||
        `${vehicleName || "Vehicle"} available now at Chicago Cars US in Summit, Illinois. ${mileage ? `${mileage} miles. ` : ""}${drivetrain ? `${drivetrain}. ` : ""}${fuelType ? `${fuelType}. ` : ""}${transmission ? `${transmission}. ` : ""}${featureNotes ? `${featureNotes}. ` : ""}Financing available.`,
      price: vehiclePrice || "Contact Dealer",
      vin: vin || "",
      mileage: mileage || "",
      drivetrain: drivetrain || "",
      fuel_type: fuelType || "",
      transmission: transmission || "",
      mpg: mpg || "",
      features: featureNotes || "",
      condition: "used",
      availability: "in stock",
      url: `${window.location.origin}/view/${slug}`,
      image_link: images.find(Boolean) || "",
      additional_image_links: images.filter(Boolean).slice(1, 10),
      seller_name: "Chicago Cars US",
      seller_phone: "(872) 364-0886",
      address: "Summit, IL",
      financing_available: true,
      trade_ins: true,
      bilingual_support: true,
    };

    const googleXml = `<?xml version="1.0" encoding="UTF-8"?>
<vehicles>
  <vehicle>
    <id>${listing.id}</id>
    <title><![CDATA[${listing.title}]]></title>
    <description><![CDATA[${listing.description}]]></description>
    <price>${listing.price}</price>
    <vin>${listing.vin}</vin>
    <condition>${listing.condition}</condition>
    <availability>${listing.availability}</availability>
    <link>${listing.url}</link>
    <image_link>${listing.image_link}</image_link>
  </vehicle>
</vehicles>`;

    const csvHeaders = [
      "id",
      "title",
      "description",
      "price",
      "vin",
      "mileage",
      "condition",
      "availability",
      "url",
      "image_link",
    ];

    const csvRow = [
      listing.id,
      listing.title,
      listing.description,
      listing.price,
      listing.vin,
      listing.mileage,
      listing.condition,
      listing.availability,
      listing.url,
      listing.image_link,
    ];

    const csv = [csvHeaders, csvRow]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    setVehicleFeedData({
      listing,
      googleXml,
      csv,
    });

    pushActivityAlert(
      "Vehicle Feed Generated",
      `${vehicleName || "Vehicle"} marketplace and Google feed created`,
      "LOW"
    );
  };

  const generateAdCampaign = () => {
    const name = vehicleName || "Vehicle";
    const price = vehiclePrice || "Financing Available";
    const lower = name.toLowerCase();

    const isLuxury = /(bmw|audi|mercedes|lexus|cadillac|porsche|infiniti|acura)/i.test(lower);
    const isTruck = /(silverado|f-150|ram|tundra|sierra|tacoma)/i.test(lower);
    const isSport = /(s2000|s5|mustang|camaro|corvette|m3|m4|supra|type r)/i.test(lower);

    let audience = ["Used car shoppers", "Chicago area buyers"];

    if (isLuxury) {
      audience.push("Luxury vehicle buyers", "Professionals", "Performance enthusiasts");
    }

    if (isTruck) {
      audience.push("Truck owners", "Contractors", "Outdoor lifestyle");
    }

    if (isSport) {
      audience.push("Car enthusiasts", "JDM community", "TikTok automotive audience");
    }

    audience.push("First-time buyers", "Hispanic community", "Bad credit financing shoppers");

    const metaHeadline = isLuxury
      ? `${name} Luxury Performance Available Now`
      : `${name} Available Today in Summit, IL`;

    const metaPrimary = `${name} available now for ${price}. Financing available for many credit situations. Trade-ins welcome. Hablamos Español. Text Arturo at (872) 364-0886 today.`;

    const googleHeadlines = [
      `${name} For Sale Chicago`,
      `Bad Credit Financing Available`,
      `Used Cars Summit IL`,
      `Trade-Ins Welcome`,
      `Text Arturo Today`,
    ];

    const googleDescriptions = [
      `${name} available now at Chicago Cars US. Financing options available.`,
      `First-time buyer programs, trade-ins welcome, and bilingual assistance.`,
    ];

    const keywords = [
      name,
      `${name} Chicago`,
      `Used ${name}`,
      `Bad credit car financing Chicago`,
      `Cars for sale Summit IL`,
      `DriveWithArturo`,
    ];

    const tiktokAd = isSport
      ? `This ${name} is built different 😮‍💨🔥 Financing available. Who’s taking it home?`
      : `${name} available now 🔥 Easy financing and trade-ins accepted.`;

    setAdCampaign({
      audience,
      metaHeadline,
      metaPrimary,
      googleHeadlines,
      googleDescriptions,
      keywords,
      tiktokAd,
    });

    pushActivityAlert(
      "AI Ad Campaign Generated",
      `${name} Meta and Google ad assets created`,
      "LOW"
    );
  };

  const backgroundPresets: Record<string, any> = {
    "luxury-studio": {
      gradient: ["#050505", "#111111"],
      glow: "rgba(212,175,55,0.35)",
      label: "Luxury Studio",
    },
    chicago: {
      gradient: ["#0f172a", "#111827"],
      glow: "rgba(59,130,246,0.35)",
      label: "Chicago Skyline",
    },
    jdm: {
      gradient: ["#120018", "#2a003f"],
      glow: "rgba(255,0,128,0.35)",
      label: "Tokyo Neon",
    },
    truck: {
      gradient: ["#2b1d12", "#3f2a00"],
      glow: "rgba(245,158,11,0.35)",
      label: "Truck Industrial",
    },
    sunset: {
      gradient: ["#431407", "#7c2d12"],
      glow: "rgba(251,146,60,0.4)",
      label: "Sunset Cinematic",
    },
  };

  const generateBackgroundStudioPreview = async () => {
    const firstImage = images.find(Boolean);

    if (!firstImage) {
      alert("Add at least one image first.");
      return;
    }

    try {
      const canvas = document.createElement("canvas");
      canvas.width = 1080;
      canvas.height = 1920;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = firstImage;
      });

      const preset = backgroundPresets[backgroundStudioMode] || backgroundPresets["luxury-studio"];

      const bgGradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      bgGradient.addColorStop(0, preset.gradient[0]);
      bgGradient.addColorStop(1, preset.gradient[1]);

      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const glow = ctx.createRadialGradient(
        canvas.width / 2,
        canvas.height * 0.52,
        120,
        canvas.width / 2,
        canvas.height * 0.52,
        720
      );

      glow.addColorStop(0, preset.glow);
      glow.addColorStop(1, "rgba(0,0,0,0)");

      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const scale = 0.94;
      const drawWidth = canvas.width * scale;
      const drawHeight = canvas.height * scale;
      const offsetX = (canvas.width - drawWidth) / 2;
      const offsetY = (canvas.height - drawHeight) / 2;

      ctx.save();
      ctx.shadowColor = preset.glow;
      ctx.shadowBlur = 60;
      ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
      ctx.restore();

      const floorGradient = ctx.createLinearGradient(0, canvas.height * 0.72, 0, canvas.height);
      floorGradient.addColorStop(0, "rgba(255,255,255,0)");
      floorGradient.addColorStop(1, "rgba(0,0,0,0.55)");
      ctx.fillStyle = floorGradient;
      ctx.fillRect(0, canvas.height * 0.72, canvas.width, canvas.height * 0.28);

      ctx.fillStyle = "#ffffff";
      ctx.font = "900 62px Arial";
      ctx.textAlign = "center";
      ctx.shadowColor = preset.glow;
      ctx.shadowBlur = 24;
      ctx.fillText(vehicleName || "AVAILABLE NOW", canvas.width / 2, canvas.height * 0.88);

      ctx.shadowBlur = 0;
      ctx.font = "700 38px Arial";
      ctx.fillStyle = "#d4af37";
      ctx.fillText(preset.label, canvas.width / 2, canvas.height * 0.93);

      setBackgroundPreview(canvas.toDataURL("image/jpeg", 0.94));

      pushActivityAlert(
        "AI Background Studio Generated",
        `${vehicleName || "Vehicle"} rendered using ${preset.label}`,
        "LOW"
      );
    } catch (err) {
      console.error(err);
      alert("Background studio render failed.");
    }
  };

  const cloudSyncSave = async (key: string, payload: any) => {
    if (!cloudSyncEnabled) return;

    try {
      setSyncStatus("syncing");
      localStorage.setItem(key, JSON.stringify(payload));
      localStorage.setItem(`cloud-sync-${key}`, JSON.stringify({ syncedAt: new Date().toISOString(), payload }));
      setLastSyncAt(new Date().toLocaleString());
      setSyncStatus("synced");
    } catch (err) {
      console.error(err);
      setSyncStatus("failed");
    }
  };

  const autoSaveStudioSession = async () => {
    const session = {
      vehicleName,
      vehiclePrice,
      images,
      scenes,
      voiceoverScript,
      viralHooks,
      socialContent,
      autoEditTimeline,
      exportPackage,
      updatedAt: new Date().toISOString(),
    };

    await cloudSyncSave("drivewitharturo-studio-session", session);
  };

  const restoreStudioSession = () => {
    try {
      const raw = localStorage.getItem("drivewitharturo-studio-session");
      if (!raw) return;

      const session = JSON.parse(raw);

      if (session.vehicleName) setVehicleName(session.vehicleName);
      if (session.vehiclePrice) setVehiclePrice(session.vehiclePrice);
      if (Array.isArray(session.images)) setImages(session.images);
      if (Array.isArray(session.scenes)) setScenes(session.scenes);
      if (session.voiceoverScript) setVoiceoverScript(session.voiceoverScript);
      if (Array.isArray(session.viralHooks)) setViralHooks(session.viralHooks);
      if (session.socialContent) setSocialContent(session.socialContent);
      if (Array.isArray(session.autoEditTimeline)) setAutoEditTimeline(session.autoEditTimeline);
      if (session.exportPackage) setExportPackage(session.exportPackage);

      setLastSyncAt(session.updatedAt || "");
    } catch (err) {
      console.error(err);
    }
  };

  const generateAutoEditTimeline = () => {
    const validImages = images.filter(Boolean);

    if (validImages.length < 6) {
      alert("Add more images to generate an AI edit timeline.");
      return;
    }

    const styles: Record<string, any> = {
      luxury: {
        transitions: ["cinematic fade", "slow zoom", "light streak"],
        pacing: "smooth",
        overlay: "premium gold typography",
      },
      aggressive: {
        transitions: ["whip pan", "flash cut", "speed ramp"],
        pacing: "fast",
        overlay: "bold CTA overlays",
      },
      jdm: {
        transitions: ["anime freeze", "glitch flash", "motion blur"],
        pacing: "high energy",
        overlay: "neon Japanese typography",
      },
      truck: {
        transitions: ["rugged shake", "dust transition", "slow push"],
        pacing: "heavy cinematic",
        overlay: "industrial typography",
      },
      viral: {
        transitions: ["bass drop cut", "flash zoom", "camera shake"],
        pacing: "TikTok fast",
        overlay: "viral hook captions",
      },
    };

    const style = styles[autoEditMode] || styles.luxury;

    const timeline = validImages.map((img, idx) => {
      const angleType =
        idx === 0
          ? "hero"
          : idx < 5
          ? "front sequence"
          : idx > validImages.length - 5
          ? "rear sequence"
          : idx % 7 === 0
          ? "detail shot"
          : "360 spin";

      return {
        frame: img,
        duration: autoEditMode === "viral" ? 0.8 : 1.6,
        transition: style.transitions[idx % style.transitions.length],
        effect:
          idx % 3 === 0
            ? "zoom"
            : idx % 3 === 1
            ? "pan"
            : "speed ramp",
        angleType,
        overlay:
          idx === 0
            ? vehicleName || "AVAILABLE NOW"
            : idx === validImages.length - 1
            ? "TEXT ARTURO"
            : style.overlay,
      };
    });

    setAutoEditTimeline(timeline);

    pushActivityAlert(
      "AI Auto-Edit Timeline Generated",
      `${vehicleName || "Vehicle"} reel edit plan created using ${autoEditMode} mode`,
      "LOW"
    );
  };

  const renderAiReelVideo = async () => {
    if (reelStoryboard.length === 0) {
      alert("Generate an AI reel timeline first.");
      return;
    }

    try {
      setRenderingReel(true);
      setRenderedReelUrl("");

      const canvas = document.createElement("canvas");
      canvas.width = 1080;
      canvas.height = 1920;

      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas rendering unavailable.");

      const stream = canvas.captureStream(30);
      const recorder = new MediaRecorder(stream, {
        mimeType: "video/webm;codecs=vp9",
      });

      const chunks: Blob[] = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      const finished = new Promise<string>((resolve) => {
        recorder.onstop = () => {
          const blob = new Blob(chunks, { type: "video/webm" });
          const url = URL.createObjectURL(blob);
          resolve(url);
        };
      });

      recorder.start();

      for (const scene of reelStoryboard) {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
          const image = new Image();
          image.crossOrigin = "anonymous";
          image.onload = () => resolve(image);
          image.onerror = reject;
          image.src = scene.frame;
        });

        const fps = 30;
        const totalFrames = Math.max(1, Math.floor(scene.duration * fps));

        for (let i = 0; i < totalFrames; i++) {
          const progress = i / totalFrames;

          ctx.clearRect(0, 0, canvas.width, canvas.height);

          let scale = 1;
          let offsetX = 0;
          let offsetY = 0;

          if (scene.motion.includes("zoom")) {
            scale = 1 + progress * 0.08;
          }

          if (scene.motion.includes("pan")) {
            offsetX = progress * -70;
          }

          if (scene.motion.includes("pullback")) {
            scale = 1.08 - progress * 0.08;
          }

          const drawWidth = canvas.width * scale;
          const drawHeight = canvas.height * scale;

          ctx.drawImage(
            img,
            offsetX - (drawWidth - canvas.width) / 2,
            offsetY - (drawHeight - canvas.height) / 2,
            drawWidth,
            drawHeight
          );

          if (trueSegmentationMode) {
            renderTrueSegmentationOverlay(
              ctx,
              img,
              scene.overlay || vehicleName || "AVAILABLE NOW",
              canvas.width,
              canvas.height,
              progress
            );
          }

          const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
          gradient.addColorStop(0, "rgba(0,0,0,0.15)");
          gradient.addColorStop(0.6, "rgba(0,0,0,0)");
          gradient.addColorStop(1, "rgba(0,0,0,0.55)");
          ctx.fillStyle = gradient;
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          ctx.fillStyle = "#d4af37";
          ctx.font = "900 54px Arial";
          ctx.fillText(vehicleName || "DriveWithArturo", 48, 118);

          ctx.fillStyle = "#ffffff";
          ctx.font = "700 42px Arial";
          ctx.fillText(scene.overlay || "Chicago Cars US", 48, canvas.height - 200);

          ctx.fillStyle = "rgba(0,0,0,0.55)";
          ctx.fillRect(0, canvas.height - 150, canvas.width, 150);

          if (depthOverlayMode && !trueSegmentationMode) {
            renderDepthOverlayText(
              ctx,
              vehiclePrice || "FINANCING AVAILABLE",
              canvas.width,
              canvas.height
            );
          } else {
            ctx.fillStyle = "#d4af37";
            ctx.font = "900 44px Arial";
            ctx.fillText(vehiclePrice || "Financing Available", 48, canvas.height - 82);
          }

          ctx.fillStyle = "#ffffff";
          ctx.font = "600 32px Arial";
          ctx.fillText("Text Arturo: (872) 364-0886", 48, canvas.height - 34);

          await new Promise((resolve) => setTimeout(resolve, 1000 / fps));
        }
      }

      recorder.stop();

      const reelUrl = await finished;
      setRenderedReelUrl(reelUrl);

      try {
        const response = await fetch(reelUrl);
        const blob = await response.blob();

        const file = new File(
          [blob],
          `${safeSlug(vehicleName || "drivewitharturo")}-ai-reel.webm`,
          { type: "video/webm" }
        );

        const nav = navigator as Navigator & {
          canShare?: (data?: ShareData) => boolean;
          share?: (data?: ShareData) => Promise<void>;
        };

        if (nav.canShare && nav.canShare({ files: [file] }) && nav.share) {
          await nav.share({
            title: vehicleName || "DriveWithArturo Reel",
            text: "Check out this vehicle reel.",
            files: [file],
          });
        }
      } catch {}
    } catch (err) {
      console.error(err);
      alert("AI reel rendering failed.");
    } finally {
      setRenderingReel(false);
    }
  };

  const generateReelStoryboard = () => {
    const filledFrames = images
      .map((img, index) => (img ? { img, index } : null))
      .filter(Boolean) as { img: string; index: number }[];

    if (filledFrames.length < minimumSpinFrames) {
      alert(`Upload at least ${minimumSpinFrames} photos before generating an AI reel timeline.`);
      return;
    }

    const hero = filledFrames[0];
    const frontQuarter = filledFrames[Math.floor(filledFrames.length * 0.15)] || hero;
    const side = filledFrames[Math.floor(filledFrames.length * 0.35)] || hero;
    const rearQuarter = filledFrames[Math.floor(filledFrames.length * 0.65)] || hero;
    const finale = filledFrames[filledFrames.length - 1] || hero;

    const scenes = [
      {
        type: "intro",
        title: "Hero Intro",
        frame: hero.img,
        duration: 2,
        motion: "slow zoom in",
        overlay: vehicleName || "DriveWithArturo",
      },
      {
        type: "feature",
        title: "Front Quarter",
        frame: frontQuarter.img,
        duration: 1.6,
        motion: "cinematic pan left",
        overlay: vehiclePrice || "Financing Available",
      },
      {
        type: "rotation",
        title: "Side Profile",
        frame: side.img,
        duration: 1.8,
        motion: "speed ramp transition",
        overlay: "Chicago Cars US",
      },
      {
        type: "rotation",
        title: "Rear Quarter",
        frame: rearQuarter.img,
        duration: 1.8,
        motion: "dynamic zoom",
        overlay: "Hablamos Español",
      },
      {
        type: "cta",
        title: "Final CTA",
        frame: finale.img,
        duration: 2.4,
        motion: "slow pullback",
        overlay: "Text Arturo (872) 364-0886",
      },
    ];

    setReelStoryboard(scenes);
    alert("AI reel storyboard generated.");
  };

  const generateMarketingContent = () => {
    const title = vehicleName || "Vehicle";
    const price = vehiclePrice || "Message for price";
    const isLuxury = /bmw|mercedes|audi|lexus|porsche|cadillac|jaguar|maserati/i.test(title);
    const isTruck = /silverado|f150|ram|tacoma|sierra|tundra/i.test(title);
    const isJDM = /s2000|supra|skyline|rx7|gtr|evo|sti|type r/i.test(title);

    let vibe = "clean daily driver";
    if (isLuxury) vibe = "luxury performance";
    if (isTruck) vibe = "powerful truck";
    if (isJDM) vibe = "JDM enthusiast build";

    const dealerDescription = `${title} available now at Chicago Cars US in Summit, IL. This ${vibe} offers premium styling, strong performance, and a clean driving experience. Financing options available for all credit types including first-time buyers. Trade-ins welcome. Message Arturo today for availability, out-the-door pricing, or to schedule a test drive. Price plus taxes and fees.`;

    const tiktokCaption = `🔥 ${title} just landed at Chicago Cars US 👀 ${price} • ${vibe} vibes • 360 view now live. Who’s taking this home? Text Arturo at (872) 364-0886 #ChicagoCars #DriveWithArturo #CarTok #Chicago #CarsForSale`;

    const facebookMarketplace = `${title}
💰 ${price}
📍 Summit, IL
✅ Financing Available
✅ Trade-Ins Welcome
✅ First-Time Buyer Friendly
📲 Text Arturo: (872) 364-0886

Hablamos Español.`;

    const spanishDescription = `${title} disponible ahora en Chicago Cars US en Summit, IL. Opciones de financiamiento disponibles para todo tipo de crédito. Aceptamos trade-ins y compradores por primera vez. Manda mensaje a Arturo para precio final, disponibilidad o prueba de manejo. Hablamos Español.`;

    const overlays = [
      `🔥 ${title}`,
      `${price}`,
      `📍 Summit, IL`,
      `💳 Financing Available`,
      `📲 Text Arturo`,
      `(872) 364-0886`,
    ];

    const hashtags = isJDM
      ? "#JDM #Honda #S2000 #CarTok #Chicago"
      : isLuxury
      ? "#LuxuryCars #ChicagoCars #BMW #Mercedes #DriveWithArturo"
      : isTruck
      ? "#TruckTok #LiftedTrucks #Silverado #F150 #Chicago"
      : "#CarsForSale #ChicagoCars #DriveWithArturo #CarTok #Chicago";

    setGeneratedMarketing({
      dealerDescription,
      tiktokCaption,
      facebookMarketplace,
      spanishDescription,
      overlays,
      hashtags,
    });

    alert("AI marketing content generated.");
  };

  const publishVehiclePage = () => {
    try {
      const slugBase = (vehicleName || vin || "vehicle")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

      const slug = `${slugBase}-${Date.now().toString().slice(-6)}`;

      const vehiclePage = {
        slug,
        vehicleName,
        vin,
        vehiclePrice,
        mileage,
        drivetrain,
        fuelType,
        transmission,
        mpg,
        featureNotes,
        images,
        createdAt: new Date().toISOString(),
      };

      const raw = localStorage.getItem(PUBLIC_SHARE_KEY);
      const existing = raw ? JSON.parse(raw) : [];

      localStorage.setItem(
        PUBLIC_SHARE_KEY,
        JSON.stringify([vehiclePage, ...existing].slice(0, 100))
      );

      const shareUrl = `${window.location.origin}/view/${slug}`;
      setPublicVehicleUrl(shareUrl);
      setQrCodeUrl(`https://api.qrserver.com/v1/create-qr-code/?size=900x900&data=${encodeURIComponent(shareUrl)}`);

      navigator.clipboard?.writeText(shareUrl).catch(() => {});

      alert("Public vehicle page created. Link copied to clipboard.");
    } catch (err) {
      console.error(err);
      alert("Failed to create public vehicle page.");
    }
  };

  const saveDraftLocally = () => {
    try {
      const draft = {
        id: Date.now(),
        createdAt: new Date().toISOString(),
        vehicleName,
        vin,
        mileage,
        drivetrain,
        fuelType,
        transmission,
        mpg,
        featureNotes,
        images,
        studioPreset,
      };

      const raw = localStorage.getItem(LOCAL_DRAFT_KEY);
      const existing = raw ? JSON.parse(raw) : [];
      const updated = [draft, ...existing].slice(0, 25);

      localStorage.setItem(LOCAL_DRAFT_KEY, JSON.stringify(updated));
      setSavedDrafts(updated);

      alert("Draft saved locally on this device.");
    } catch (err) {
      console.error(err);
      alert("Failed to save draft locally.");
    }
  };

  const loadDraft = (draft: any) => {
    setVehicleName(draft.vehicleName || "");
    setVin(draft.vin || "");
    setMileage(draft.mileage || "");
    setDrivetrain(draft.drivetrain || "");
    setFuelType(draft.fuelType || "");
    setTransmission(draft.transmission || "");
    setMpg(draft.mpg || "");
    setFeatureNotes(draft.featureNotes || "");
    setImages(Array.isArray(draft.images) ? draft.images : Array(TOTAL_FRAMES).fill(null));
    setStudioPreset(draft.studioPreset || "luxury");
    setCurrentFrame(0);
    setCaptureSlot(0);
    alert("Draft restored.");
  };

  const deleteDraft = (id: number) => {
    const updated = savedDrafts.filter((draft) => draft.id !== id);
    localStorage.setItem(LOCAL_DRAFT_KEY, JSON.stringify(updated));
    setSavedDrafts(updated);
  };

  useEffect(() => {
    loadDrafts();
    loadStoredLeads();
    loadInventoryVehicles();
    loadActivityFeed();
    loadLocalAnalytics();

    setNotificationsEnabled(
      localStorage.getItem("drivewitharturo-push-enabled") === "true"
    );
  }, []);

  useEffect(() => {
    if (completion < 1) return;

    const autoSave = setTimeout(() => {
      try {
        const autosave = {
          id: "autosave",
          createdAt: new Date().toISOString(),
          vehicleName,
          vin,
          images,
          studioPreset,
        };

        const raw = localStorage.getItem(LOCAL_DRAFT_KEY);
        const existing = raw ? JSON.parse(raw) : [];

        const filtered = existing.filter((draft: any) => draft.id !== "autosave");

        localStorage.setItem(
          LOCAL_DRAFT_KEY,
          JSON.stringify([autosave, ...filtered].slice(0, 25))
        );
      } catch {}
    }, 1800);

    return () => clearTimeout(autoSave);
  }, [images, vehicleName, vin, studioPreset, completion]);

  const uploadFileToStorage = async (file: File, folder: string) => {
    const supabase = ensureSupabase();

    if (!supabase || !supabaseReady) {
      loadSupabaseClient();
      alert("Supabase is still connecting. Wait 3 seconds and tap capture/upload again.");
      return null;
    }

    const base = safeSlug(vehicleName || vin || "vehicle");
    const cleanFolder = safeSlug(folder);
    const fileName = `${base}/${cleanFolder}-${Date.now()}.jpg`;

    const { error } = await supabase.storage.from("vehicle-frames").upload(fileName, file, {
      cacheControl: "3600",
      upsert: true,
    });

    if (error) {
      console.error(error);
      alert("Upload failed. Check bucket.");
      return null;
    }

    const { data } = supabase.storage.from("vehicle-frames").getPublicUrl(fileName);
    return data.publicUrl as string;
  };

  const handleUpload = async (index: number, file: File | null) => {
    if (!file) return;

    const uploadFile = await enhanceImageBeforeUpload(file);

    const supabase = ensureSupabase();

    if (!supabase || !supabaseReady) {
      loadSupabaseClient();
      alert("Supabase is still connecting. Wait 3 seconds and tap capture/upload again.");
      return;
    }

    try {
      setUploading(true);
      const base = safeSlug(vehicleName || vin || "vehicle");
      const frameNumber = ("000" + (index + 1)).slice(-3);
      const fileName = `${base}/frame-${frameNumber}-${Date.now()}.jpg`;

      const { error } = await supabase.storage.from("vehicle-frames").upload(fileName, uploadFile, {
        cacheControl: "3600",
        upsert: true,
      });

      if (error) {
        console.error(error);
        alert(`Upload failed: ${error.message || "Check your Supabase bucket and storage policies."}`);
        return;
      }

      const { data } = supabase.storage.from("vehicle-frames").getPublicUrl(fileName);
      setImages((prev) => {
        const updated = [...prev];
        updated[index] = data.publicUrl;
        return updated;
      });
    } catch (err: any) {
      console.error(err);
      alert(`Upload error: ${err?.message || "Unknown upload error"}`);
    } finally {
      setUploading(false);
    }
  };

  const handleInteriorUpload = async (file: File | null) => {
    if (!file) return;
    try {
      setUploading(true);
      const publicUrl = await uploadFileToStorage(file, "interior");
      if (publicUrl) setInteriorImage(publicUrl);
    } catch (err) {
      console.error(err);
      alert("Interior upload error");
    } finally {
      setUploading(false);
    }
  };

  const addHotspot = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!hotspotMode || !interiorImage) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const title = prompt("Hotspot title? Example: Dashboard, Seats, Sunroof");
    if (!title) return;
    const note = prompt("Add a short note for the customer") || "";
    setHotspots((prev) => [...prev, { id: Date.now(), x, y, title, note }]);
  };

  const deleteHotspot = (id: number) => setHotspots((prev) => prev.filter((h) => h.id !== id));

  const saveDamageReport = async (report: DamageReport) => {
    if (!supabaseRef.current || !currentVehicleId) {
      setDamageReports((prev) => [...prev, report]);
      alert("Damage mark added locally. Save + Share first to make it permanent.");
      return;
    }

    const { data, error } = await supabaseRef.current.from("vehicle_damage_reports").insert({
      vehicle_id: currentVehicleId,
      frame_number: report.frame_number,
      image_url: report.image_url,
      damage_type: report.damage_type,
      severity: report.severity,
      x: report.x,
      y: report.y,
      note: report.note,
    }).select().single();

    if (error) {
      console.error(error);
      alert("Damage report save failed.");
      return;
    }

    setDamageReports((prev) => [...prev, data]);
    await trackEvent("damage_marked", `${report.damage_type}_${report.severity}`);
  };

  const addDamageMark = async (e: React.MouseEvent<HTMLImageElement>) => {
    if (!damageMode || !activeFrameUrl) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const note = prompt("Damage note? Example: light scratch on driver door") || "";
    await saveDamageReport({
      frame_number: activeOriginalFrameNumber,
      image_url: activeFrameUrl,
      damage_type: damageType,
      severity: damageSeverity,
      x,
      y,
      note,
    });
  };

  const loadDamageReports = async (vehicleId: string) => {
    if (!supabaseRef.current || !vehicleId) return;
    const { data, error } = await supabaseRef.current
      .from("vehicle_damage_reports")
      .select("*")
      .eq("vehicle_id", vehicleId)
      .order("created_at", { ascending: false });
    if (!error && data) setDamageReports(data);
  };

  const damageForCurrentFrame = damageReports.filter((report) => report.frame_number === activeOriginalFrameNumber);

  const loadImageForVideo = (src: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

  const drawImageCover = (
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    canvasWidth: number,
    canvasHeight: number
  ) => {
    const imgAspect = img.width / img.height;
    const canvasAspect = canvasWidth / canvasHeight;

    let drawWidth = canvasWidth;
    let drawHeight = canvasHeight;
    let drawX = 0;
    let drawY = 0;

    if (imgAspect > canvasAspect) {
      drawHeight = canvasHeight;
      drawWidth = canvasHeight * imgAspect;
      drawX = (canvasWidth - drawWidth) / 2;
    } else {
      drawWidth = canvasWidth;
      drawHeight = canvasWidth / imgAspect;
      drawY = (canvasHeight - drawHeight) / 2;
    }

    ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
  };

  const exportSpinVideo = async () => {
    const frameUrls = activeFrameEntries.map((frame) => frame.url);

    if (frameUrls.length < minimumSpinFrames) {
      alert(`Upload at least ${minimumSpinFrames} pictures before exporting a smooth 360 spin video.`);
      return;
    }

    if (!window.MediaRecorder) {
      alert("Video export is not supported in this browser. Try Chrome, Edge, or Safari 17+.");
      return;
    }

    try {
      setExportingVideo(true);
      setVideoDownloadUrl("");
      setExportedVideoFile(null);

      const canvas = document.createElement("canvas");
      canvas.width = 1080;
      canvas.height = 1920;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas not available");

      const stream = canvas.captureStream(30);
      const mimeType = MediaRecorder.isTypeSupported("video/mp4")
        ? "video/mp4"
        : MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
        ? "video/webm;codecs=vp9"
        : MediaRecorder.isTypeSupported("video/webm")
        ? "video/webm"
        : "";

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      const chunks: BlobPart[] = [];

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) chunks.push(event.data);
      };

      const finished = new Promise<Blob>((resolve) => {
        recorder.onstop = () => {
          resolve(new Blob(chunks, { type: mimeType || "video/webm" }));
        };
      });

      recorder.start();

      const loadedFrames = await Promise.all(frameUrls.map(loadImageForVideo));
      const holdMs = 70;
      const loops = 2;

      for (let loop = 0; loop < loops; loop++) {
        for (const img of loadedFrames) {
          ctx.fillStyle = "#000";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          drawImageCover(ctx, img, canvas.width, canvas.height);

          ctx.fillStyle = "rgba(0,0,0,0.45)";
          ctx.fillRect(0, canvas.height - 160, canvas.width, 160);
          ctx.fillStyle = "#d4af37";
          ctx.font = "700 44px Arial";
          ctx.fillText(vehicleName || "DriveWithArturo 360", 48, canvas.height - 92);
          ctx.fillStyle = "#ffffff";
          ctx.font = "500 28px Arial";
          ctx.fillText("Text Arturo: (872) 364-0886", 48, canvas.height - 48);

          await new Promise((resolve) => setTimeout(resolve, holdMs));
        }
      }

      recorder.stop();
      const videoBlob = await finished;
      const url = URL.createObjectURL(videoBlob);
      setVideoDownloadUrl(url);

      const extension = (videoBlob.type || mimeType).includes("mp4") ? "mp4" : "webm";
      const file = new File(
        [videoBlob],
        `${safeSlug(vehicleName || vin || "drivewitharturo-360")}.${extension}`,
        { type: videoBlob.type || mimeType || "video/webm" }
      );

      setExportedVideoFile(file);
      alert("Video created. Tap Share / Save Video to open your phone share sheet, then choose Save Video or your social app.");
    } catch (err: any) {
      console.error(err);
      alert(`Video export failed: ${err?.message || "Unknown error"}`);
    } finally {
      setExportingVideo(false);
    }
  };

  const shareExportedVideo = async () => {
    if (!exportedVideoFile) {
      alert("Create the video first.");
      return;
    }

    const nav = navigator as Navigator & {
      canShare?: (data?: ShareData) => boolean;
      share?: (data?: ShareData) => Promise<void>;
    };

    try {
      if (nav.canShare && nav.canShare({ files: [exportedVideoFile] }) && nav.share) {
        await nav.share({
          title: vehicleName || "DriveWithArturo 360 Video",
          text: "Check out this 360 vehicle spin.",
          files: [exportedVideoFile],
        });
      } else {
        alert("Your browser does not support direct video sharing. Use Download Video File instead.");
      }
    } catch (err: any) {
      console.error(err);
      alert(`iPhone blocked direct sharing from the browser. Tap Download Video File, open the video, then use Share > Save Video. For automatic Save to Photos, this needs a native iPhone app.`);
    }
  };

  const saveToSupabase = async () => {
    if (!supabaseRef.current) {
      alert("Supabase not ready");
      return;
    }

    const uploadedImages = images.map((url, i) => ({ url, i })).filter((x) => x.url);
    if (!vehicleName || uploadedImages.length === 0) {
      alert("Need vehicle name + images");
      return;
    }

    try {
      setSaving(true);
      const { data: vehicle, error: vehicleError } = await supabaseRef.current.from("vehicles").insert({
        name: vehicleName,
        vin,
        year,
        make,
        model,
        trim,
        body_class: bodyClass,
        hologram_type: hologramType,
      }).select().single();

      if (vehicleError) {
        console.error(vehicleError);
        alert("Vehicle save failed");
        return;
      }

      const rows = uploadedImages.map((img) => ({
        vehicle_id: vehicle.id,
        image_url: img.url,
        frame_number: img.i + 1,
      }));

      const { error: imgError } = await supabaseRef.current.from("vehicle_images").insert(rows);
      if (imgError) {
        console.error(imgError);
        alert("Image save failed");
        return;
      }

      if (interiorImage || hotspots.length > 0) {
        await supabaseRef.current.from("vehicle_hotspots").insert({
          vehicle_id: vehicle.id,
          interior_image_url: interiorImage,
          hotspots,
        });
      }

      const unsavedDamage = damageReports.filter((report) => !report.id);
      if (unsavedDamage.length > 0) {
        const damageRows = unsavedDamage.map((report) => ({
          vehicle_id: vehicle.id,
          frame_number: report.frame_number,
          image_url: report.image_url,
          damage_type: report.damage_type,
          severity: report.severity,
          x: report.x,
          y: report.y,
          note: report.note,
        }));
        const { data: savedDamage } = await supabaseRef.current.from("vehicle_damage_reports").insert(damageRows).select();
        if (savedDamage) setDamageReports(savedDamage);
      }

      const link = `${window.location.origin}${window.location.pathname}?vehicle=${vehicle.id}`;
      setShareLink(link);
      setCurrentVehicleId(vehicle.id);
      await loadAnalytics(vehicle.id);
      await loadDamageReports(vehicle.id);
      alert("Saved + share link ready ✅");
    } catch (err) {
      console.error(err);
      alert("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const loadSharedVehicle = async (id: string) => {
    if (!supabaseRef.current) return;
    setLoadingVehicle(true);

    const { data: vehicle, error: vehicleError } = await supabaseRef.current.from("vehicles").select("*").eq("id", id).single();
    if (vehicleError) {
      console.error(vehicleError);
      alert("Could not load vehicle");
      setLoadingVehicle(false);
      return;
    }

    const { data: imgs, error: imageError } = await supabaseRef.current
      .from("vehicle_images")
      .select("*")
      .eq("vehicle_id", id)
      .order("frame_number");

    if (imageError) {
      console.error(imageError);
      alert("Could not load images");
      setLoadingVehicle(false);
      return;
    }

    const arr = Array(TOTAL_FRAMES).fill(null);
    (imgs || []).forEach((i: any) => {
      arr[i.frame_number - 1] = i.image_url;
    });

    const { data: hotspotRows } = await supabaseRef.current.from("vehicle_hotspots").select("*").eq("vehicle_id", id).limit(1);
    if (hotspotRows && hotspotRows.length > 0) {
      setInteriorImage(hotspotRows[0].interior_image_url || null);
      setHotspots(hotspotRows[0].hotspots || []);
    }

    setCurrentVehicleId(id);
    setVehicleName(vehicle.name || "");
    setVin(vehicle.vin || "");
    setYear(vehicle.year || "");
    setMake(vehicle.make || "");
    setModel(vehicle.model || "");
    setTrim(vehicle.trim || "");
    setBodyClass(vehicle.body_class || "");
    setHologramType(vehicle.hologram_type || "universal");
    setImages(arr);
    setCustomerMode(true);
    await trackEvent("view", "customer_link_open");
    await loadAnalytics(id);
    await loadDamageReports(id);
    setLoadingVehicle(false);
  };

  const loadInventoryDashboard = async () => {
    if (!supabaseRef.current) {
      alert("Supabase not ready yet. Refresh and try again.");
      return;
    }

    try {
      setInventoryLoading(true);
      const { data, error } = await supabaseRef.current.from("vehicles").select("*").order("created_at", { ascending: false });
      if (error) {
        console.error(error);
        alert("Could not load inventory.");
        return;
      }
      setInventoryVehicles(data || []);
      setShowInventoryDashboard(true);
    } catch (err) {
      console.error(err);
      alert("Inventory load error.");
    } finally {
      setInventoryLoading(false);
    }
  };

  const filteredInventory = inventoryVehicles.filter((vehicle) => {
    const q = inventorySearch.toLowerCase().trim();
    if (!q) return true;
    return [vehicle.name, vehicle.vehicleName, vehicle.vin, vehicle.year, vehicle.make, vehicle.model, vehicle.trim, vehicle.body_class, vehicle.vehiclePrice]
      .join(" ")
      .toLowerCase()
      .includes(q);
  });

  const [activeModule, setActiveModule] = useState("Dashboard");
  const [moduleSearch, setModuleSearch] = useState("");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [notificationCenterOpen, setNotificationCenterOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [aiCopilotOpen, setAiCopilotOpen] = useState(true);
  const [copilotInput, setCopilotInput] = useState("");
  const [copilotMessages, setCopilotMessages] = useState<any[]>([
    {
      id: 1,
      role: "assistant",
      content:
        "DriveWithArturo AI Copilot online. Ask for captions, follow-ups, pricing recommendations, or marketing help.",
    },
  ]);
  const [liveDataEnabled, setLiveDataEnabled] = useState(true);
  const [liveConnectionStatus, setLiveConnectionStatus] = useState("CONNECTED");
  const [themeEngineOpen, setThemeEngineOpen] = useState(false);
  const [quickDockExpanded, setQuickDockExpanded] = useState(true);
  const [workspaceModeEnabled, setWorkspaceModeEnabled] = useState(false);
  const [savedWorkspaces, setSavedWorkspaces] = useState<any[]>([
    {
      id: 1,
      name: "Sales Desk Layout",
      description: "CRM + OTD + AI Copilot",
    },
    {
      id: 2,
      name: "Media Studio Layout",
      description: "Media + Marketing + Analytics",
    },
  ]);

  const [activeWorkspaceName, setActiveWorkspaceName] = useState("Custom Workspace");

  const [runningApps, setRunningApps] = useState<any[]>([
    {
      id: 1,
      title: "Dealership CRM + Customer Pipeline",
      icon: "💬",
      minimized: false,
      pinned: true,
    },
    {
      id: 2,
      title: "OTD Calculator + Payment Desk",
      icon: "🧮",
      minimized: false,
      pinned: true,
    },
    {
      id: 3,
      title: "AI Copilot",
      icon: "🤖",
      minimized: false,
      pinned: true,
    },
  ]);

  const [workspaceWindows, setWorkspaceWindows] = useState<any[]>([
    {
      id: 1,
      title: "Dealership CRM + Customer Pipeline",
      x: 40,
      y: 140,
      width: 360,
      height: 240,
    },
    {
      id: 2,
      title: "OTD Calculator + Payment Desk",
      x: 430,
      y: 140,
      width: 360,
      height: 240,
    },
    {
      id: 3,
      title: "AI Copilot",
      x: 820,
      y: 140,
      width: 320,
      height: 240,
    },
  ]);
  const [activeTheme, setActiveTheme] = useState("DriveWithArturo Gold");
  const [ambientEnvironment, setAmbientEnvironment] = useState("Chicago Skyline Night");
  const [ambientEngineOpen, setAmbientEngineOpen] = useState(false);
  const [automationBuilderOpen, setAutomationBuilderOpen] = useState(false);

  const [dealershipMemoryOpen, setDealershipMemoryOpen] = useState(false);

  const [commandCenterOpen, setCommandCenterOpen] = useState(false);

  const [pluginMarketplaceOpen, setPluginMarketplaceOpen] = useState(false);

  const [installedPlugins, setInstalledPlugins] = useState<any[]>([
    {
      id: 1,
      name: "WhatsApp CRM Connector",
      category: "CRM",
      installed: true,
      description: "Sync customer conversations and follow-ups.",
    },
    {
      id: 2,
      name: "VIN Decoder AI",
      category: "Inventory",
      installed: true,
      description: "Decode VINs and generate inventory intelligence.",
    },
    {
      id: 3,
      name: "Marketplace Syndication",
      category: "Marketing",
      installed: false,
      description: "Publish inventory to marketplace channels automatically.",
    },
    {
      id: 4,
      name: "Auction Scanner",
      category: "Acquisition",
      installed: false,
      description: "Monitor auction inventory and pricing opportunities.",
    },
  ]);

  const [commandCenterNodes, setCommandCenterNodes] = useState<any[]>([
    {
      id: 1,
      label: "CRM Activity",
      x: 18,
      y: 26,
      size: 120,
    },
    {
      id: 2,
      label: "Inventory Heatmap",
      x: 62,
      y: 38,
      size: 150,
    },
    {
      id: 3,
      label: "AI Automation",
      x: 38,
      y: 72,
      size: 132,
    },
    {
      id: 4,
      label: "Sales Analytics",
      x: 76,
      y: 18,
      size: 142,
    },
  ]);

  const [dealershipMemory, setDealershipMemory] = useState<any[]>([
    {
      id: 1,
      category: "Customer Preference",
      title: "Luxury SUV Buyer",
      memory:
        "Customer historically engages more with luxury SUV inventory and bilingual follow-ups.",
      confidence: 94,
    },
    {
      id: 2,
      category: "Marketing Intelligence",
      title: "TikTok Performance",
      memory:
        "Performance vehicles receive the highest engagement during evening posting hours.",
      confidence: 91,
    },
    {
      id: 3,
      category: "Sales Behavior",
      title: "Closing Pattern",
      memory:
        "Most successful dealership closes occur after 5PM appointments.",
      confidence: 88,
    },
  ]);

  const [automationFlows, setAutomationFlows] = useState<any[]>([
    {
      id: 1,
      trigger: "Lead inactive for 2 days",
      action: "Send AI follow-up text",
      status: "ACTIVE",
    },
    {
      id: 2,
      trigger: "Vehicle exceeds 45 days",
      action: "Alert pricing engine",
      status: "ACTIVE",
    },
  ]);

  const ambientPresets = [
    {
      name: "Chicago Skyline Night",
      background:
        "radial-gradient(circle at top,#1e293b 0%,#020617 55%,#000 100%)",
      glow: "rgba(56,189,248,0.22)",
    },
    {
      name: "Luxury Showroom",
      background:
        "linear-gradient(135deg,#111827 0%,#1f2937 40%,#000 100%)",
      glow: "rgba(212,175,55,0.25)",
    },
    {
      name: "Cyberpunk Performance",
      background:
        "linear-gradient(135deg,#0f172a 0%,#1e1b4b 40%,#020617 100%)",
      glow: "rgba(244,63,94,0.22)",
    },
    {
      name: "Executive Minimal",
      background:
        "linear-gradient(135deg,#f8fafc 0%,#cbd5e1 100%)",
      glow: "rgba(15,23,42,0.08)",
    },
  ];

  const currentAmbient =
    ambientPresets.find((preset) => preset.name === ambientEnvironment) ||
    ambientPresets[0];

  const dealershipThemes = [
    {
      name: "DriveWithArturo Gold",
      primary: "#d4af37",
      background: "#050505",
      accent: "#111",
    },
    {
      name: "Chicago Night",
      primary: "#38bdf8",
      background: "#020617",
      accent: "#0f172a",
    },
    {
      name: "Luxury White",
      primary: "#111111",
      background: "#f8fafc",
      accent: "#e5e7eb",
    },
    {
      name: "Cyberpunk Performance",
      primary: "#f43f5e",
      background: "#0f172a",
      accent: "#1e293b",
    },
  ];

  const currentTheme =
    dealershipThemes.find((theme) => theme.name === activeTheme) ||
    dealershipThemes[0];

  const [liveEvents, setLiveEvents] = useState<any[]>([
    {
      id: 1,
      type: "system",
      title: "Live Data Layer Online",
      message: "Real-time dealership event stream initialized.",
      createdAt: new Date().toLocaleTimeString(),
    },
  ]);
  const [commandSearch, setCommandSearch] = useState("");

  const [toastNotifications, setToastNotifications] = useState<any[]>([]);

  const [notifications, setNotifications] = useState<any[]>([
    {
      id: 1,
      title: "System Ready",
      message: "DriveWithArturo OS initialized successfully.",
      type: "system",
      unread: true,
      createdAt: new Date().toLocaleTimeString(),
    },
  ]);

  const favoriteModules = [
    "Dealership CRM + Customer Pipeline",
    "OTD Calculator + Payment Desk",
    "AI Inventory Photo Studio + Media Production",
    "AI Inventory Pricing Engine + Market Intelligence",
    "AI Sales Analytics + Performance Dashboard",
  ];

  const moduleMenuItems = [
    "Mobile App + Offline Lot Mode",
    "AI Dealership Operating System",
    "AR Showroom + Interactive Vehicle Experience",
    "AI Inventory Photo Studio + Media Production",
    "Digital Retailing + Online Deal Structuring",
    "AI Voice Sales Assistant + Call Center",
    "Customer Portal + Saved Vehicle Experience",
    "AI Inventory Pricing Engine + Market Intelligence",
    "Service Department + Recon Workflow System",
    "Manager Dashboard + Store Performance Command Center",
    "Inventory Acquisition + Appraisal System",
    "Deal Jacket + Desking System",
    "Holiday + Special Event Follow-Up Engine",
    "Dealership CRM + Customer Pipeline",
    "Auto TikTok / Instagram Posting Queue",
    "SEO Vehicle Landing Page Generator",
    "AI Sales Analytics + Performance Dashboard",
    "AR Vehicle Showroom Mode",
    "Vehicle Specs + Marketing Details",
    "Draft Projects + Auto Save",
    "AI Reel Storyboard Generator",
    "AI Marketing Generator",
    "Public Vehicle Page",
    "AI Phone Assistant + Lead Conversation Center",
    "AI Financing Assistant + Payment Estimator",
    "Google Vehicle Feed + Marketplace Syndication",
    "AI Meta Ads + Google Ads Generator",
    "Creator Export Packager",
    "Advanced Analytics + Heatmaps",
    "SEO Vehicle Website Builder",
    "OTD Calculator + Payment Desk",
  ];

  const pushLiveEvent = (type: string, title: string, message: string) => {
    const event = {
      id: Date.now(),
      type,
      title,
      message,
      createdAt: new Date().toLocaleTimeString(),
    };

    setLiveEvents((prev) => [event, ...prev].slice(0, 50));

    if (liveDataEnabled) {
      pushSystemNotification(title, message, type);
    }
  };

  useEffect(() => {
    if (!liveDataEnabled) {
      setLiveConnectionStatus("PAUSED");
      return;
    }

    setLiveConnectionStatus("CONNECTED");

    const liveEventTemplates = [
      {
        type: "lead",
        title: "Live Lead Signal",
        message: "A customer interaction was detected in the CRM pipeline.",
      },
      {
        type: "analytics",
        title: "Analytics Updated",
        message: "Dashboard engagement metrics refreshed automatically.",
      },
      {
        type: "inventory",
        title: "Inventory Sync",
        message: "Vehicle data layer synchronized across modules.",
      },
      {
        type: "pricing",
        title: "Pricing Watch",
        message: "Market intelligence layer checked active pricing signals.",
      },
    ];

    const interval = setInterval(() => {
      const next = liveEventTemplates[Math.floor(Math.random() * liveEventTemplates.length)];
      pushLiveEvent(next.type, next.title, next.message);
    }, 45000);

    return () => clearInterval(interval);
  }, [liveDataEnabled]);

  const runAiCopilot = () => {
    if (!copilotInput.trim()) return;

    const prompt = copilotInput.toLowerCase();

    let aiResponse = "AI Copilot analyzed your dealership request.";

    if (prompt.includes("caption")) {
      aiResponse = "🔥 Chicago inventory alert — aggressive financing available with bilingual assistance and same-day delivery options.";
    } else if (prompt.includes("follow")) {
      aiResponse = "Hey! Just checking in to see if you're still searching for your next vehicle. We have fresh inventory available today.";
    } else if (prompt.includes("pricing")) {
      aiResponse = "AI pricing engine recommends monitoring inventory aging and adjusting pricing based on local demand.";
    }

    setCopilotMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        role: "user",
        content: copilotInput,
      },
      {
        id: Date.now() + 1,
        role: "assistant",
        content: aiResponse,
      },
    ]);

    setCopilotInput("");
  };

  const pushToastNotification = (
    title: string,
    message: string,
    type: "success" | "warning" | "error" | "info" = "info"
  ) => {
    const toast = {
      id: Date.now(),
      title,
      message,
      type,
    };

    setToastNotifications((prev) => [toast, ...prev]);

    setTimeout(() => {
      setToastNotifications((prev) =>
        prev.filter((item) => item.id !== toast.id)
      );
    }, 4500);
  };

  const pushSystemNotification = (
    title: string,
    message: string,
    type = "system"
  ) => {
    pushToastNotification(
      title,
      message,
      type === "warning"
        ? "warning"
        : type === "save"
        ? "success"
        : "info"
    );

    setNotifications((prev) => [
      {
        id: Date.now(),
        title,
        message,
        type,
        unread: true,
        createdAt: new Date().toLocaleTimeString(),
      },
      ...prev,
    ]);
  };

  const markNotificationsRead = () => {
    setNotifications((prev) =>
      prev.map((notification) => ({ ...notification, unread: false }))
    );
  };

  const unreadNotifications = notifications.filter((n) => n.unread).length;

  const saveWorkspaceLayout = () => {
    const newWorkspace = {
      id: Date.now(),
      name: `${activeWorkspaceName} ${savedWorkspaces.length + 1}`,
      description: `${workspaceWindows.length} active windows saved`,
    };

    setSavedWorkspaces((prev) => [newWorkspace, ...prev]);

    pushSystemNotification(
      "Workspace Saved",
      `${newWorkspace.name} saved successfully.`,
      "workspace"
    );
  };

  const loadWorkspaceLayout = (workspace: any) => {
    setActiveWorkspaceName(workspace.name);

    pushSystemNotification(
      "Workspace Loaded",
      `${workspace.name} restored successfully.`,
      "workspace"
    );
  };

  const startWindowDrag = (
    event: React.MouseEvent,
    id: number
  ) => {
    event.preventDefault();

    const startX = event.clientX;
    const startY = event.clientY;

    const targetWindow = workspaceWindows.find((w) => w.id === id);
    if (!targetWindow) return;

    const originalX = targetWindow.x;
    const originalY = targetWindow.y;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      setWorkspaceWindows((prev) =>
        prev.map((window) =>
          window.id === id
            ? {
                ...window,
                x: Math.round((originalX + deltaX) / 10) * 10,
                y: Math.round((originalY + deltaY) / 10) * 10,
                zIndex: 999,
              }
            : window
        )
      );
    };

    const handleMouseUp = () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const resizeWorkspaceWindow = (
    id: number,
    direction: "grow" | "shrink"
  ) => {
    setWorkspaceWindows((prev) =>
      prev.map((window) => {
        if (window.id !== id) return window;

        const sizeChange = direction === "grow" ? 40 : -40;

        return {
          ...window,
          width: Math.max(260, window.width + sizeChange),
          height: Math.max(180, window.height + sizeChange),
          zIndex: 999,
        };
      })
    );
  };

  const toggleRunningAppState = (
    id: number,
    minimized?: boolean
  ) => {
    setRunningApps((prev) =>
      prev.map((app) =>
        app.id === id
          ? {
              ...app,
              minimized:
                typeof minimized === "boolean"
                  ? minimized
                  : !app.minimized,
            }
          : app
      )
    );

    setWorkspaceWindows((prev) =>
      prev.map((window) =>
        window.id === id
          ? {
              ...window,
              hidden:
                typeof minimized === "boolean"
                  ? minimized
                  : !window.hidden,
            }
          : window
      )
    );
  };

  const focusWorkspaceWindow = (id: number) => {
    setWorkspaceWindows((prev) =>
      prev.map((window) => ({
        ...window,
        zIndex: window.id === id ? 999 : 1,
        hidden: window.id === id ? false : window.hidden,
      }))
    );

    toggleRunningAppState(id, false);
  };

  const toggleWindowState = (
    id: number,
    mode: "minimize" | "maximize"
  ) => {
    setWorkspaceWindows((prev) =>
      prev.map((window) => {
        if (window.id !== id) return window;

        if (mode === "minimize") {
          return {
            ...window,
            height: 70,
          };
        }

        return {
          ...window,
          x: 40,
          y: 120,
          width: 900,
          height: 620,
          zIndex: 999,
        };
      })
    );
  };

  const moveWorkspaceWindow = (
    id: number,
    direction: "left" | "right" | "up" | "down"
  ) => {
    setWorkspaceWindows((prev) =>
      prev.map((window) => {
        if (window.id !== id) return window;

        const distance = 30;

        return {
          ...window,
          x:
            direction === "left"
              ? window.x - distance
              : direction === "right"
              ? window.x + distance
              : window.x,
          y:
            direction === "up"
              ? window.y - distance
              : direction === "down"
              ? window.y + distance
              : window.y,
        };
      })
    );
  };

  const saveCurrentModule = () => {
    pushSystemNotification(
      "Module Saved",
      `${activeModule} saved successfully`,
      "save"
    );

    pushActivityAlert(
      "Module Saved",
      `${activeModule} data saved successfully`,
      "LOW"
    );
  };

  const resetCurrentModule = () => {
    pushSystemNotification(
      "Module Reset",
      `${activeModule} reset triggered`,
      "warning"
    );

    pushActivityAlert(
      "Module Reset",
      `${activeModule} module reset requested`,
      "LOW"
    );
  };

  const commandPaletteResults = moduleMenuItems.filter((item) =>
    item.toLowerCase().includes(commandSearch.toLowerCase().trim())
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandPaletteOpen((prev) => !prev);
      }

      if (event.key === "/") {
        const target = event.target as HTMLElement;
        const tagName = target?.tagName?.toLowerCase();

        if (tagName !== "input" && tagName !== "textarea") {
          event.preventDefault();
          setCommandPaletteOpen(true);
        }
      }

      if (event.key === "Escape") {
        setCommandPaletteOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const filteredModuleMenuItems = moduleMenuItems.filter((item) =>
    item.toLowerCase().includes(moduleSearch.toLowerCase().trim())
  );

  const scrollToModule = (title: string) => {
    setActiveModule(title);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const headings = Array.from(document.querySelectorAll("h3"));

    headings.forEach((heading) => {
      const title = heading.textContent?.trim() || "";
      if (!moduleMenuItems.includes(title)) return;

      const section = heading.parentElement as HTMLElement | null;
      if (!section) return;

      section.style.display = activeModule === title ? "block" : "none";
    });
  }, [activeModule]);

  const dashboardCards = [
    {
      label: "Hot Leads",
      value: salesAnalytics.leads,
      subtitle: "CRM + lead pipeline",
      target: "Dealership CRM + Customer Pipeline",
    },
    {
      label: "Appointments",
      value: salesAnalytics.appointments,
      subtitle: "AI sales analytics",
      target: "AI Sales Analytics + Performance Dashboard",
    },
    {
      label: "Units Sold",
      value: salesAnalytics.sold,
      subtitle: "Store performance",
      target: "Manager Dashboard + Store Performance Command Center",
    },
    {
      label: "Closing Ratio",
      value: `${salesAnalytics.avgClosingRatio}%`,
      subtitle: "Performance dashboard",
      target: "AI Sales Analytics + Performance Dashboard",
    },
    {
      label: "Pricing Engine",
      value: pricingInsights.length || "AI",
      subtitle: "Market intelligence",
      target: "AI Inventory Pricing Engine + Market Intelligence",
    },
    {
      label: "OTD Desk",
      value: otdResult ? money(otdResult.balanceAfterDown) : "Ready",
      subtitle: "Payment + tax calculator",
      target: "OTD Calculator + Payment Desk",
    },
  ];

  const openInventoryVehicle = async (vehicle: InventoryVehicle) => {
    setPublicInventoryMode(false);
    await loadSharedVehicle(vehicle.id);
    await loadAnalytics(vehicle.id);
    setCurrentVehicleId(vehicle.id);
    setShowInventoryDashboard(false);
    setCustomerMode(false);
    setCaptureMode(false);
    stopLiveCamera();
    setShareLink(`${window.location.origin}${window.location.pathname}?vehicle=${vehicle.id}`);
  };

  const startDrag = (x: number) => {
    if (captureMode) return;
    isDragging.current = true;
    lastX.current = x;
    velocity.current = 0;
    stopAutoSpin();
  };

  const rotate = (x: number) => {
    if (!isDragging.current || captureMode || activeFrameCount === 0) return;
    const delta = x - lastX.current;
    velocity.current = delta;
    if (Math.abs(delta) > 5) {
      setCurrentFrame((p) => (p + (delta > 0 ? -1 : 1) + activeFrameCount) % activeFrameCount);
      lastX.current = x;
    }
  };

  const stopDrag = () => {
    if (!isDragging.current) return;
    const now = Date.now();
    if (customerMode && now - lastDragTrackRef.current > 3000) {
      lastDragTrackRef.current = now;
      trackEvent("spin", "drag_or_swipe");
    }
    isDragging.current = false;
    let v = velocity.current;
    const momentumLoop = () => {
      if (Math.abs(v) < 0.4) {
        scheduleAutoSpin();
        return;
      }
      setCurrentFrame((p) => {
        if (activeFrameCount === 0) return 0;
        let next = p + (v > 0 ? -1 : 1);
        if (next < 0) next = activeFrameCount - 1;
        if (next >= activeFrameCount) next = 0;
        return next;
      });
      v *= 0.91;
      requestAnimationFrame(momentumLoop);
    };
    momentumLoop();
  };

  const goToNextEmpty = () => setCaptureSlot(images.findIndex((img) => !img) === -1 ? 0 : images.findIndex((img) => !img));
  const goToSmartNext = () => setCaptureSlot(nextMissingFrame);

  const deleteFrameSlot = (index: number) => {
    setImages((prev) => {
      const updated = [...prev];
      updated[index] = null;
      return updated;
    });
    setCaptureSlot(index);
    setCurrentFrame(0);
  };

  const clearAllFrames = () => {
    if (!confirm("Clear all 75 frame slots from this project?")) return;
    setImages(Array(TOTAL_FRAMES).fill(null));
    setCaptureSlot(0);
    setCurrentFrame(0);
  };

  const compactFrames = () => {
    const filled = images.filter(Boolean) as string[];
    const updated = Array(TOTAL_FRAMES).fill(null) as VehicleImage[];
    filled.slice(0, TOTAL_FRAMES).forEach((url, index) => {
      updated[index] = url;
    });
    setImages(updated);
    setCaptureSlot(Math.min(filled.length, TOTAL_FRAMES - 1));
    setCurrentFrame(0);
    alert(`Reordered ${filled.length} photo${filled.length === 1 ? "" : "s"} into the first frame slots.`);
  };

  const hologramStyle = {
    position: "absolute" as const,
    width:
      hologramType === "pickup"
        ? "88%"
        : hologramType === "van" || hologramType === "truck"
        ? "84%"
        : hologramType === "coupe"
        ? "72%"
        : hologramType === "suv"
        ? "80%"
        : "78%",
    height:
      hologramType === "van" || hologramType === "truck"
        ? "55%"
        : hologramType === "suv"
        ? "48%"
        : hologramType === "coupe"
        ? "34%"
        : "42%",
    border: readyToCapture
      ? "3px solid rgba(34,197,94,0.95)"
      : motionPermission && alignmentScore < 60
      ? "3px solid rgba(239,68,68,0.9)"
      : "2px dashed rgba(212,175,55,0.85)",
    borderRadius: hologramType === "truck" || hologramType === "van" ? "18px" : "45% / 28%",
    boxShadow: readyToCapture
      ? "0 0 40px rgba(34,197,94,0.55), inset 0 0 24px rgba(34,197,94,0.18)"
      : motionPermission && alignmentScore < 60
      ? "0 0 40px rgba(239,68,68,0.45), inset 0 0 24px rgba(239,68,68,0.14)"
      : "0 0 35px rgba(212,175,55,0.35), inset 0 0 22px rgba(212,175,55,0.12)",
    zIndex: 12,
  };

  const Viewer = (
    <div
      style={{
        position: "relative",
        height: customerMode ? "65vh" : captureMode ? 620 : 340,
        background: "radial-gradient(circle at center, #1a1a1a 0%, #050505 70%, #000 100%)",
        border: "1px solid #2a2a2a",
        borderRadius: customerMode ? 0 : 18,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 18,
        touchAction: "none",
        userSelect: "none",
        overflow: "hidden",
      }}
      onMouseDown={(e) => startDrag(e.clientX)}
      onMouseMove={(e) => rotate(e.clientX)}
      onMouseUp={stopDrag}
      onMouseLeave={stopDrag}
      onTouchStart={(e) => startDrag(e.touches[0].clientX)}
      onTouchMove={(e) => rotate(e.touches[0].clientX)}
      onTouchEnd={stopDrag}
    >
      {captureMode && (
        <>
          {cameraOn ? (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 1 }}
            />
          ) : (
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 12,
                padding: 24,
                textAlign: "center",
              }}
            >
              <button
                onClick={startLiveCamera}
                style={{ padding: "14px 24px", borderRadius: 999, background: "#d4af37", color: "#000", border: "none", fontWeight: 900 }}
              >
                Start Live Camera Guide
              </button>
              {cameraError && <div style={{ color: "#ffb4b4", maxWidth: 520 }}>{cameraError}</div>}
              <div style={{ color: "#aaa", fontSize: 13 }}>No camera available? Use Upload Slot or the per-frame upload buttons below.</div>
            </div>
          )}

          <div style={{ ...hologramStyle, pointerEvents: "none" }} />

          <div
            style={{
              position: "absolute",
              top: 14,
              left: 14,
              right: 14,
              zIndex: 20,
              background: "rgba(0,0,0,0.72)",
              border: readyToCapture ? "1px solid rgba(34,197,94,0.85)" : "1px solid rgba(212,175,55,0.65)",
              borderRadius: 16,
              padding: 12,
              backdropFilter: "blur(6px)",
            }}
          >
            <div style={{ color: readyToCapture ? "#22c55e" : "#d4af37", fontWeight: 900, fontSize: 18 }}>REAL MOTION ALIGNMENT</div>
            <div style={{ color: "#ddd", fontSize: 13 }}>Slot {captureSlot + 1} / {TOTAL_FRAMES} · Target {targetAngle}° · Every {angleSpacing}° · {hologramType.toUpperCase()}</div>
            <div style={{ color: readyToCapture ? "#22c55e" : "#d4af37", fontSize: 13, fontWeight: 800, marginTop: 4 }}>{alignmentMessage}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 10, color: "#aaa", fontSize: 12 }}>
              <div>Roll: {deviceRoll.toFixed(1)}°</div>
              <div>Pitch: {devicePitch.toFixed(1)}°</div>
              <div>Score: {alignmentScore}%</div>
            </div>
            {!motionPermission && (
              <button
                onClick={enableMotionAlignment}
                style={{ marginTop: 10, width: "100%", padding: 10, borderRadius: 999, border: "1px solid #d4af37", background: "#111", color: "#d4af37", fontWeight: 900 }}
              >
                Enable Real Motion Alignment
              </button>
            )}
            {!motionSupported && <div style={{ color: "#ffb4b4", fontSize: 12, marginTop: 8 }}>Motion sensors are not available in this browser.</div>}
          </div>

          <div
            style={{
              position: "absolute",
              left: 18,
              right: 18,
              bottom: 108,
              zIndex: 20,
              background: "rgba(0,0,0,0.62)",
              border: "1px solid rgba(212,175,55,0.45)",
              borderRadius: 16,
              padding: 12,
              backdropFilter: "blur(6px)",
            }}
          >
            <div style={{ color: "#d4af37", fontWeight: 900 }}>Smart Capture AI · Score {smartCaptureScore}% · {smartCaptureStatus}</div>
            <div style={{ color: "#ddd", fontSize: 13, marginTop: 4 }}>Missing {missingFrames.length}/{TOTAL_FRAMES} · Next recommended: Frame {nextMissingFrame + 1}</div>
            <div style={{ color: "#aaa", fontSize: 12, marginTop: 4 }}>{smartCaptureTip}</div>
            <div style={{ height: 8, background: "#222", borderRadius: 999, overflow: "hidden", marginTop: 10 }}>
              <div style={{ width: `${progressPercent}%`, height: "100%", background: "linear-gradient(90deg, #8a6d1d, #d4af37)" }} />
            </div>
          </div>

          <div style={{ position: "absolute", bottom: 18, left: 18, right: 18, display: "grid", gridTemplateColumns: "1fr 78px 1fr", gap: 10, alignItems: "center", zIndex: 25 }}>
            <button onClick={goToSmartNext} style={{ borderRadius: 999, padding: 12, background: "rgba(0,0,0,0.78)", color: "#d4af37", border: "1px solid #d4af37", fontWeight: 900 }}>Smart Next</button>
            <button
              onClick={takeLivePhoto}
              disabled={uploading || !cameraOn}
              style={{ width: 78, height: 78, borderRadius: "50%", background: readyToCapture ? "#22c55e" : "#d4af37", border: "5px solid rgba(255,255,255,0.35)", boxShadow: "0 0 30px rgba(212,175,55,0.75)", cursor: uploading || !cameraOn ? "not-allowed" : "pointer", opacity: uploading || !cameraOn ? 0.55 : 1, color: "#000", fontWeight: 900, fontSize: 24 }}
            >
              📸
            </button>
            <label style={{ borderRadius: 999, padding: 12, background: "rgba(0,0,0,0.78)", color: "#fff", border: "1px solid #333", fontWeight: 900, textAlign: "center", cursor: uploading ? "wait" : "pointer" }}>
              Upload Slot
              <input type="file" accept="image/*" capture="environment" disabled={uploading} style={{ display: "none" }} onChange={async (e) => { await handleUpload(captureSlot, e.target.files?.[0] || null); e.currentTarget.value = ""; }} />
            </label>
          </div>

          {uploading && <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", background: "rgba(0,0,0,0.88)", color: "#d4af37", border: "1px solid #d4af37", padding: "12px 18px", borderRadius: 999, zIndex: 40, fontWeight: 900 }}>Uploading...</div>}
        </>
      )}

      {!captureMode && activeFrameUrl ? (
        <div style={{ position: "relative", display: "inline-block" }}>
          <img
            src={activeFrameUrl}
            alt="Vehicle 360 frame"
            draggable={false}
            onClick={addDamageMark}
            style={{ maxHeight: customerMode ? "58vh" : 300, maxWidth: "100%", borderRadius: 12, pointerEvents: damageMode ? "auto" : "none", cursor: damageMode ? "crosshair" : "default" }}
          />
          {damageForCurrentFrame.map((damage, index) => (
            <div
              key={damage.id || index}
              onClick={(e) => {
                e.stopPropagation();
                trackEvent("damage_marker_click", damage.damage_type);
                alert(`${damage.damage_type.toUpperCase()} · ${damage.severity.toUpperCase()}\n\n${damage.note || "No note"}`);
              }}
              style={{
                position: "absolute",
                left: `${damage.x}%`,
                top: `${damage.y}%`,
                transform: "translate(-50%, -50%)",
                width: damage.severity === "heavy" ? 34 : 28,
                height: damage.severity === "heavy" ? 34 : 28,
                borderRadius: "50%",
                background: damage.severity === "heavy" ? "rgba(239,68,68,0.95)" : damage.severity === "medium" ? "rgba(245,158,11,0.95)" : "rgba(212,175,55,0.95)",
                color: "#000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 900,
                boxShadow: "0 0 22px rgba(255,255,255,0.25)",
                zIndex: 8,
                cursor: "pointer",
                border: "2px solid rgba(0,0,0,0.45)",
              }}
              title={`${damage.damage_type} · ${damage.severity}`}
            >
              !
            </div>
          ))}
        </div>
      ) : !captureMode ? (
        <p style={{ color: "#777" }}>Upload at least 30 photos to preview a smooth 360 spin. The viewer will only use filled slots, so there will be no empty blank frames.</p>
      ) : null}
    </div>
  );

  const FrameSlots = (
    <div style={{ background: "#050505", border: "1px solid #333", borderRadius: 16, padding: 14, marginBottom: 18 }}>
      <h3 style={{ color: "#d4af37", marginTop: 0 }}>75 Frame Capture Slots</h3>
      <div style={{ color: "#aaa", fontSize: 13, marginBottom: 12 }}>
        Tap a slot to jump to that angle. The public 360 viewer uses only uploaded photos, so no blank frames appear. Minimum recommended for spin/export: {minimumSpinFrames} photos.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
        <button onClick={compactFrames} disabled={completion === 0} style={{ padding: 10, borderRadius: 999, border: "1px solid #d4af37", background: "#111", color: "#d4af37", fontWeight: 900, opacity: completion === 0 ? 0.5 : 1 }}>Compact Filled Slots</button>
        <button onClick={clearAllFrames} disabled={completion === 0} style={{ padding: 10, borderRadius: 999, border: "1px solid #552222", background: "#220000", color: "#ffb4b4", fontWeight: 900, opacity: completion === 0 ? 0.5 : 1 }}>Clear All Frames</button>
      </div>
      {completion > 0 && completion < minimumSpinFrames && (
        <div style={{ color: "#ffcf70", fontSize: 12, marginBottom: 12, background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.25)", borderRadius: 12, padding: 10 }}>
          {completion}/{minimumSpinFrames} minimum photos uploaded. Add {minimumSpinFrames - completion} more for smooth export/share.
        </div>
      )}
      <label
        style={{
          display: "block",
          width: "100%",
          padding: 14,
          marginBottom: 14,
          background: "#111",
          color: "#d4af37",
          border: "1px solid #d4af37",
          borderRadius: 999,
          fontWeight: 900,
          textAlign: "center",
          cursor: uploading ? "wait" : "pointer",
          opacity: uploading ? 0.65 : 1,
        }}
      >
        {uploading ? "Uploading Photos..." : "Bulk Upload From Phone Gallery"}
        <input
          type="file"
          accept="image/*"
          multiple
          disabled={uploading}
          style={{ display: "none" }}
          onChange={handleBulkUpload}
        />
      </label>
      <div style={{ color: "#777", fontSize: 12, marginBottom: 12 }}>
        Tip: select your walkaround photos in order. The app sorts by file name and fills the next empty frame slots automatically.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(52px, 1fr))", gap: 8 }}>
        {images.map((img, index) => {
          const isCurrent = index === captureSlot;
          const isUploaded = Boolean(img);
          return (
            <div key={index} style={{ borderRadius: 12, border: isCurrent ? "2px solid #d4af37" : isUploaded ? "1px solid #22c55e" : "1px solid #333", background: isCurrent ? "rgba(212,175,55,0.18)" : isUploaded ? "rgba(34,197,94,0.12)" : "#111", padding: 6, display: "grid", gap: 5 }}>
              <button onClick={() => setCaptureSlot(index)} style={{ background: "transparent", color: isCurrent ? "#d4af37" : isUploaded ? "#22c55e" : "#aaa", border: "none", fontWeight: 900, cursor: "pointer" }}>{index + 1}</button>
              <label style={{ background: isUploaded ? "#12351f" : "#000", color: isUploaded ? "#22c55e" : "#d4af37", border: "1px solid #333", borderRadius: 999, fontSize: 10, padding: "4px 2px", textAlign: "center", cursor: uploading ? "wait" : "pointer" }}>
                {isUploaded ? "Replace" : "Upload"}
                <input type="file" accept="image/*" disabled={uploading} style={{ display: "none" }} onChange={async (e) => { setCaptureSlot(index); await handleUpload(index, e.target.files?.[0] || null); e.currentTarget.value = ""; }} />
              </label>
              {isUploaded && (
                <button
                  onClick={() => deleteFrameSlot(index)}
                  style={{
                    background: "#220000",
                    color: "#ffb4b4",
                    border: "1px solid #552222",
                    borderRadius: 999,
                    fontSize: 10,
                    padding: "4px 2px",
                    cursor: "pointer",
                  }}
                >
                  Delete
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const InteriorHotspotBox = (
    <div style={{ marginTop: 18, background: customerMode ? "#000" : "#050505", border: customerMode ? "none" : "1px solid #1f1f1f", borderRadius: customerMode ? 0 : 16, padding: customerMode ? 18 : 14 }}>
      <h2 style={{ color: "#d4af37", marginTop: 0 }}>Interior Hotspots</h2>
      {!customerMode && (
        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          <label style={{ flex: 1, background: "#111", border: "1px solid #d4af37", color: "#d4af37", borderRadius: 999, padding: 12, textAlign: "center", fontWeight: 900, cursor: uploading ? "wait" : "pointer", opacity: uploading ? 0.65 : 1 }}>
            Upload Interior Image
            <input type="file" accept="image/*" disabled={uploading} style={{ display: "none" }} onChange={(e) => handleInteriorUpload(e.target.files?.[0] || null)} />
          </label>
          <button onClick={() => setHotspotMode(!hotspotMode)} style={{ flex: 1, background: hotspotMode ? "#d4af37" : "#111", color: hotspotMode ? "#000" : "#d4af37", border: "1px solid #d4af37", borderRadius: 999, padding: 12, fontWeight: 900, cursor: "pointer" }}>{hotspotMode ? "Tap Image to Add" : "Add Hotspots"}</button>
        </div>
      )}
      <div onClick={addHotspot} style={{ position: "relative", minHeight: 260, background: "#111", borderRadius: 16, overflow: "hidden", border: hotspotMode ? "2px dashed #d4af37" : "1px solid #2a2a2a", cursor: hotspotMode ? "crosshair" : "default", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {interiorImage ? <img src={interiorImage} alt="Interior" style={{ width: "100%" }} /> : <p style={{ color: "#777" }}>Upload interior image to add hotspots</p>}
        {hotspots.map((spot) => (
          <div key={spot.id} onClick={(e) => { e.stopPropagation(); trackEvent("hotspot_click", spot.title); alert(`${spot.title}\n\n${spot.note}`); }} style={{ position: "absolute", left: `${spot.x}%`, top: `${spot.y}%`, transform: "translate(-50%, -50%)", width: 28, height: 28, borderRadius: "50%", background: "#d4af37", color: "#000", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, boxShadow: "0 0 20px rgba(212,175,55,0.8)", cursor: "pointer", zIndex: 5 }} title={spot.title}>+</div>
        ))}
      </div>
      {hotspots.length > 0 && !customerMode && (
        <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
          {hotspots.map((spot) => (
            <div key={spot.id} style={{ background: "#111", border: "1px solid #2a2a2a", borderRadius: 12, padding: 10, display: "flex", justifyContent: "space-between", gap: 10 }}>
              <div><div style={{ color: "#d4af37", fontWeight: 900 }}>{spot.title}</div><div style={{ color: "#aaa", fontSize: 13 }}>{spot.note}</div></div>
              <button onClick={() => deleteHotspot(spot.id)} style={{ background: "#220000", color: "#ffb4b4", border: "1px solid #552222", borderRadius: 999, padding: "6px 10px", cursor: "pointer" }}>Delete</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const DamagePanel = !customerMode && (
    <div style={{ background: "#050505", border: "1px solid #333", borderRadius: 16, padding: 14, marginBottom: 18 }}>
      <h3 style={{ color: "#d4af37", marginTop: 0 }}>AI Damage Detection Lite</h3>
      <div style={{ color: "#aaa", fontSize: 13, marginBottom: 12 }}>Tap the current exterior frame to mark scratches, dents, chips, or other damage.</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
        <select value={damageType} onChange={(e) => setDamageType(e.target.value)} style={{ padding: 12, background: "#000", color: "#d4af37", border: "1px solid #333", borderRadius: 10 }}>
          <option value="scratch">Scratch</option><option value="dent">Dent</option><option value="chip">Chip</option><option value="crack">Crack</option><option value="paint">Paint Issue</option><option value="wheel">Wheel Damage</option><option value="interior">Interior Wear</option><option value="other">Other</option>
        </select>
        <select value={damageSeverity} onChange={(e) => setDamageSeverity(e.target.value)} style={{ padding: 12, background: "#000", color: "#d4af37", border: "1px solid #333", borderRadius: 10 }}>
          <option value="light">Light</option><option value="medium">Medium</option><option value="heavy">Heavy</option>
        </select>
      </div>
      <button onClick={() => setDamageMode(!damageMode)} style={{ width: "100%", padding: 12, background: damageMode ? "#d4af37" : "#111", color: damageMode ? "#000" : "#d4af37", border: "1px solid #d4af37", borderRadius: 999, fontWeight: 900, marginBottom: 12, cursor: "pointer" }}>{damageMode ? "Damage Marking ON — Tap Vehicle Image" : "Turn On Damage Marking"}</button>
      <div style={{ color: "#aaa", fontSize: 13, marginBottom: 8 }}>Damage reports: {damageReports.length} total · Current frame: {damageForCurrentFrame.length}</div>
      {damageReports.slice(0, 8).map((damage, index) => (
        <div key={damage.id || index} style={{ background: "#111", border: "1px solid #222", borderRadius: 12, padding: 10, marginTop: 8, color: "#aaa", fontSize: 13 }}><b style={{ color: "#d4af37" }}>Frame {damage.frame_number}</b> · {damage.damage_type} · {damage.severity}<br />{damage.note || "No note"}</div>
      ))}
    </div>
  );

  const InventoryDashboard = showInventoryDashboard && (
    <div style={{ background: "#050505", border: "1px solid #333", borderRadius: 18, padding: 14, marginBottom: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div><h2 style={{ color: "#d4af37", margin: 0 }}>Inventory Dashboard</h2><div style={{ color: "#aaa", fontSize: 13 }}>{filteredInventory.length} vehicles showing · newest first</div></div>
        <button onClick={() => setShowInventoryDashboard(false)} style={{ background: "#111", color: "#aaa", border: "1px solid #333", borderRadius: 999, padding: "8px 12px", fontWeight: 900, cursor: "pointer" }}>Close</button>
      </div>
      <input placeholder="Search by vehicle, VIN, make, model, body type..." value={inventorySearch} onChange={(e) => setInventorySearch(e.target.value)} style={{ width: "100%", padding: 12, background: "#000", color: "#fff", border: "1px solid #333", borderRadius: 10, marginBottom: 12 }} />
      <div style={{ display: "grid", gap: 10 }}>
        {filteredInventory.map((vehicle) => {
          const link = `${window.location.origin}${window.location.pathname}?vehicle=${vehicle.id}`;
          const vehicleTitle = vehicle.name || `${vehicle.year || ""} ${vehicle.make || ""} ${vehicle.model || ""}`.trim() || "Untitled Vehicle";
          return (
            <div key={vehicle.id} style={{ background: "#111", border: "1px solid #222", borderRadius: 14, padding: 12 }}>
              <div style={{ color: "#d4af37", fontWeight: 900 }}>{vehicleTitle}</div>
              <div style={{ color: "#aaa", fontSize: 13, marginTop: 4 }}>VIN: {vehicle.vin || "N/A"} · Body: {vehicle.body_class || "N/A"}</div>
              <div style={{ color: "#777", fontSize: 12, marginTop: 4 }}>Created: {vehicle.created_at ? new Date(vehicle.created_at).toLocaleString() : "N/A"}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 12 }}>
                <button onClick={() => openInventoryVehicle(vehicle)} style={{ padding: 10, background: "#d4af37", color: "#000", border: "none", borderRadius: 999, fontWeight: 900, cursor: "pointer" }}>Open</button>
                <button onClick={() => navigator.clipboard.writeText(link)} style={{ padding: 10, background: "#111", color: "#d4af37", border: "1px solid #d4af37", borderRadius: 999, fontWeight: 900, cursor: "pointer" }}>Copy Link</button>
                <a href={link} target="_blank" rel="noreferrer" style={{ padding: 10, background: "#050505", color: "#fff", border: "1px solid #333", borderRadius: 999, fontWeight: 900, textAlign: "center", textDecoration: "none" }}>View Live</a>
              </div>
            </div>
          );
        })}
        {filteredInventory.length === 0 && <div style={{ color: "#777", textAlign: "center", padding: 18, border: "1px dashed #333", borderRadius: 14 }}>No vehicles found.</div>}
      </div>
    </div>
  );

  const AnalyticsPanel = currentVehicleId && !customerMode && (
    <div style={{ background: "#050505", border: "1px solid #333", borderRadius: 14, padding: 14, marginBottom: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}><h3 style={{ color: "#d4af37", marginTop: 0 }}>Analytics</h3><button onClick={() => loadAnalytics(currentVehicleId)} style={{ height: 36, padding: "0 12px", borderRadius: 999, border: "1px solid #d4af37", background: "#111", color: "#d4af37", fontWeight: 900 }}>Refresh</button></div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 12 }}>{[["Views", countEvent("view")], ["Spins", countEvent("spin")], ["CTA Clicks", countEvent("cta_click")], ["Hotspots", countEvent("hotspot_click")]].map((item) => (<div key={item[0] as string} style={{ background: "#111", border: "1px solid #222", borderRadius: 12, padding: 10, textAlign: "center" }}><div style={{ color: "#d4af37", fontSize: 22, fontWeight: 900 }}>{item[1]}</div><div style={{ color: "#aaa", fontSize: 12 }}>{item[0]}</div></div>))}</div>
      {analytics.slice(0, 8).map((event) => (<div key={event.id} style={{ display: "flex", justifyContent: "space-between", gap: 10, color: "#aaa", fontSize: 12, borderTop: "1px solid #1f1f1f", padding: "8px 0" }}><span><b style={{ color: "#d4af37" }}>{event.event_type}</b> · {event.event_label}</span><span>{new Date(event.created_at).toLocaleString()}</span></div>))}
    </div>
  );

  if (publicViewVehicle) {
    return (
      <div style={{ minHeight: "100vh", background: "#000", color: "#fff", fontFamily: "Arial" }}>
        <div style={{ padding: 22, background: "linear-gradient(180deg, #151515, #000)", borderBottom: "1px solid #222" }}>
          <div style={{ color: "#d4af37", fontWeight: 900, letterSpacing: 2, fontSize: 12, marginBottom: 8 }}>
            DRIVEWITHARTURO PUBLIC 360 PAGE
          </div>
          <h1 style={{ color: "#d4af37", margin: "0 0 6px" }}>{publicViewVehicle.vehicleName || "Vehicle 360 View"}</h1>
          {publicViewVehicle.vehiclePrice && (
            <div style={{ fontSize: 28, fontWeight: 900, color: "#fff", marginBottom: 6 }}>
              {publicViewVehicle.vehiclePrice}
            </div>
          )}
          <div style={{ color: "#aaa", fontSize: 13 }}>
            VIN: {publicViewVehicle.vin || "Contact for details"} · Summit, IL · Hablamos Español
          </div>
        </div>

        {Viewer}

        <div style={{ margin: 18, background: "linear-gradient(180deg, #111, #050505)", border: "1px solid #222", borderRadius: 24, padding: 18 }}>
          <h2 style={{ color: "#d4af37", marginTop: 0 }}>Ready to see it in person?</h2>
          <p style={{ color: "#aaa", lineHeight: 1.5 }}>
            Text Arturo for availability, financing options, trade-in value, and a full out-the-door number.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <a href="sms:+18723640886" style={{ background: "#d4af37", color: "#000", padding: 14, borderRadius: 999, textAlign: "center", textDecoration: "none", fontWeight: 900 }}>Text Arturo</a>
            <a href="tel:+18723640886" style={{ background: "#111", color: "#d4af37", padding: 14, borderRadius: 999, textAlign: "center", textDecoration: "none", fontWeight: 900, border: "1px solid #d4af37" }}>Call Now</a>
            <a href="https://wa.me/18723640886" style={{ background: "#111", color: "#25D366", padding: 14, borderRadius: 999, textAlign: "center", textDecoration: "none", fontWeight: 900, border: "1px solid #25D366" }}>WhatsApp</a>
            <a href="sms:+18723640886?body=Hi Arturo, I want to schedule a test drive" style={{ background: "#111", color: "#fff", padding: 14, borderRadius: 999, textAlign: "center", textDecoration: "none", fontWeight: 900, border: "1px solid #333" }}>Test Drive</a>
          </div>
          <div style={{ marginTop: 16, color: "#777", fontSize: 12, lineHeight: 1.5 }}>
            Price plus taxes, title, registration, doc, and dealer fees. Financing subject to approval. Vehicle subject to availability.
          </div>
        </div>

        <div style={{ margin: 18, background: "#050505", border: "1px solid #222", borderRadius: 24, padding: 18 }}>
          {commandPaletteOpen && (
        <div
          onClick={() => setCommandPaletteOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", zIndex: 2000, display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: 100 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: "min(760px,92vw)", background: "#050505", border: "1px solid #333", borderRadius: 24, overflow: "hidden", boxShadow: "0 0 40px rgba(0,0,0,0.6)" }}
          >
            <div style={{ padding: 16, borderBottom: "1px solid #222" }}>
              <input
                autoFocus
                placeholder="Search modules, CRM, vehicles, analytics..."
                value={commandSearch}
                onChange={(e) => setCommandSearch(e.target.value)}
                style={{ width: "100%", boxSizing: "border-box", padding: 16, background: "#111", color: "#fff", border: "1px solid #333", borderRadius: 16, fontSize: 16 }}
              />

              <div style={{ color: "#777", fontSize: 12, marginTop: 8 }}>
                Press Ctrl+K or / to open • ESC to close
              </div>
            </div>

            <div style={{ maxHeight: 480, overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
              {commandPaletteResults.length > 0 ? (
                commandPaletteResults.map((item, index) => (
                  <button
                    key={item}
                    onClick={() => {
                      scrollToModule(item);
                      setCommandPaletteOpen(false);
                    }}
                    style={{ textAlign: "left", padding: 16, borderRadius: 16, border: "1px solid #222", background: "#090909", color: "#fff", cursor: "pointer" }}
                  >
                    <div style={{ color: "#d4af37", fontWeight: 900, marginBottom: 4 }}>
                      {moduleMenuItems.indexOf(item) + 1}. {item}
                    </div>
                    <div style={{ color: "#888", fontSize: 12 }}>
                      Open module instantly
                    </div>
                  </button>
                ))
              ) : (
                <div style={{ color: "#888", padding: 20, textAlign: "center" }}>
                  No results found.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div style={{ position: "fixed", top: 18, right: 18, zIndex: 3000, display: "flex", flexDirection: "column", gap: 10, width: 340 }}>
        {toastNotifications.map((toast) => (
          <div
            key={toast.id}
            style={{
              background:
                toast.type === "success"
                  ? "linear-gradient(135deg,#052e16,#14532d)"
                  : toast.type === "warning"
                  ? "linear-gradient(135deg,#451a03,#78350f)"
                  : toast.type === "error"
                  ? "linear-gradient(135deg,#450a0a,#7f1d1d)"
                  : "linear-gradient(135deg,#111,#050505)",
              border:
                toast.type === "success"
                  ? "1px solid #22c55e"
                  : toast.type === "warning"
                  ? "1px solid #f59e0b"
                  : toast.type === "error"
                  ? "1px solid #ef4444"
                  : `1px solid ${currentTheme.primary}`,
              borderRadius: 18,
              padding: 14,
              color: "#fff",
              boxShadow: "0 0 25px rgba(0,0,0,0.35)",
              animation: "fadeIn 0.25s ease",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 6 }}>
              <div style={{ fontWeight: 900 }}>{toast.title}</div>

              <button
                onClick={() =>
                  setToastNotifications((prev) =>
                    prev.filter((item) => item.id !== toast.id)
                  )
                }
                style={{ border: "none", background: "transparent", color: "#fff", cursor: "pointer", fontWeight: 900 }}
              >
                ✕
              </button>
            </div>

            <div style={{ color: "#ddd", lineHeight: 1.5, fontSize: 13 }}>
              {toast.message}
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={() => setPluginMarketplaceOpen((prev) => !prev)}
            style={{ padding: "12px 14px", borderRadius: 14, border: `1px solid ${currentTheme.primary}`, background: currentTheme.accent, color: currentTheme.primary, fontWeight: 900 }}
          >
            🧩
          </button>

          {pluginMarketplaceOpen && (
            <div style={{ position: "absolute", top: 70, left: 410, width: 460, background: currentTheme.background, border: `1px solid ${currentTheme.primary}`, borderRadius: 24, padding: 16, zIndex: 2950, boxShadow: "0 0 40px rgba(0,0,0,0.5)", backdropFilter: "blur(18px)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div>
                  <div style={{ color: currentTheme.primary, fontWeight: 900, fontSize: 20 }}>
                    Dealership App Store
                  </div>
                  <div style={{ color: "#888", fontSize: 12, marginTop: 4 }}>
                    Install and manage dealership plugins and integrations.
                  </div>
                </div>

                <div style={{ background: "#111827", color: currentTheme.primary, padding: "8px 12px", borderRadius: 999, fontWeight: 900, fontSize: 11 }}>
                  {installedPlugins.filter((plugin) => plugin.installed).length} INSTALLED
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 12, maxHeight: 460, overflowY: "auto" }}>
                {installedPlugins.map((plugin) => (
                  <div key={plugin.id} style={{ background: "#090909", border: "1px solid #222", borderRadius: 18, padding: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
                      <div>
                        <div style={{ color: currentTheme.primary, fontWeight: 900, marginBottom: 4 }}>
                          {plugin.name}
                        </div>

                        <div style={{ color: "#888", fontSize: 11 }}>
                          {plugin.category}
                        </div>
                      </div>

                      <button
                        onClick={() => {
                          setInstalledPlugins((prev) =>
                            prev.map((item) =>
                              item.id === plugin.id
                                ? {
                                    ...item,
                                    installed: !item.installed,
                                  }
                                : item
                            )
                          );

                          pushSystemNotification(
                            plugin.installed
                              ? "Plugin Disabled"
                              : "Plugin Installed",
                            `${plugin.name} updated successfully.`,
                            "success"
                          );
                        }}
                        style={{ padding: "10px 14px", borderRadius: 12, border: plugin.installed ? "1px solid #7f1d1d" : `1px solid ${currentTheme.primary}`, background: plugin.installed ? "#450a0a" : currentTheme.primary, color: plugin.installed ? "#fecaca" : "#000", fontWeight: 900, cursor: "pointer", height: "fit-content" }}
                      >
                        {plugin.installed ? "Disable" : "Install"}
                      </button>
                    </div>

                    <div style={{ color: "#ddd", lineHeight: 1.6, fontSize: 13, marginBottom: 10 }}>
                      {plugin.description}
                    </div>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <div style={{ background: "#111", border: "1px solid #222", borderRadius: 999, padding: "6px 10px", color: currentTheme.primary, fontSize: 11, fontWeight: 900 }}>
                        Modular Plugin
                      </div>

                      <div style={{ background: "#111", border: "1px solid #222", borderRadius: 999, padding: "6px 10px", color: "#888", fontSize: 11, fontWeight: 900 }}>
                        API Ready
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={() => setCommandCenterOpen((prev) => !prev)}
            style={{ padding: "12px 14px", borderRadius: 14, border: `1px solid ${currentTheme.primary}`, background: currentTheme.accent, color: currentTheme.primary, fontWeight: 900 }}
          >
            🌐
          </button>

          {commandCenterOpen && (
            <div style={{ position: "absolute", top: 70, left: 330, width: 720, height: 520, background: "rgba(2,6,23,0.96)", border: `1px solid ${currentTheme.primary}`, borderRadius: 26, padding: 18, zIndex: 2900, overflow: "hidden", boxShadow: "0 0 40px rgba(0,0,0,0.55)", backdropFilter: "blur(18px)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div>
                  <div style={{ color: currentTheme.primary, fontWeight: 900, fontSize: 22 }}>
                    3D Dealership Command Center
                  </div>
                  <div style={{ color: "#888", fontSize: 12, marginTop: 4 }}>
                    Futuristic dealership intelligence visualization layer
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ background: "#052e16", color: "#bbf7d0", padding: "8px 12px", borderRadius: 999, fontWeight: 900, fontSize: 11 }}>
                    LIVE
                  </div>

                  <div style={{ background: "#111827", color: currentTheme.primary, padding: "8px 12px", borderRadius: 999, fontWeight: 900, fontSize: 11 }}>
                    3D VISUALIZATION
                  </div>
                </div>
              </div>

              <div style={{ position: "relative", height: 420, borderRadius: 24, overflow: "hidden", background: "radial-gradient(circle at center,#0f172a 0%,#020617 65%,#000 100%)", border: "1px solid #1e293b" }}>
                <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle,rgba(56,189,248,0.12) 0%,transparent 70%)", filter: "blur(40px)" }} />

                <div style={{ position: "absolute", left: "50%", top: "50%", width: 240, height: 240, transform: "translate(-50%,-50%)", borderRadius: "50%", border: `2px solid ${currentTheme.primary}`, boxShadow: `0 0 40px ${currentTheme.primary}55 inset`, background: "radial-gradient(circle at center,rgba(255,255,255,0.06) 0%,transparent 70%)" }} />

                {commandCenterNodes.map((node) => (
                  <div
                    key={node.id}
                    style={{
                      position: "absolute",
                      left: `${node.x}%`,
                      top: `${node.y}%`,
                      width: node.size,
                      height: node.size,
                      transform: "translate(-50%,-50%)",
                      borderRadius: "50%",
                      background: `radial-gradient(circle,${currentTheme.primary}33 0%,transparent 70%)`,
                      border: `1px solid ${currentTheme.primary}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      padding: 16,
                      boxShadow: `0 0 30px ${currentTheme.primary}44`,
                      backdropFilter: "blur(12px)",
                    }}
                  >
                    <div>
                      <div style={{ color: currentTheme.primary, fontWeight: 900, marginBottom: 6 }}>
                        {node.label}
                      </div>

                      <div style={{ color: "#cbd5e1", fontSize: 11, lineHeight: 1.5 }}>
                        Live dealership intelligence node
                      </div>
                    </div>
                  </div>
                ))}

                <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
                  <line x1="24%" y1="28%" x2="50%" y2="50%" stroke={currentTheme.primary} strokeWidth="1.5" opacity="0.5" />
                  <line x1="62%" y1="38%" x2="50%" y2="50%" stroke={currentTheme.primary} strokeWidth="1.5" opacity="0.5" />
                  <line x1="38%" y1="72%" x2="50%" y2="50%" stroke={currentTheme.primary} strokeWidth="1.5" opacity="0.5" />
                  <line x1="76%" y1="18%" x2="50%" y2="50%" stroke={currentTheme.primary} strokeWidth="1.5" opacity="0.5" />
                </svg>
              </div>
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={() => setDealershipMemoryOpen((prev) => !prev)}
            style={{ padding: "12px 14px", borderRadius: 14, border: `1px solid ${currentTheme.primary}`, background: currentTheme.accent, color: currentTheme.primary, fontWeight: 900 }}
          >
            🧠
          </button>

          {dealershipMemoryOpen && (
            <div style={{ position: "absolute", top: 70, left: 250, width: 440, background: currentTheme.background, border: `1px solid ${currentTheme.primary}`, borderRadius: 22, padding: 14, zIndex: 2800, boxShadow: "0 0 35px rgba(0,0,0,0.45)", backdropFilter: "blur(18px)" }}>
              <div style={{ color: currentTheme.primary, fontWeight: 900, marginBottom: 12 }}>
                AI Dealership Memory Brain
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {dealershipMemory.map((memoryItem) => (
                  <div key={memoryItem.id} style={{ background: "#090909", border: "1px solid #222", borderRadius: 18, padding: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
                      <div>
                        <div style={{ color: currentTheme.primary, fontWeight: 900, marginBottom: 4 }}>
                          {memoryItem.title}
                        </div>

                        <div style={{ color: "#888", fontSize: 11 }}>
                          {memoryItem.category}
                        </div>
                      </div>

                      <div style={{ background: "#052e16", color: "#bbf7d0", padding: "6px 10px", borderRadius: 999, fontWeight: 900, fontSize: 11, height: "fit-content" }}>
                        {memoryItem.confidence}%
                      </div>
                    </div>

                    <div style={{ color: "#ddd", lineHeight: 1.6, fontSize: 13 }}>
                      {memoryItem.memory}
                    </div>
                  </div>
                ))}

                <button
                  onClick={() => {
                    const nextMemory = {
                      id: Date.now(),
                      category: "AI Intelligence",
                      title: "New Relationship Pattern",
                      memory:
                        "AI dealership brain detected a new customer engagement pattern.",
                      confidence: 90,
                    };

                    setDealershipMemory((prev) => [nextMemory, ...prev]);

                    pushSystemNotification(
                      "Memory Learned",
                      "AI dealership memory stored new relationship intelligence.",
                      "success"
                    );
                  }}
                  style={{ padding: 14, borderRadius: 16, border: "none", background: currentTheme.primary, color: "#000", fontWeight: 900 }}
                >
                  Learn New Pattern
                </button>
              </div>
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={() => setAutomationBuilderOpen((prev) => !prev)}
            style={{ padding: "12px 14px", borderRadius: 14, border: `1px solid ${currentTheme.primary}`, background: currentTheme.accent, color: currentTheme.primary, fontWeight: 900 }}
          >
            ⚡
          </button>

          {automationBuilderOpen && (
            <div style={{ position: "absolute", top: 70, left: 170, width: 420, background: currentTheme.background, border: `1px solid ${currentTheme.primary}`, borderRadius: 22, padding: 14, zIndex: 2700, boxShadow: "0 0 35px rgba(0,0,0,0.45)", backdropFilter: "blur(18px)" }}>
              <div style={{ color: currentTheme.primary, fontWeight: 900, marginBottom: 12 }}>
                AI Automation Flow Builder
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {automationFlows.map((flow) => (
                  <div key={flow.id} style={{ background: "#090909", border: "1px solid #222", borderRadius: 18, padding: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 10 }}>
                      <div style={{ color: currentTheme.primary, fontWeight: 900 }}>
                        Automation #{flow.id}
                      </div>

                      <div style={{ background: "#052e16", color: "#bbf7d0", padding: "6px 10px", borderRadius: 999, fontWeight: 900, fontSize: 11 }}>
                        {flow.status}
                      </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <div style={{ background: "#111", borderRadius: 14, padding: 12 }}>
                        <div style={{ color: "#888", fontSize: 11, marginBottom: 4 }}>
                          IF THIS HAPPENS
                        </div>
                        <div style={{ color: "#fff", fontWeight: 800 }}>
                          {flow.trigger}
                        </div>
                      </div>

                      <div style={{ display: "flex", justifyContent: "center", color: currentTheme.primary, fontWeight: 900 }}>
                        ↓
                      </div>

                      <div style={{ background: "#111", borderRadius: 14, padding: 12 }}>
                        <div style={{ color: "#888", fontSize: 11, marginBottom: 4 }}>
                          THEN EXECUTE
                        </div>
                        <div style={{ color: "#fff", fontWeight: 800 }}>
                          {flow.action}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  onClick={() => {
                    const nextFlow = {
                      id: Date.now(),
                      trigger: "New AI dealership trigger",
                      action: "Execute automated workflow",
                      status: "ACTIVE",
                    };

                    setAutomationFlows((prev) => [nextFlow, ...prev]);

                    pushSystemNotification(
                      "Automation Created",
                      "New AI automation flow deployed successfully.",
                      "success"
                    );
                  }}
                  style={{ padding: 14, borderRadius: 16, border: "none", background: currentTheme.primary, color: "#000", fontWeight: 900 }}
                >
                  Create Automation Flow
                </button>
              </div>
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={() => setAmbientEngineOpen((prev) => !prev)}
            style={{ padding: "12px 14px", borderRadius: 14, border: `1px solid ${currentTheme.primary}`, background: currentTheme.accent, color: currentTheme.primary, fontWeight: 900 }}
          >
            🌌
          </button>

          {ambientEngineOpen && (
            <div style={{ position: "absolute", top: 70, left: 90, width: 340, background: currentTheme.background, border: `1px solid ${currentTheme.primary}`, borderRadius: 22, padding: 14, zIndex: 2600, boxShadow: "0 0 35px rgba(0,0,0,0.45)", backdropFilter: "blur(18px)" }}>
              <div style={{ color: currentTheme.primary, fontWeight: 900, marginBottom: 12 }}>
                Ambient Environment Engine
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {ambientPresets.map((preset) => (
                  <button
                    key={preset.name}
                    onClick={() => {
                      setAmbientEnvironment(preset.name);
                      pushSystemNotification(
                        "Environment Updated",
                        `${preset.name} environment activated.`,
                        "theme"
                      );
                    }}
                    style={{ background: preset.background, border: ambientEnvironment === preset.name ? `1px solid ${currentTheme.primary}` : "1px solid #333", borderRadius: 18, padding: 14, minHeight: 74, color: preset.name === "Executive Minimal" ? "#111" : "#fff", textAlign: "left", position: "relative", overflow: "hidden" }}
                  >
                    <div style={{ position: "absolute", inset: 0, background: preset.glow, filter: "blur(40px)" }} />

                    <div style={{ position: "relative", zIndex: 2 }}>
                      <div style={{ fontWeight: 900, marginBottom: 4 }}>
                        {preset.name}
                      </div>

                      <div style={{ fontSize: 12, opacity: 0.8 }}>
                        Cinematic dealership workspace atmosphere
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={() => setThemeEngineOpen((prev) => !prev)}
            style={{ padding: "12px 14px", borderRadius: 14, border: `1px solid ${currentTheme.primary}`, background: currentTheme.accent, color: currentTheme.primary, fontWeight: 900 }}
          >
            🎨
          </button>

          {themeEngineOpen && (
            <div style={{ position: "absolute", top: 70, left: 20, width: 320, background: currentTheme.background, border: `1px solid ${currentTheme.primary}`, borderRadius: 20, padding: 14, zIndex: 2500, boxShadow: "0 0 35px rgba(0,0,0,0.45)" }}>
              <div style={{ color: currentTheme.primary, fontWeight: 900, marginBottom: 12 }}>
                Theme Engine
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {dealershipThemes.map((theme) => (
                  <button
                    key={theme.name}
                    onClick={() => {
                      setActiveTheme(theme.name);
                      pushSystemNotification(
                        "Theme Updated",
                        `${theme.name} theme applied successfully.`,
                        "theme"
                      );
                    }}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, background: theme.accent, border: activeTheme === theme.name ? `1px solid ${theme.primary}` : "1px solid #333", borderRadius: 16, padding: 12, cursor: "pointer" }}
                  >
                    <div style={{ textAlign: "left" }}>
                      <div style={{ color: theme.primary, fontWeight: 900 }}>
                        {theme.name}
                      </div>
                      <div style={{ color: "#888", fontSize: 12 }}>
                        Dealership UI theme preset
                      </div>
                    </div>

                    <div style={{ width: 24, height: 24, borderRadius: 999, background: theme.primary }} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
            <h2 style={{ color: "#d4af37", margin: 0 }}>AI Sales Assistant</h2>

            <select
              value={aiPersonality}
              onChange={(e) => setAiPersonality(e.target.value)}
              style={{ background: "#111", color: "#d4af37", border: "1px solid #333", borderRadius: 10, padding: 8 }}
            >
              <option value="professional">Professional</option>
              <option value="closer">Aggressive Closer</option>
              <option value="friendly">Friendly</option>
              <option value="bilingual">Bilingual</option>
              <option value="hispanic">Hispanic Community</option>
            </select>
          </div>

          <div style={{ background: "#000", border: "1px solid #222", borderRadius: 18, padding: 12, maxHeight: 320, overflowY: "auto", marginBottom: 12 }}>
            {chatMessages.length === 0 ? (
              <div style={{ color: "#666", fontSize: 14 }}>
                Ask about availability, financing, down payment, trade-ins, shipping, CarPlay, MPG, or appointments.
              </div>
            ) : (
              chatMessages.map((msg, idx) => (
                <div
                  key={`${msg.createdAt}-${idx}`}
                  style={{
                    display: "flex",
                    justifyContent: msg.role === "assistant" ? "flex-start" : "flex-end",
                    marginBottom: 10,
                  }}
                >
                  <div
                    style={{
                      maxWidth: "82%",
                      background: msg.role === "assistant" ? "#111" : "#d4af37",
                      color: msg.role === "assistant" ? "#fff" : "#000",
                      padding: 12,
                      borderRadius: 16,
                      lineHeight: 1.5,
                      fontSize: 14,
                    }}
                  >
                    {msg.content}
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
            <input
              placeholder="Ask a question about this vehicle"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && chatInput.trim()) {
                  generateAiResponse(chatInput.trim());
                }
              }}
              style={{ flex: 1, padding: 12, background: "#111", color: "#fff", border: "1px solid #333", borderRadius: 12 }}
            />

            <button
              onClick={() => {
                if (chatInput.trim()) {
                  generateAiResponse(chatInput.trim());
                }
              }}
              style={{ padding: "0 18px", borderRadius: 12, border: "none", background: "#d4af37", color: "#000", fontWeight: 900 }}
            >
              Send
            </button>
          </div>
        </div>

        <div style={{ margin: 18, background: "#050505", border: "1px solid #222", borderRadius: 24, padding: 18 }}>
          <h2 style={{ color: "#d4af37", marginTop: 0 }}>Request Availability / Financing</h2>
          <div style={{ display: "grid", gap: 10 }}>
            <input
              placeholder="Full Name"
              value={leadForm.name}
              onChange={(e) => setLeadForm((p) => ({ ...p, name: e.target.value }))}
              style={{ padding: 12, background: "#111", border: "1px solid #333", borderRadius: 12, color: "#fff" }}
            />
            <input
              placeholder="Phone Number"
              value={leadForm.phone}
              onChange={(e) => setLeadForm((p) => ({ ...p, phone: e.target.value }))}
              style={{ padding: 12, background: "#111", border: "1px solid #333", borderRadius: 12, color: "#fff" }}
            />
            <input
              placeholder="Email Address"
              value={leadForm.email}
              onChange={(e) => setLeadForm((p) => ({ ...p, email: e.target.value }))}
              style={{ padding: 12, background: "#111", border: "1px solid #333", borderRadius: 12, color: "#fff" }}
            />
            <input
              placeholder="Preferred Appointment Time"
              value={leadForm.appointment}
              onChange={(e) => setLeadForm((p) => ({ ...p, appointment: e.target.value }))}
              style={{ padding: 12, background: "#111", border: "1px solid #333", borderRadius: 12, color: "#fff" }}
            />
            <textarea
              placeholder="Questions, trade-in info, or notes"
              value={leadForm.notes}
              onChange={(e) => setLeadForm((p) => ({ ...p, notes: e.target.value }))}
              rows={4}
              style={{ padding: 12, background: "#111", border: "1px solid #333", borderRadius: 12, color: "#fff", resize: "vertical" }}
            />

            <div style={{ display: "flex", gap: 18, flexWrap: "wrap", color: "#ddd", fontSize: 14 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={leadForm.tradeIn}
                  onChange={(e) => setLeadForm((p) => ({ ...p, tradeIn: e.target.checked }))}
                />
                I have a trade-in
              </label>

              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={leadForm.financing}
                  onChange={(e) => setLeadForm((p) => ({ ...p, financing: e.target.checked }))}
                />
                I need financing
              </label>
            </div>

            <button
              onClick={submitVehicleLead}
              style={{
                padding: 14,
                borderRadius: 999,
                border: "none",
                background: "#d4af37",
                color: "#000",
                fontWeight: 900,
                fontSize: 16,
              }}
            >
              Submit Lead Request
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!adminUnlocked && !publicViewVehicle && !customerMode) {
    return (
      <div style={{ minHeight: "100vh", background: "linear-gradient(180deg, #000, #090909)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "Arial" }}>
        <div style={{ width: "100%", maxWidth: 420, background: "#050505", border: "1px solid #333", borderRadius: 24, padding: 24, boxShadow: "0 0 40px rgba(0,0,0,0.5)" }}>
          <div style={{ color: "#d4af37", fontWeight: 900, letterSpacing: 2, fontSize: 12, marginBottom: 10 }}>
            DRIVEWITHARTURO 360 ADMIN
          </div>
          <h1 style={{ color: "#d4af37", marginTop: 0 }}>Admin Panel Locked</h1>
          <p style={{ color: "#aaa", lineHeight: 1.5 }}>Enter your passcode to access capture tools, inventory, leads, reels, and public page controls.</p>
          <input
            type="password"
            inputMode="numeric"
            placeholder="Enter passcode"
            value={adminPasscodeInput}
            onChange={(e) => setAdminPasscodeInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && adminPasscodeInput === ADMIN_PASSCODE) {
                localStorage.setItem("drivewitharturo-admin-unlocked", "true");
                setAdminUnlocked(true);
              }
            }}
            style={{ width: "100%", boxSizing: "border-box", padding: 14, background: "#111", color: "#fff", border: "1px solid #333", borderRadius: 14, marginBottom: 12, fontSize: 18, textAlign: "center" }}
          />
          <button
            onClick={() => {
              if (adminPasscodeInput === ADMIN_PASSCODE) {
                localStorage.setItem("drivewitharturo-admin-unlocked", "true");
                setAdminUnlocked(true);
              } else {
                alert("Incorrect passcode.");
              }
            }}
            style={{ width: "100%", padding: 14, borderRadius: 999, border: "none", background: "#d4af37", color: "#000", fontWeight: 900, fontSize: 16 }}
          >
            Unlock Admin Panel
          </button>
        </div>
      </div>
    );
  }

  if (loadingVehicle) {
    return <div style={{ minHeight: "100vh", background: "#000", color: "#d4af37", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Arial" }}>Loading vehicle...</div>;
  }

  if (publicInventoryMode) {
    return (
      <div style={{ minHeight: "100vh", background: "linear-gradient(180deg, #000, #070707)", color: "#fff", fontFamily: "Arial" }}>
        <div style={{ padding: 24, borderBottom: "1px solid #1f1f1f", background: "linear-gradient(180deg, rgba(18,18,18,0.98), rgba(0,0,0,1))" }}>
          <div style={{ color: "#d4af37", fontWeight: 900, letterSpacing: 2, fontSize: 12, marginBottom: 10 }}>DRIVEWITHARTURO INVENTORY</div>
          <h1 style={{ color: "#d4af37", marginBottom: 6 }}>Chicago Used Cars · 360 Vehicle Experience</h1>
          <p style={{ color: "#aaa", maxWidth: 720, lineHeight: 1.6 }}>Browse premium used vehicles in Summit, Illinois with immersive 360° viewing, interior hotspots, financing assistance, trade-in support, and direct contact with Arturo.</p>
          <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "1fr auto", gap: 10 }}><input placeholder="Search inventory by make, model, VIN, body style..." value={inventorySearch} onChange={(e) => setInventorySearch(e.target.value)} style={{ padding: 14, background: "#111", color: "#fff", border: "1px solid #333", borderRadius: 12 }} /><button onClick={() => setPublicInventoryMode(false)} style={{ padding: "0 18px", background: "#111", color: "#d4af37", border: "1px solid #d4af37", borderRadius: 999, fontWeight: 900, cursor: "pointer" }}>Dealer Studio</button></div>
        </div>
        <div style={{ padding: 18, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 18 }}>
          {filteredInventory.map((vehicle) => {
            const title = vehicle.name || `${vehicle.year || ""} ${vehicle.make || ""} ${vehicle.model || ""}`.trim();
            const shareUrl = `${window.location.origin}${window.location.pathname}?vehicle=${vehicle.id}`;
            return (
              <div key={vehicle.id} style={{ background: "linear-gradient(180deg, #121212, #080808)", border: "1px solid #1f1f1f", borderRadius: 24, overflow: "hidden", boxShadow: "0 0 35px rgba(0,0,0,0.45)" }}>
                <div style={{ height: 220, background: "radial-gradient(circle at center, #1a1a1a 0%, #050505 70%, #000 100%)", display: "flex", alignItems: "center", justifyContent: "center", color: "#777", fontWeight: 700, borderBottom: "1px solid #1f1f1f" }}>360 Vehicle Viewer</div>
                <div style={{ padding: 18 }}><div style={{ color: "#d4af37", fontWeight: 900, fontSize: 20 }}>{title}</div><div style={{ color: "#aaa", marginTop: 8, lineHeight: 1.5 }}>{vehicle.body_class || "Used Vehicle"} · Financing Available · Trade-ins Welcome · Hablamos Español</div><div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}><button onClick={() => openInventoryVehicle(vehicle)} style={{ padding: 14, background: "#d4af37", color: "#000", border: "none", borderRadius: 999, fontWeight: 900, cursor: "pointer" }}>View 360</button><a href={shareUrl} target="_blank" rel="noreferrer" style={{ padding: 14, background: "#111", color: "#d4af37", border: "1px solid #d4af37", borderRadius: 999, fontWeight: 900, textAlign: "center", textDecoration: "none" }}>Open Page</a></div></div>
              </div>
            );
          })}
        </div>
        <div style={{ padding: 24, borderTop: "1px solid #1f1f1f", color: "#777", fontSize: 13, lineHeight: 1.6 }}>DriveWithArturo · Chicago Cars US · Summit, Illinois · Used car financing available for qualified buyers. Vehicles subject to availability. Listed prices are plus taxes and fees. Contact Arturo for complete out-the-door pricing.</div>
      </div>
    );
  }

  if (customerMode) {
    return (
      <div style={{ minHeight: "100vh", background: "#000", color: "#fff", fontFamily: "Arial" }}>
        <div style={{ padding: 22, background: "linear-gradient(180deg, rgba(18,18,18,0.96), rgba(0,0,0,0.98))", borderBottom: "1px solid #1f1f1f" }}>
          <div style={{ color: "#d4af37", fontSize: 12, fontWeight: 900, letterSpacing: 2, marginBottom: 8 }}>DRIVEWITHARTURO 360 EXPERIENCE</div>
          <h1 style={{ color: "#d4af37", marginBottom: 4 }}>{vehicleName || "Vehicle 360 View"}</h1>
          <p style={{ color: "#aaa", marginTop: 0, marginBottom: 0 }}>Swipe, drag, or let the luxury auto-spin showcase the vehicle.</p>
          {vehicleSubtitle && <div style={{ marginTop: 12, display: "inline-flex", padding: "8px 14px", borderRadius: 999, background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.25)", color: "#d4af37", fontWeight: 700, fontSize: 13 }}>{vehicleSubtitle}</div>}
        </div>
        {Viewer}
        {InteriorHotspotBox}
        <div style={{ margin: 18, background: "linear-gradient(180deg, #0e0e0e, #050505)", border: "1px solid #1f1f1f", borderRadius: 24, padding: 20, boxShadow: "0 0 35px rgba(0,0,0,0.45)" }}>
          <div style={{ color: "#d4af37", fontWeight: 900, fontSize: 20, marginBottom: 6 }}>Interested in this vehicle?</div>
          <div style={{ color: "#aaa", marginBottom: 18 }}>Contact Arturo directly for financing options, trade-ins, test drives, and out-the-door pricing.</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}><a href="sms:+18723640886" onClick={() => trackEvent("cta_click", "text_arturo")} style={{ background: "#d4af37", color: "#000", padding: 14, borderRadius: 999, textAlign: "center", textDecoration: "none", fontWeight: 900 }}>Text Arturo</a><a href="tel:+18723640886" onClick={() => trackEvent("cta_click", "call")} style={{ background: "#111", color: "#d4af37", padding: 14, borderRadius: 999, textAlign: "center", textDecoration: "none", fontWeight: 900, border: "1px solid #d4af37" }}>Call</a><a href="https://wa.me/18723640886" onClick={() => trackEvent("cta_click", "whatsapp")} style={{ background: "#111", color: "#25D366", padding: 14, borderRadius: 999, textAlign: "center", textDecoration: "none", fontWeight: 900, border: "1px solid #25D366" }}>WhatsApp</a><a href="sms:+18723640886?body=Hi Arturo, I want to schedule a test drive" onClick={() => trackEvent("cta_click", "test_drive")} style={{ background: "#111", color: "#d4af37", padding: 14, borderRadius: 999, textAlign: "center", textDecoration: "none", fontWeight: 900, border: "1px solid #d4af37" }}>Test Drive</a></div>
          <div style={{ marginTop: 16, padding: 14, borderRadius: 18, background: "#050505", border: "1px solid #222", color: "#aaa", fontSize: 13, lineHeight: 1.5 }}>Chicago Cars US · Summit, IL · Hablamos Español · Financing subject to approval. Price plus taxes, title, registration, doc, and dealer fees. Contact Arturo for the full out-the-door number.</div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #000000, #090909)",
        color: "#fff",
        padding: 20,
        fontFamily: "Arial",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          marginBottom: 18,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1 style={{ color: "#d4af37", margin: 0 }}>DriveWithArturo 360 Studio</h1>
          <div style={{ color: "#888", fontSize: 13, marginTop: 6 }}>
            Stabilized build mode — core 360, capture, leads, analytics, and sharing are active.
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            onClick={() => setCaptureMode((prev) => !prev)}
            style={{ padding: "10px 14px", borderRadius: 999, border: "1px solid #333", background: captureMode ? "#d4af37" : "#111", color: captureMode ? "#000" : "#fff", fontWeight: 900 }}
          >
            {captureMode ? "Viewing Capture" : "Open Capture"}
          </button>
          <button
            onClick={saveToSupabase}
            disabled={saving}
            style={{ padding: "10px 14px", borderRadius: 999, border: "none", background: "#d4af37", color: "#000", fontWeight: 900, opacity: saving ? 0.6 : 1 }}
          >
            {saving ? "Saving..." : "Save Vehicle"}
          </button>
        </div>
      </div>

      <div style={{ background: "#050505", border: "1px solid #333", borderRadius: 18, padding: 16, marginBottom: 18 }}>
        <h3 style={{ color: "#d4af37", marginTop: 0 }}>Vehicle Details</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
          <input placeholder="Vehicle Name" value={vehicleName} onChange={(e) => setVehicleName(e.target.value)} style={{ padding: 12, background: "#111", color: "#fff", border: "1px solid #333", borderRadius: 10 }} />
          <input placeholder="VIN" value={vin} onChange={(e) => setVin(e.target.value.toUpperCase())} style={{ padding: 12, background: "#111", color: "#fff", border: "1px solid #333", borderRadius: 10 }} />
          <input placeholder="Price" value={vehiclePrice} onChange={(e) => setVehiclePrice(e.target.value)} style={{ padding: 12, background: "#111", color: "#fff", border: "1px solid #333", borderRadius: 10 }} />
          <input placeholder="Mileage" value={mileage} onChange={(e) => setMileage(e.target.value)} style={{ padding: 12, background: "#111", color: "#fff", border: "1px solid #333", borderRadius: 10 }} />
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
          <button onClick={decodeVin} disabled={decodingVin || vin.length !== 17} style={{ padding: "10px 14px", borderRadius: 999, border: "1px solid #d4af37", background: "#111", color: "#d4af37", fontWeight: 900, opacity: decodingVin || vin.length !== 17 ? 0.5 : 1 }}>
            {decodingVin ? "Decoding..." : "Decode VIN"}
          </button>
          <button onClick={startVinScanner} style={{ padding: "10px 14px", borderRadius: 999, border: "1px solid #333", background: "#111", color: "#fff", fontWeight: 900 }}>
            VIN Scanner
          </button>
          <label style={{ padding: "10px 14px", borderRadius: 999, border: "1px solid #333", background: "#111", color: "#fff", fontWeight: 900, cursor: "pointer" }}>
            Bulk Upload Photos
            <input type="file" accept="image/*" multiple onChange={handleBulkUpload} style={{ display: "none" }} />
          </label>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 18 }}>
        <div style={{ background: "#050505", border: "1px solid #333", borderRadius: 16, padding: 14 }}>
          <div style={{ color: "#888", fontSize: 12 }}>Capture Progress</div>
          <div style={{ color: "#d4af37", fontSize: 30, fontWeight: 900 }}>{completion}/{TOTAL_FRAMES}</div>
          <div style={{ color: "#aaa", fontSize: 13 }}>{progressPercent}% complete · {smartCaptureStatus}</div>
        </div>
        <div style={{ background: "#050505", border: "1px solid #333", borderRadius: 16, padding: 14 }}>
          <div style={{ color: "#888", fontSize: 12 }}>Smart Score</div>
          <div style={{ color: "#d4af37", fontSize: 30, fontWeight: 900 }}>{smartCaptureScore}%</div>
          <div style={{ color: "#aaa", fontSize: 13 }}>{smartCaptureTip}</div>
        </div>
        <div style={{ background: "#050505", border: "1px solid #333", borderRadius: 16, padding: 14 }}>
          <div style={{ color: "#888", fontSize: 12 }}>Alignment</div>
          <div style={{ color: readyToCapture ? "#22c55e" : "#d4af37", fontSize: 30, fontWeight: 900 }}>{alignmentScore}%</div>
          <div style={{ color: "#aaa", fontSize: 13 }}>{alignmentMessage}</div>
        </div>
      </div>

      {vinScannerOpen && (
        <div style={{ background: "#050505", border: "1px solid #333", borderRadius: 18, padding: 16, marginBottom: 18 }}>
          <h3 style={{ color: "#d4af37", marginTop: 0 }}>VIN Scanner</h3>
          <video
            ref={vinVideoRef}
            playsInline
            muted
            autoPlay
            style={{ width: "100%", maxHeight: 360, objectFit: "cover", borderRadius: 14, background: "#000" }}
          />
          <div style={{ color: "#aaa", marginTop: 10 }}>{vinScanMessage}</div>
          <button onClick={stopVinScanner} style={{ marginTop: 12, padding: 12, borderRadius: 999, border: "1px solid #333", background: "#111", color: "#fff", fontWeight: 900 }}>Close Scanner</button>
        </div>
      )}

      {captureMode && (
        <div style={{ background: "#050505", border: "1px solid #333", borderRadius: 18, padding: 16, marginBottom: 18 }}>
          <h3 style={{ color: "#d4af37", marginTop: 0 }}>Capture Mode</h3>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
            <button onClick={startLiveCamera} style={{ padding: "10px 14px", borderRadius: 999, border: "none", background: "#d4af37", color: "#000", fontWeight: 900 }}>Start Camera</button>
            <button onClick={takeLivePhoto} disabled={!cameraOn || uploading} style={{ padding: "10px 14px", borderRadius: 999, border: "1px solid #333", background: "#111", color: "#fff", fontWeight: 900, opacity: !cameraOn || uploading ? 0.5 : 1 }}>Capture Slot {captureSlot + 1}</button>
            <button onClick={stopLiveCamera} style={{ padding: "10px 14px", borderRadius: 999, border: "1px solid #333", background: "#111", color: "#fff", fontWeight: 900 }}>Stop Camera</button>
            <button onClick={() => setCameraGridEnabled((prev) => !prev)} style={{ padding: "10px 14px", borderRadius: 999, border: cameraGridEnabled ? "1px solid #d4af37" : "1px solid #333", background: cameraGridEnabled ? "#d4af37" : "#111", color: cameraGridEnabled ? "#000" : "#fff", fontWeight: 900 }}>
              {cameraGridEnabled ? "Grid ON" : "Grid OFF"}
            </button>
            <button onClick={enableMotionAlignment} style={{ padding: "10px 14px", borderRadius: 999, border: "1px solid #333", background: "#111", color: "#fff", fontWeight: 900 }}>Enable Motion</button>
          </div>
          {cameraError && <div style={{ color: "#fca5a5", marginBottom: 10 }}>{cameraError}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14, marginBottom: 14 }}>
            <div style={{ background: "#090909", border: "1px solid #222", borderRadius: 16, padding: 14 }}>
              <div style={{ color: "#888", fontSize: 12, marginBottom: 6 }}>CURRENT SHOT GUIDE</div>
              <div style={{ color: "#d4af37", fontWeight: 900, fontSize: 22, marginBottom: 8 }}>
                {captureSequence[currentShotIndex]?.title || "Vehicle Shot"}
              </div>
              <div style={{ color: "#ddd", lineHeight: 1.6, fontSize: 14 }}>
                {captureSequence[currentShotIndex]?.guidance || "Center the vehicle and keep the phone level."}
              </div>
              <div style={{ color: "#888", fontSize: 12, marginTop: 10 }}>
                Shot {currentShotIndex + 1} of {captureSequence.length} • Target frame {captureSlot + 1} • Angle {targetAngle}°
              </div>
            </div>

            <div style={{ background: "#090909", border: "1px solid #222", borderRadius: 16, padding: 14 }}>
              <div style={{ color: "#888", fontSize: 12, marginBottom: 6 }}>ALIGNMENT ASSISTANT</div>
              <div style={{ color: readyToCapture ? "#22c55e" : "#d4af37", fontWeight: 900, fontSize: 26 }}>
                {alignmentScore}% {readyToCapture ? "LOCKED" : "ALIGN"}
              </div>
              <div style={{ color: "#ddd", lineHeight: 1.6, fontSize: 14 }}>
                {alignmentMessage}
              </div>
              <button onClick={nextCaptureShot} style={{ marginTop: 12, padding: "10px 14px", borderRadius: 999, border: "none", background: "#d4af37", color: "#000", fontWeight: 900 }}>
                Next Shot Guide
              </button>
            </div>
          </div>

          {cameraOn && (
            <div style={{ background: "#090909", border: "1px solid #222", borderRadius: 16, padding: 14, marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
                <div>
                  <div style={{ color: "#d4af37", fontWeight: 900 }}>Camera Zoom</div>
                  <div style={{ color: "#888", fontSize: 12 }}>
                    {cameraZoomSupported ? `Zoom: ${cameraZoom.toFixed(1)}x` : "Safari may limit web camera zoom. Use pinch-to-zoom fallback if hardware zoom is unavailable."}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {[0.5, 1, 1.5, 2, 3].map((zoomLevel) => (
                    <button key={zoomLevel} onClick={() => applyCameraZoom(zoomLevel)} style={{ padding: "8px 12px", borderRadius: 999, border: cameraZoom === zoomLevel ? "1px solid #d4af37" : "1px solid #333", background: cameraZoom === zoomLevel ? "#d4af37" : "#111", color: cameraZoom === zoomLevel ? "#000" : "#fff", fontWeight: 900 }}>
                      {zoomLevel}x
                    </button>
                  ))}
                </div>
              </div>

              <input
                type="range"
                min="0.5"
                max="5"
                step="0.1"
                value={cameraZoom}
                onChange={(e) => applyCameraZoom(Number(e.target.value))}
                style={{ width: "100%" }}
              />
            </div>
          )}

          <div style={{ position: "relative", width: "100%", borderRadius: 18, overflow: "hidden", border: readyToCapture ? "2px solid #22c55e" : "2px solid #d4af37", boxShadow: readyToCapture ? "0 0 35px rgba(34,197,94,0.35)" : "0 0 35px rgba(212,175,55,0.3)" }}>
            <video ref={videoRef} playsInline muted style={{ width: "100%", maxHeight: 460, objectFit: "cover", borderRadius: 14, background: "#000", display: "block" }} />

            {cameraGridEnabled && (
              <div style={{ position: "absolute", inset: 0, pointerEvents: "none", opacity: 0.55 }}>
                <div style={{ position: "absolute", left: "33.33%", top: 0, bottom: 0, width: 1, background: "rgba(255,255,255,0.55)" }} />
                <div style={{ position: "absolute", left: "66.66%", top: 0, bottom: 0, width: 1, background: "rgba(255,255,255,0.55)" }} />
                <div style={{ position: "absolute", top: "33.33%", left: 0, right: 0, height: 1, background: "rgba(255,255,255,0.55)" }} />
                <div style={{ position: "absolute", top: "66.66%", left: 0, right: 0, height: 1, background: "rgba(255,255,255,0.55)" }} />
              </div>
            )}

            <div style={{ position: "absolute", inset: 0, pointerEvents: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ width: "72%", height: "58%", border: "6px solid rgba(212,175,55,0.82)", borderRadius: "45% 45% 36% 36%", boxShadow: "0 0 0 9999px rgba(0,0,0,0.28), 0 0 45px rgba(212,175,55,0.65), inset 0 0 35px rgba(212,175,55,0.22)" }} />
            </div>

            <div style={{ position: "absolute", left: 18, right: 18, top: 18, display: "flex", justifyContent: "space-between", gap: 10, pointerEvents: "none" }}>
              <div style={{ background: "rgba(0,0,0,0.68)", color: "#d4af37", padding: "8px 12px", borderRadius: 999, fontWeight: 900, fontSize: 12 }}>
                {captureSequence[currentShotIndex]?.title || "Shot Guide"}
              </div>
              <div style={{ background: readyToCapture ? "rgba(5,46,22,0.82)" : "rgba(69,26,3,0.82)", color: readyToCapture ? "#bbf7d0" : "#fde68a", padding: "8px 12px", borderRadius: 999, fontWeight: 900, fontSize: 12 }}>
                {readyToCapture ? "READY" : "ADJUST"}
              </div>
            </div>

            <div style={{ position: "absolute", left: "50%", top: 10, transform: "translateX(-50%)", color: "#d4af37", fontSize: 32, fontWeight: 900, textShadow: "0 0 18px #000", pointerEvents: "none" }}>↑</div>
            <div style={{ position: "absolute", left: "50%", bottom: 10, transform: "translateX(-50%)", color: "#d4af37", fontSize: 32, fontWeight: 900, textShadow: "0 0 18px #000", pointerEvents: "none" }}>↓</div>
            <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#d4af37", fontSize: 32, fontWeight: 900, textShadow: "0 0 18px #000", pointerEvents: "none" }}>←</div>
            <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: "#d4af37", fontSize: 32, fontWeight: 900, textShadow: "0 0 18px #000", pointerEvents: "none" }}>→</div>

            <div style={{ position: "absolute", left: 18, right: 18, bottom: 18, background: "rgba(0,0,0,0.72)", color: "#fff", padding: 12, borderRadius: 14, pointerEvents: "none", lineHeight: 1.5, fontSize: 13 }}>
              {captureSequence[currentShotIndex]?.guidance || "Match the vehicle inside the hologram outline."}
            </div>
          </div>
        </div>
      )}

      {Viewer}
      {!customerMode && captureMode && FrameSlots}
      {InteriorHotspotBox}
      {DamagePanel}
      {AnalyticsPanel}

      <div style={{ background: "#050505", border: "1px solid #333", borderRadius: 18, padding: 16, marginTop: 18 }}>
        <h3 style={{ color: "#d4af37", marginTop: 0 }}>AI Models + Dashboards</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 }}>
          {[
            ["AI Marketing Model", generatedMarketing ? "READY" : "STANDBY"],
            ["AI Lead Model", leadTemperature || "COLD"],
            ["Finance Assistant", financeEstimate ? "ESTIMATE READY" : "READY"],
            ["Inventory Dashboard", `${inventoryVehicles.length} Vehicles`],
            ["CRM Leads", `${storedLeads.length} Leads`],
            ["Analytics", `${analyticsData.pageViews || 0} Views`],
          ].map(([label, value]) => (
            <div key={label} style={{ background: "#090909", border: "1px solid #222", borderRadius: 14, padding: 14 }}>
              <div style={{ color: "#888", fontSize: 12, marginBottom: 6 }}>{label}</div>
              <div style={{ color: "#d4af37", fontWeight: 900, fontSize: 20 }}>{value}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background: "#050505", border: "1px solid #333", borderRadius: 18, padding: 16, marginTop: 18 }}>
        <h3 style={{ color: "#d4af37", marginTop: 0 }}>Spin Styles + Background Studio</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
          <label style={{ color: "#aaa", fontSize: 13 }}>
            Spin / Studio Preset
            <select value={studioPreset} onChange={(e) => setStudioPreset(e.target.value)} style={{ width: "100%", marginTop: 6, padding: 12, background: "#111", color: "#fff", border: "1px solid #333", borderRadius: 10 }}>
              <option value="luxury">Luxury</option>
              <option value="natural">Natural</option>
              <option value="sport">Sport</option>
              <option value="dark">Dark</option>
              <option value="outdoor">Outdoor</option>
            </select>
          </label>

          <label style={{ color: "#aaa", fontSize: 13 }}>
            Background Style
            <select value={backgroundMode} onChange={(e) => setBackgroundMode(e.target.value)} style={{ width: "100%", marginTop: 6, padding: 12, background: "#111", color: "#fff", border: "1px solid #333", borderRadius: 10 }}>
              <option value="original">Original</option>
              <option value="luxury">Luxury Black</option>
              <option value="white">Clean White</option>
              <option value="chicago">Chicago Night</option>
              <option value="jdm">JDM Neon</option>
              <option value="truck">Truck / Outdoor</option>
              <option value="sunset">Sunset Promo</option>
            </select>
          </label>

          <label style={{ color: "#aaa", fontSize: 13 }}>
            Background Studio
            <select value={backgroundStudioMode} onChange={(e) => setBackgroundStudioMode(e.target.value)} style={{ width: "100%", marginTop: 6, padding: 12, background: "#111", color: "#fff", border: "1px solid #333", borderRadius: 10 }}>
              <option value="luxury-studio">Luxury Studio</option>
              <option value="chicago-night">Chicago Night</option>
              <option value="performance-tunnel">Performance Tunnel</option>
              <option value="minimal-white">Minimal White</option>
            </select>
          </label>
        </div>

        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 14, color: "#ddd" }}>
          <label><input type="checkbox" checked={studioEnhance} onChange={(e) => setStudioEnhance(e.target.checked)} /> Studio Enhance</label>
          <label><input type="checkbox" checked={removeBackground} onChange={(e) => setRemoveBackground(e.target.checked)} /> AI Background Style</label>
          <label><input type="checkbox" checked={addWatermark} onChange={(e) => setAddWatermark(e.target.checked)} /> DriveWithArturo Watermark</label>
          <label><input type="checkbox" checked={depthOverlayMode} onChange={(e) => setDepthOverlayMode(e.target.checked)} /> Depth Overlays</label>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginTop: 18 }}>
        <button onClick={generateSocialContent} style={{ padding: 14, borderRadius: 14, border: "1px solid #333", background: "#111", color: "#d4af37", fontWeight: 900 }}>Generate Marketing</button>
        <button onClick={generateVoiceoverAndHooks} style={{ padding: 14, borderRadius: 14, border: "1px solid #333", background: "#111", color: "#d4af37", fontWeight: 900 }}>Generate Hooks</button>
        <button onClick={generateCreatorExportPackage} style={{ padding: 14, borderRadius: 14, border: "1px solid #333", background: "#111", color: "#d4af37", fontWeight: 900 }}>Creator Export</button>
        <button onClick={exportSpinVideo} disabled={exportingVideo || completion < minimumSpinFrames} style={{ padding: 14, borderRadius: 14, border: "1px solid #333", background: exportingVideo ? "#444" : completion >= minimumSpinFrames ? "#d4af37" : "#111", color: completion >= minimumSpinFrames ? "#000" : "#666", fontWeight: 900, opacity: exportingVideo || completion < minimumSpinFrames ? 0.7 : 1, cursor: exportingVideo || completion < minimumSpinFrames ? "not-allowed" : "pointer" }}>
          {exportingVideo ? "Exporting Video..." : completion >= minimumSpinFrames ? "Export Video" : `Need ${minimumSpinFrames - completion} More Frames`}
        </button>
      </div>

      {videoDownloadUrl && (
        <div style={{ background: "#050505", border: "1px solid #333", borderRadius: 18, padding: 16, marginTop: 18 }}>
          <h3 style={{ color: "#d4af37", marginTop: 0 }}>Video Export Complete</h3>
          <div style={{ color: "#aaa", marginBottom: 14 }}>
            Your 360 vehicle video is ready to download or share.
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            <button
              onClick={async () => {
                try {
                  if (navigator.share && exportedVideoFile) {
                    await navigator.share({
                      title: `${vehicleName || "Vehicle"} 360 Video`,
                      text: "DriveWithArturo 360 Export",
                      files: [exportedVideoFile],
                    });
                  } else {
                    window.open(videoDownloadUrl, "_blank");
                  }
                } catch (err) {
                  console.warn(err);
                }
              }}
              style={{ padding: 14, borderRadius: 14, border: "none", background: "#d4af37", color: "#000", fontWeight: 900, cursor: "pointer" }}
            >
              Share / Save Video
            </button>

            <a
              href={videoDownloadUrl}
              download={exportedVideoFile?.name || `${safeSlug(vehicleName || vin || "drivewitharturo-360")}.webm`}
              style={{ padding: 14, borderRadius: 14, border: "1px solid #d4af37", background: "#111", color: "#d4af37", fontWeight: 900, textAlign: "center", textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              Download Video
            </a>
          </div>
        </div>
      )}

      {viralHooks.length > 0 && (
        <div style={{ background: "#050505", border: "1px solid #333", borderRadius: 18, padding: 16, marginTop: 18 }}>
          <h3 style={{ color: "#d4af37", marginTop: 0 }}>Generated Viral Hooks + Voiceover</h3>
          {voiceoverScript?.narration && (
            <div style={{ background: "#090909", border: "1px solid #222", borderRadius: 14, padding: 14, color: "#ddd", lineHeight: 1.6, marginBottom: 12 }}>
              <div style={{ color: "#d4af37", fontWeight: 900, marginBottom: 6 }}>Voiceover Script</div>
              {voiceoverScript.narration}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
            {viralHooks.map((hook, index) => (
              <div key={`${hook.type}-${index}`} style={{ background: "#090909", border: "1px solid #222", borderRadius: 14, padding: 14 }}>
                <div style={{ color: "#d4af37", fontWeight: 900, marginBottom: 6 }}>{hook.type}</div>
                <div style={{ color: "#fff", lineHeight: 1.5 }}>{hook.text}</div>
                <div style={{ color: "#888", fontSize: 12, marginTop: 8 }}>Audio: {hook.audio}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {socialContent && (
        <div style={{ background: "#050505", border: "1px solid #333", borderRadius: 18, padding: 16, marginTop: 18 }}>
          <h3 style={{ color: "#d4af37", marginTop: 0 }}>Generated Marketing</h3>
          <textarea readOnly value={socialContent.tiktokCaption || ""} style={{ width: "100%", minHeight: 120, padding: 12, background: "#000", color: "#fff", border: "1px solid #333", borderRadius: 10 }} />
        </div>
      )}

      {shareLink && (
        <div style={{ background: "#050505", border: "1px solid #333", borderRadius: 14, padding: 14, marginTop: 18 }}>
          <h3 style={{ color: "#d4af37", marginTop: 0 }}>Share Link</h3>
          <input value={shareLink} readOnly style={{ width: "100%", padding: 12, background: "#000", color: "#fff", border: "1px solid #333", borderRadius: 8, marginBottom: 10 }} />
          <button onClick={() => navigator.clipboard.writeText(shareLink)} style={{ width: "100%", padding: 12, background: "#d4af37", color: "#000", border: "none", borderRadius: 999, fontWeight: 900 }}>Copy Share Link</button>
        </div>
      )}
    </div>
  );
}
